import { usePageBackOptions } from '@/contexts/PageNavigationContext';
import React, { useState, useEffect, useMemo, useRef } from 'react';
import API, { fmt } from '@/lib/api';
import { vatLabel } from '@/lib/taxDisplay';
import '@/components/article-toolbar.css';
import { lijnPrijsVan, stukPrijsVan } from '@/lib/artikelPrijs';
import { isIOS as isIOSPlatform } from '@/lib/platform';
import Pagination, { usePagination } from '@/components/Pagination';
import { toast } from 'sonner';
import { Gear, ListDashes, PlusCircle, Trash, Plus, X, PencilSimple, MagnifyingGlass, DownloadSimple, UploadSimple, Barcode, Sparkle, Tag, Package, CheckCircle, Truck, Coin, Cube, Image as ImageIcon, Globe, IdentificationCard, Warning, Copy, Prohibit } from '@phosphor-icons/react';
import { useActiesToggle } from '@/components/useActiesToggle';
import InventarisPage from './InventarisPage';
import IOSToggle from '@/components/IOSToggle';
import BarcodeScanner from '@/components/BarcodeScanner';
import BarcodePreview from '@/components/BarcodePreview';
import LabelExportModal from '@/components/LabelExportModal';
import ArtikelImportModal from '@/components/ArtikelImportModal';
import ImportTaricPanel from '@/components/ImportTaricPanel';
import HsCodeReviewModal from '@/components/HsCodeReviewModal';
import VariantenPanel from '@/components/VariantenPanel';
import VariantCodeSettings from '@/components/VariantCodeSettings';
import PrijsWizardModal from '@/components/PrijsWizardModal';
import { generateEan13, isValidEan13 } from '@/lib/ean';
import { hsLevelLabel, hsValidate, hsNormalize } from '@/lib/hscode';
import { useConfirm } from '@/components/ConfirmDialog';
import { useAuth } from '@/contexts/AuthContext';

const TABS = [
  { key: 'lijst', label: 'Alle artikels', icon: ListDashes },
  { key: 'nieuw', label: 'Nieuw artikel', icon: PlusCircle },
  { key: 'inventaris', label: 'Inventaris', icon: Package },
  { key: 'import_taric', label: 'Import & TARIC', icon: Globe },
  { key: 'params', label: 'Instellingen', icon: Gear },
];

const BUILTIN_EENHEDEN = [
  { value: 'stuk', label: 'Per stuk' },
  { value: 'meter', label: 'Per meter' },
  { value: 'pack', label: 'Per pack' },
  { value: 'uur', label: 'Per uur' },
  { value: 'kg', label: 'Per kg' },
  { value: 'liter', label: 'Per liter' },
];

/**
 * Pure helper: detecteer pack-gegevens uit een vrije-tekst omschrijving.
 *
 * Herkent patronen zoals:
 *   "25st/pk 3,0M 75L/M"   → {pack_grootte:25, eenheid_aantal:75, eenheid_label:'L/m', confidence:'high'}
 *   "60st/pak 25m"         → {pack_grootte:60, eenheid_aantal:1500, eenheid_label:'L/m', confidence:'medium'} (60×25=1500)
 *   "10/pak 5L"            → {pack_grootte:10, eenheid_aantal:50, eenheid_label:'L', confidence:'medium'}
 *   "100 stuks"            → {pack_grootte:100, eenheid_aantal:0, eenheid_label:'', confidence:'low'}
 *
 * Returnt null als er onvoldoende signalen zijn.
 */
export function detecteerPackUitOmschrijving(text) {
  if (!text || typeof text !== 'string') return null;
  const t = text.trim();
  if (t.length < 4) return null;

  // 1. Pack-grootte ("25st/pk", "60 stuks/pak", "100/pak", "12 stk")
  let pack_grootte = 0;
  const m_pack = t.match(/(\d+)\s*(?:st|stuks|stk|stuk|pcs|x)?\s*\/?\s*(?:pk|pak|pack|doos|krat|bundel)/i);
  if (m_pack) pack_grootte = parseInt(m_pack[1], 10);

  // 2. Direct lopende meter ("75L/M", "75 L/m", "75 lm", "75 lopende meter")
  let eenheid_aantal = 0;
  let eenheid_label = '';
  const m_lm = t.match(/(\d+(?:[.,]\d+)?)\s*(?:l\/?m|lm|lopende\s+meter|lpm)\b/i);
  if (m_lm) {
    eenheid_aantal = parseFloat(m_lm[1].replace(',', '.'));
    eenheid_label = 'L/m';
  }

  // 3. Anders: lengte per stuk × pack-grootte ("3,0M" × 25st/pk = 75 L/m)
  if (!eenheid_aantal && pack_grootte > 0) {
    // "3,0M" of "3.0m" (m als laatste van blok, niet gevolgd door cijfer/woord-char)
    const m_len = t.match(/(\d+(?:[.,]\d+)?)\s*m(?![a-zA-Z\d])/i);
    if (m_len) {
      const lengte = parseFloat(m_len[1].replace(',', '.'));
      if (lengte > 0 && lengte < 1000) {
        eenheid_aantal = Math.round(pack_grootte * lengte * 100) / 100;
        eenheid_label = 'L/m';
      }
    }
  }

  // 4. Volume-detectie ("5L", "500ml" → liter)
  if (!eenheid_aantal && pack_grootte > 0) {
    const m_vol_l = t.match(/(\d+(?:[.,]\d+)?)\s*[lL](?![a-zA-Z\d])/);
    const m_vol_ml = t.match(/(\d+(?:[.,]\d+)?)\s*ml(?![a-zA-Z\d])/i);
    if (m_vol_l) {
      const v = parseFloat(m_vol_l[1].replace(',', '.'));
      eenheid_aantal = Math.round(pack_grootte * v * 100) / 100;
      eenheid_label = 'L';
    } else if (m_vol_ml) {
      const v = parseFloat(m_vol_ml[1].replace(',', '.'));
      eenheid_aantal = Math.round(pack_grootte * v / 1000 * 100) / 100;
      eenheid_label = 'L';
    }
  }

  // 5. Gewicht-detectie ("5kg", "500g")
  if (!eenheid_aantal && pack_grootte > 0) {
    const m_kg = t.match(/(\d+(?:[.,]\d+)?)\s*kg(?![a-zA-Z\d])/i);
    const m_g = t.match(/(\d+(?:[.,]\d+)?)\s*g(?![a-zA-Z\d])/i);
    if (m_kg) {
      const w = parseFloat(m_kg[1].replace(',', '.'));
      eenheid_aantal = Math.round(pack_grootte * w * 100) / 100;
      eenheid_label = 'kg';
    } else if (m_g) {
      const w = parseFloat(m_g[1].replace(',', '.'));
      eenheid_aantal = Math.round(pack_grootte * w / 1000 * 100) / 100;
      eenheid_label = 'kg';
    }
  }

  // Geen enkel signaal → null
  if (!pack_grootte && !eenheid_aantal) return null;

  // Confidence-score op basis van hoeveel signalen we hadden
  let confidence = 'low';
  if (eenheid_aantal && eenheid_label && pack_grootte) confidence = 'high';
  else if (eenheid_aantal && eenheid_label) confidence = 'medium';

  return {
    pack_grootte: pack_grootte || 0,
    eenheid_aantal: eenheid_aantal || 0,
    eenheid_label: eenheid_label || '',
    confidence,
    bron_tekst: t,
  };
}

export default function ArtikelsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const confirm = useConfirm();
  const [tab, setTab] = useState('lijst');
  const [params, setParams] = useState(null);
  const [artikels, setArtikels] = useState([]);
  // Foto-controle: artikels waarvan de foto onvindbaar is voor PDF's ({ [id]: {type, reason, herstelbaar} })
  const [fotoStatus, setFotoStatus] = useState({ map: {}, count: 0, herstelbaar: 0 });
  const [alleenFotoProbleem, setAlleenFotoProbleem] = useState(false);
  const [fotoHerstelBusy, setFotoHerstelBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [catF, setCatF] = useState('');
  const [zonderAankoop, setZonderAankoop] = useState(false);
  // Prijs-modus toggle: 'stuk' (default) | 'pack' (× pack_grootte) | 'beide' (gestapeld)
  const [prijsModus, setPrijsModus] = useState(() => {
    try {
      const v = localStorage.getItem('art_prijs_modus');
      return v === 'pack' || v === 'beide' ? v : 'stuk';
    } catch { return 'stuk'; }
  });
  // 🔀 Sort-state: kolom + richting. null = default (categorie → art_code)
  const [sortBy, setSortBy] = useState(() => {
    try { return JSON.parse(localStorage.getItem('artikels_sort') || 'null'); } catch (_e) { return null; }
  });
  const toggleSort = (col) => {
    setSortBy((prev) => {
      let next;
      if (!prev || prev.col !== col) next = { col, dir: 'asc' };
      else if (prev.dir === 'asc') next = { col, dir: 'desc' };
      else next = null;  // 3e klik → reset naar default
      try { localStorage.setItem('artikels_sort', JSON.stringify(next)); } catch (_e) { /* localStorage geblokkeerd */ }
      return next;
    });
  };
  // Toggle voor "Gegroepeerd per categorie"-weergave (persist in localStorage)
  const [grouped, setGrouped] = useState(() => {
    try { return localStorage.getItem('art_grouped') === '1'; } catch { return false; }
  });
  const toggleGrouped = () => setGrouped(g => {
    const nieuw = !g;
    try { localStorage.setItem('art_grouped', nieuw ? '1' : '0'); } catch { /* localStorage geblokkeerd */ }
    return nieuw;
  });
  // Inklap-state per categorie (gegroepeerd-modus)
  const [groupClosed, setGroupClosed] = useState({});
  const toggleGroup = (cat) => setGroupClosed(p => ({ ...p, [cat]: !p[cat] }));
  const [editId, setEditId] = useState(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [eenheden, setEenheden] = useState(BUILTIN_EENHEDEN);
  // Scan-balk in "Nieuw artikel"-tab — checkt of artikel al bestaat voor je het aanmaakt
  const [nieuwScan, setNieuwScan] = useState('');
  const [nieuwScanSugg, setNieuwScanSugg] = useState([]);
  const [nieuwScanIdx, setNieuwScanIdx] = useState(0);
  const [nieuwScanLoading, setNieuwScanLoading] = useState(false);
  const [nieuwAantal, setNieuwAantal] = useState(1);
  const [nieuwPendingArt, setNieuwPendingArt] = useState(null);
  // Te verwerken regels — bouw bulk-lijst op om in 1 klik te verwerken
  // Persistentie in localStorage per gebruiker-per-dag (auto-leeg bij nieuwe dag)
  const ONTV_DATE_KEY = 'ontv_regels_datum';
  const ONTV_LIST_KEY = 'ontv_regels_lijst';
  const todayStr = () => new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  const [nieuwOntvRegels, setNieuwOntvRegels] = useState(() => {
    try {
      const savedDate = localStorage.getItem(ONTV_DATE_KEY);
      const savedList = localStorage.getItem(ONTV_LIST_KEY);
      if (savedDate === todayStr() && savedList) {
        const parsed = JSON.parse(savedList);
        if (Array.isArray(parsed)) return parsed;
      }
      // Nieuwe dag of geen data → start leeg en clear localStorage
      localStorage.removeItem(ONTV_LIST_KEY);
      localStorage.removeItem(ONTV_DATE_KEY);
    } catch (e) { /* ignore */ }
    return [];
  });
  const [nieuwOntvBusy, setNieuwOntvBusy] = useState(false);
  const [nieuwOntvFilter, setNieuwOntvFilter] = useState('');

  // Synchroniseer regels naar localStorage bij elke wijziging
  useEffect(() => {
    try {
      if (nieuwOntvRegels.length > 0) {
        localStorage.setItem(ONTV_DATE_KEY, todayStr());
        localStorage.setItem(ONTV_LIST_KEY, JSON.stringify(nieuwOntvRegels));
      } else {
        localStorage.removeItem(ONTV_LIST_KEY);
        localStorage.removeItem(ONTV_DATE_KEY);
      }
    } catch (e) { /* ignore quota errors */ }
  }, [nieuwOntvRegels]);

  // Browser-waarschuwing bij wegnavigeren / tabsluiten met onverwerkte regels
  usePageBackOptions({ dirty: nieuwOntvRegels.length > 0, busy: nieuwOntvBusy, message: 'Er staan nog onverwerkte ontvangstregels. Wil je deze pagina verlaten?' });
  useEffect(() => {
    if (nieuwOntvRegels.length === 0) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = `Je hebt ${nieuwOntvRegels.length} onverwerkte ontvangst-regel(s). Wil je echt weggaan zonder te verwerken?`;
      return e.returnValue;
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [nieuwOntvRegels.length]);
  const nieuwScanRef = useRef(null);
  const nieuwAantalRef = useRef(null);
  const artCodeRef = useRef(null);

  // Laad alle eenheden (defaults + custom uit instellingen) zodra component mount
  useEffect(() => {
    API.get('/eenheden').then(r => {
      if (Array.isArray(r.data) && r.data.length > 0) setEenheden(r.data);
    }).catch(() => setEenheden(BUILTIN_EENHEDEN));
  }, []);
  // Genereer-vraag: null = nog niet gekozen voor dit nieuwe artikel, true/false = keuze gemaakt
  const [genAsk, setGenAsk] = useState(null);
  // Label-export state
  const [labelOpen, setLabelOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importInitialCsv, setImportInitialCsv] = useState('');
  const [wizardOpen, setWizardOpen] = useState(false);
  const [aiFotoBusy, setAiFotoBusy] = useState(false);
  const [aiEenheidBusy, setAiEenheidBusy] = useState(false);
  const [bulkEenheidBusy, setBulkEenheidBusy] = useState(false);
  const [hsBusy, setHsBusy] = useState(false);
  const [hsReview, setHsReview] = useState(null); // { suggestie, gebruikt_materiaal }
  const [tariefBusy, setTariefBusy] = useState(false);
  const [layoutModus, setLayoutModus] = useState(() => {
    try { return localStorage.getItem('art-form-layout') || 'klassiek'; }
    catch { return 'klassiek'; }
  });
  const wisselLayout = (modus) => {
    setLayoutModus(modus);
    try { localStorage.setItem('art-form-layout', modus); } catch { /* ignore */ }
    toast.success(modus === 'compact' ? '✅ Compacte layout actief' : '✅ Klassieke layout actief');
  };
  const [aiFotoProgress, setAiFotoProgress] = useState('');
  const [selected, setSelected] = useState({}); // { [id]: true }

  // ─── Catalogus PDF modaal ───
  const [catModalOpen, setCatModalOpen] = useState(false);
  const [catPrijsgroep, setCatPrijsgroep] = useState('premium');
  const [catAlleenMetFoto, setCatAlleenMetFoto] = useState(true);
  const [catBarcodeMode, setCatBarcodeMode] = useState('ean');
  // Voorwaarden-tekst (transportkosten, levertijden, etc.) — wordt onthouden in localStorage
  const [catVoorwaarden, setCatVoorwaarden] = useState(() => {
    try { return localStorage.getItem('catalogus-voorwaarden') || ''; } catch { return ''; }
  });
  // Toggle: afdrukken of niet? Wordt ook onthouden in localStorage.
  const [catVoorwaardenAfdrukken, setCatVoorwaardenAfdrukken] = useState(() => {
    try { return localStorage.getItem('catalogus-voorwaarden-print') !== 'false'; } catch { return true; }
  });
  // Geldigheid einddatum (YYYY-MM-DD). Wordt onthouden in localStorage. Leeg = "tot wijziging".
  const [catGeldigTot, setCatGeldigTot] = useState(() => {
    try { return localStorage.getItem('catalogus-geldig-tot') || ''; } catch { return ''; }
  });
  // Categorie-volgorde + inclusie-set voor de PDF (per item: { cat, included }).
  // Wordt onthouden in localStorage zodat de gekozen volgorde de volgende keer terugkomt.
  const [catVolgorde, setCatVolgorde] = useState(() => {
    try {
      const raw = localStorage.getItem('catalogus-cat-volgorde');
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  // Toggle: elke categorie op een nieuwe pagina van de PDF
  const [catNieuwePagina, setCatNieuwePagina] = useState(() => {
    try { return localStorage.getItem('catalogus-nieuwe-pagina') === 'true'; } catch { return false; }
  });
  // Toggle: voorwaarden op ELKE pagina afdrukken (standaard AAN — voorkomt "ik heb het niet gezien"-discussies)
  const [catVoorwaardenElkePagina, setCatVoorwaardenElkePagina] = useState(() => {
    try {
      const v = localStorage.getItem('catalogus-voorwaarden-elke-pagina');
      // Default = true wanneer nog nooit ingesteld (nieuwe gebruikers + bestaande)
      return v === null ? true : v === 'true';
    } catch { return true; }
  });
  // Catalogus stijl: 'compact-pro' (nieuw, default) | 'klassiek' (oud, 3 kolommen)
  const [catStijl, setCatStijl] = useState(() => {
    try { return localStorage.getItem('catalogus-stijl') || 'compact-pro'; } catch { return 'compact-pro'; }
  });
  // Opmaak: accentkleur ('' = huisstijl), zwart-wit afdruk, tekstschaal
  const [catKleur, setCatKleur] = useState(() => { try { return localStorage.getItem('catalogus-kleur') || ''; } catch { return ''; } });
  const [catZwartWit, setCatZwartWit] = useState(() => { try { return localStorage.getItem('catalogus-zwart-wit') === 'true'; } catch { return false; } });
  const [catTekstSchaal, setCatTekstSchaal] = useState(() => { try { return localStorage.getItem('catalogus-tekst-schaal') || '1'; } catch { return '1'; } });
  // Taal, persoonlijke klantprijslijst, voorpagina, labels
  const [catTaal, setCatTaal] = useState(() => { try { return localStorage.getItem('catalogus-taal') || 'nl'; } catch { return 'nl'; } });
  const [catKlantId, setCatKlantId] = useState('');
  const [catKlantZoek, setCatKlantZoek] = useState('');
  const [catKlanten, setCatKlanten] = useState([]);
  const [catVoorpagina, setCatVoorpagina] = useState(() => { try { return localStorage.getItem('catalogus-voorpagina') !== 'false'; } catch { return true; } });
  const [catLabelNieuwDagen, setCatLabelNieuwDagen] = useState(() => { try { return localStorage.getItem('catalogus-label-nieuw') ?? '30'; } catch { return '30'; } });
  const [catLabelActie, setCatLabelActie] = useState(() => { try { return localStorage.getItem('catalogus-label-actie') !== 'false'; } catch { return true; } });
  const [catMailOpen, setCatMailOpen] = useState(false);
  const [catMail, setCatMail] = useState({ to: '', onderwerp: '', bericht: '' });
  const [catMailBusy, setCatMailBusy] = useState(false);
  const catKlant = catKlanten.find(k => k.id === catKlantId) || null;
  // Vertaalstatus van artikelnamen/categorieën voor de gekozen catalogus-taal (fr/en/de)
  const [catVertStatus, setCatVertStatus] = useState(null);
  const [catVertBusy, setCatVertBusy] = useState(false);
  const [catVertProgress, setCatVertProgress] = useState(null);
  const [catVwVertaalBusy, setCatVwVertaalBusy] = useState(false);
  const laadCatVertStatus = async (t) => {
    if (!['fr', 'en', 'de'].includes(t)) { setCatVertStatus(null); return; }
    try { const r = await API.get(`/artikels/vertaal/status?taal=${t}`); setCatVertStatus(r.data); } catch { setCatVertStatus(null); }
  };
  useEffect(() => { if (catModalOpen) laadCatVertStatus(catTaal); }, [catModalOpen, catTaal]); // eslint-disable-line react-hooks/exhaustive-deps
  const vertaalAlleArtikels = async () => {
    setCatVertBusy(true);
    let done = 0;
    const totaal = catVertStatus?.ontbrekend || 0;
    setCatVertProgress({ done, totaal });
    try {
      for (let i = 0; i < 50; i++) {
        const r = await API.post('/artikels/vertaal', { taal: catTaal, max: 50 });
        done += r.data.vertaald || 0;
        setCatVertProgress({ done, totaal: Math.max(totaal, done) });
        if (!r.data.resterend || !r.data.vertaald) {
          if (r.data.mislukt) toast.error(`${r.data.mislukt} artikel(s) konden niet vertaald worden — probeer later opnieuw`);
          break;
        }
      }
      toast.success(`${done} artikel(s) vertaald naar ${catTaal.toUpperCase()}`);
      load();
    } catch (e) {
      toast.error('Vertalen mislukt: ' + (e.response?.data?.detail || e.message));
    } finally {
      setCatVertBusy(false);
      setCatVertProgress(null);
      laadCatVertStatus(catTaal);
    }
  };
  const bewaarCatVertaling = async (cat, vertaling) => {
    try { await API.put('/artikels/categorie-vertalingen', { taal: catTaal, items: [{ cat, vertaling }] }); }
    catch (e) { toast.error('Opslaan mislukt: ' + (e.response?.data?.detail || e.message)); }
  };
  const nlVoorwaardenTekst = () => { try { return (localStorage.getItem('catalogus-voorwaarden') || '').trim(); } catch { return ''; } };
  const vertaalVoorwaarden = async (t) => {
    const bron = nlVoorwaardenTekst();
    if (!bron) { toast.error('Geen Nederlandse voorwaarden gevonden om te vertalen'); return ''; }
    setCatVwVertaalBusy(true);
    try {
      const r = await API.post('/artikels/vertaal-tekst', { tekst: bron, taal: t });
      const txt = r.data.tekst || '';
      setCatVoorwaarden(txt);
      try { localStorage.setItem(vwKey(t), txt); } catch { /* noop */ }
      return txt;
    } catch (e) {
      toast.error('Voorwaarden vertalen mislukt: ' + (e.response?.data?.detail || e.message));
      return '';
    } finally { setCatVwVertaalBusy(false); }
  };
  const catTaalTekst = {
    nl: { onderwerp: 'Onze catalogus', tekst: 'Beste,\n\nIn bijlage vindt u onze actuele catalogus.\n\nMet vriendelijke groeten', wa: 'Hierbij onze catalogus (PDF):' },
    fr: { onderwerp: 'Notre catalogue', tekst: 'Bonjour,\n\nVeuillez trouver ci-joint notre catalogue actuel.\n\nCordialement', wa: 'Voici notre catalogue (PDF) :' },
    en: { onderwerp: 'Our catalogue', tekst: 'Dear,\n\nPlease find attached our current catalogue.\n\nKind regards', wa: 'Here is our catalogue (PDF):' },
    de: { onderwerp: 'Unser Katalog', tekst: 'Guten Tag,\n\nanbei finden Sie unseren aktuellen Katalog.\n\nMit freundlichen Grüßen', wa: 'Hier ist unser Katalog (PDF):' },
  };
  const catPublicLink = async () => {
    // Publieke (tijdelijke) link naar de PDF — 7 dagen geldig — voor WhatsApp
    let token = catPdfReady?.serverToken;
    if (!token) {
      const r = await API.post('/artikels/catalogus-pdf', { ...(catPdfReady?.payload || {}), mode: 'token' });
      token = r.data?.token;
    }
    if (!token) throw new Error('Geen link');
    try { await API.post(`/temp-pdf/${token}/extend?hours=168`); } catch { /* 10 min blijft gelden */ }
    return `${process.env.REACT_APP_BACKEND_URL}/api/temp-pdf/${token}`;
  };
  const catWhatsApp = async () => {
    try {
      const link = await catPublicLink();
      const t = catTaalTekst[catTaal] || catTaalTekst.nl;
      const tel = (catKlant?.tel || '').replace(/[^\d]/g, '').replace(/^00/, '');
      window.open(`https://wa.me/${tel}?text=${encodeURIComponent(`${t.wa}\n${link}`)}`, '_blank', 'noopener');
      API.post('/artikels/catalogus-pdf/log', { kanaal: 'whatsapp', klant_id: catKlantId || '', to: catKlant?.tel || '', taal: catTaal, prijsgroep: catPdfReady?.payload?.prijsgroep || catPrijsgroep, fname: catPdfReady?.fname || '' }).catch(() => {});
      if (catKlant) toast.success(`WhatsApp geopend — gelogd in klantfiche van ${catKlant.naam}`);
    } catch (e) {
      toast.error('WhatsApp-link maken mislukt: ' + (e.response?.data?.detail || e.message));
    }
  };
  const catVerstuurMail = async () => {
    if (!catMail.to.trim() || !catMail.to.includes('@')) { toast.error('Vul een geldig e-mailadres in'); return; }
    setCatMailBusy(true);
    try {
      const r = await API.post('/artikels/catalogus-pdf/mail', { ...(catPdfReady?.payload || {}), to: catMail.to.trim(), onderwerp: catMail.onderwerp, bericht: catMail.bericht });
      toast.success(`Catalogus gemaild naar ${r.data.to}`);
      setCatMailOpen(false);
    } catch (e) {
      toast.error('Mailen mislukt: ' + (e.response?.data?.detail || e.message));
    } finally { setCatMailBusy(false); }
  };
  const catOpmaakPayload = {
    kleur: catKleur || '', zwart_wit: catZwartWit, tekst_schaal: parseFloat(catTekstSchaal) || 1,
    taal: catTaal, klant_id: catKlantId || '', voorpagina: catVoorpagina,
    label_nieuw_dagen: parseInt(catLabelNieuwDagen, 10) || 0, label_actie: catLabelActie,
  };
  // Voorwaarden per taal onthouden (NL = bestaande sleutel)
  const vwKey = (t) => t === 'nl' ? 'catalogus-voorwaarden' : `catalogus-voorwaarden-${t}`;
  const wisselCatTaal = (t) => {
    try { if (catVoorwaarden.trim()) localStorage.setItem(vwKey(catTaal), catVoorwaarden); } catch { /* noop */ }
    setCatTaal(t);
    try { localStorage.setItem('catalogus-taal', t); setCatVoorwaarden(localStorage.getItem(vwKey(t)) || ''); } catch { /* noop */ }
  };
  useEffect(() => {
    if (!catModalOpen || catKlanten.length) return;
    API.get('/klanten').then(r => setCatKlanten((r.data || []).map(k => ({ id: k.id, naam: k.bedrijf || k.naam || '', email: k.email || '', tel: k.gsm || k.telefoon || k.tel || '', taal: (k.taal || '').toLowerCase(), kortinggroep: k.kortinggroep || '' })))).catch(() => {});
  }, [catModalOpen, catKlanten.length]);
  const [catLoading, setCatLoading] = useState(false);
  const [catPdfReady, setCatPdfReady] = useState(null);  // { url, fname } na succesvolle generatie — iOS-safe download-link
  useEffect(() => {
    if (!catPdfReady) return;
    const generated = catPdfReady.payload || {};
    if (generated.prijsgroep !== catPrijsgroep || (generated.klant_id || '') !== catKlantId || generated.stijl !== catStijl) {
      URL.revokeObjectURL(catPdfReady.url);
      setCatPdfReady(null);
      setCatMailOpen(false);
    }
  }, [catPrijsgroep, catKlantId, catStijl, catPdfReady]);

  // Sync catVolgorde met huidige cats wanneer een van de modals opent ÉN bij data-load:
  // - voeg nieuwe categorieën toe (default included=true onderaan)
  // - verwijder cats die niet meer bestaan
  // - persist naar localStorage zodat hoofdlijst-sortering meteen werkt zonder modal te openen
  useEffect(() => {
    if (!catModalOpen && !(params?.categorieen?.length)) return;
    setCatVolgorde(prev => {
      const huidige = new Set((params?.categorieen || []).filter(Boolean));
      const bestaande = prev.filter(x => huidige.has(x.cat));
      const bekend = new Set(bestaande.map(x => x.cat));
      const nieuwe = [...huidige].filter(c => !bekend.has(c)).sort((a, b) => a.localeCompare(b, 'nl-BE'));
      const next = [...bestaande, ...nieuwe.map(c => ({ cat: c, included: true }))];
      try { localStorage.setItem('catalogus-cat-volgorde', JSON.stringify(next)); } catch (_e) { /* localStorage geblokkeerd */ }
      return next;
    });
  }, [catModalOpen, params?.categorieen]);

  // Helpers voor de volgorde-UI
  const moveCat = (idx, dir) => {
    setCatVolgorde(p => {
      const arr = [...p];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return p;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      // Persist direct zodat de wijziging ook actief is op de hoofdlijst (gegroepeerd-per-cat view)
      try { localStorage.setItem('catalogus-cat-volgorde', JSON.stringify(arr)); } catch (_e) { /* localStorage geblokkeerd */ }
      return arr;
    });
  };
  const toggleCatIncluded = (idx) => {
    setCatVolgorde(p => {
      const arr = p.map((x, i) => i === idx ? { ...x, included: !x.included } : x);
      try { localStorage.setItem('catalogus-cat-volgorde', JSON.stringify(arr)); } catch (_e) { /* localStorage geblokkeerd */ }
      return arr;
    });
  };

  // ─── Data-controle modaal ───
  const [dcOpen, setDcOpen] = useState(false);
  const [dcLoading, setDcLoading] = useState(false);
  const [dcResult, setDcResult] = useState(null);
  const [dcSevFilter, setDcSevFilter] = useState('all');

  const emptyForm = { categorie: '', art_code: '', ean: '', omschrijving: '', eenheid: 'stuk', pack_grootte: '', prijs_basis: 'stuk', aankoopprijs: '', btw: 21, leverancier: '', familie: '', prijzen: {}, voorraad: '', min_voorraad: '', gewicht_kg: '', volume_m3: '', hs_code: '', hs_beschrijving: '', hs_motivatie: '', oorsprong_land: '', toon_ean_op_document: false, leveranciers: [], eenheid_aantal: '', eenheid_label: '', toon_eenheid_prijs: true, catalogus_verbergen: false, varianten_actief: false, dimensies: [], varianten: [], actie: false, actie_tot: '', actie_tekst: '', vertalingen: {} };
  const [form, setForm] = useState({ ...emptyForm });
  // Pack-artikel waarvan de ingevoerde prijzen PER PAK zijn (i.p.v. per stuk)
  const perPak = form.eenheid === 'pack' && form.prijs_basis === 'pack';
  const packVanIngevoerd = (p) => lijnPrijsVan({ eenheid: form.eenheid, pack_grootte: form.pack_grootte, prijs_basis: form.prijs_basis }, p);
  const stukVanIngevoerd = (p) => stukPrijsVan({ eenheid: form.eenheid, pack_grootte: form.pack_grootte, prijs_basis: form.prijs_basis }, p);
  // Winst per prijsgroep (zelfde basis als aankoopprijs: per stuk, of per pak bij prijs_basis=pack)
  const winstHint = (p, key) => {
    const ap = parseFloat(form.aankoopprijs) || 0;
    if (!(ap > 0) || !(p > 0)) return null;
    const winst = p - ap;
    const marge = (winst / ap) * 100;
    const c = marge >= 30 ? '#16A34A' : marge >= 10 ? '#D97706' : '#DC2626';
    return (
      <div className="text-[10px] font-mono font-semibold mt-0.5" style={{ color: c }} title="Winst = verkoopprijs − aankoopprijs · marge t.o.v. aankoopprijs" data-testid={`art-winst-${key}`}>
        Winst € {winst.toFixed(2).replace('.', ',')} · {marge.toFixed(0)}%
      </div>
    );
  };
  const [newGroep, setNewGroep] = useState('');
  const [importMsg, setImportMsg] = useState('');
  const [leveranciersList, setLeveranciersList] = useState([]);
  const [inst, setInst] = useState({});
  // 💡 Auto-detect pack uit omschrijving: state voor live-suggesties
  const [packSuggestie, setPackSuggestie] = useState(null);
  const [packSuggestieAfgewezen, setPackSuggestieAfgewezen] = useState(false);

  const loadFotoStatus = () => {
    API.get('/artikels/foto-status').then(r => {
      const map = {};
      (r.data?.broken || []).forEach(b => { map[b.id] = b; });
      setFotoStatus({ map, count: r.data?.count || 0, herstelbaar: r.data?.herstelbaar || 0 });
    }).catch(() => {});
  };
  const herstelFotos = async (ids) => {
    if (fotoHerstelBusy) return;
    setFotoHerstelBusy(true);
    try {
      const r = await API.post('/artikels/foto-status/herstel', { ids: ids || [] });
      const d = r.data || {};
      alert(`🔧 ${d.hersteld || 0} foto('s) hersteld${d.mislukt ? ` · ${d.mislukt} mislukt` : ''}.`);
      load();
    } catch (e) {
      alert('Herstellen mislukt: ' + (e.response?.data?.detail || e.message));
    } finally {
      setFotoHerstelBusy(false);
    }
  };
  const load = () => {
    API.get('/artikel-params').then(r => setParams(r.data)).catch(() => {});
    API.get('/artikels').then(r => setArtikels(r.data)).catch(() => {});
    API.get('/leveranciers').then(r => setLeveranciersList(r.data || [])).catch(() => {});
    API.get('/instellingen').then(r => setInst(r.data || {})).catch(() => {});
    loadFotoStatus();
  };
  useEffect(load, []);

  // Live zoek-suggesties op de "Nieuw artikel"-tab — kijk of artikel al bestaat
  useEffect(() => {
    if (tab !== 'nieuw') return;
    const code = (nieuwScan || '').trim();
    if (code.length < 2) { setNieuwScanSugg([]); setNieuwScanLoading(false); return; }
    setNieuwScanLoading(true);
    const handle = setTimeout(() => {
      API.get('/artikels-zoek', { params: { q: code, limit: 8 } })
        .then(r => { setNieuwScanSugg(r.data || []); setNieuwScanIdx(0); })
        .catch(() => setNieuwScanSugg([]))
        .finally(() => setNieuwScanLoading(false));
    }, 200);
    return () => clearTimeout(handle);
  }, [nieuwScan, tab]);

  // 💡 Auto-detect pack uit omschrijving (debounced)
  // Toont een suggestie-banner met "✓ Toepassen"-knop. Past nooit automatisch toe (gebruiker beslist).
  useEffect(() => {
    if (tab !== 'nieuw' || packSuggestieAfgewezen) { setPackSuggestie(null); return; }
    const omschrijving = (form.omschrijving || '').trim();
    if (!omschrijving) { setPackSuggestie(null); return; }
    const handle = setTimeout(() => {
      const result = detecteerPackUitOmschrijving(omschrijving);
      if (!result) { setPackSuggestie(null); return; }
      // Skip suggestie als alle 3 velden al exact dezelfde waarden hebben als wat we detecteerden
      const huidigPack = parseFloat(form.pack_grootte) || 0;
      const huidigAantal = parseFloat(form.eenheid_aantal) || 0;
      const huidigLabel = (form.eenheid_label || '').trim();
      const exactMatch =
        (!result.pack_grootte || huidigPack === result.pack_grootte) &&
        (!result.eenheid_aantal || huidigAantal === result.eenheid_aantal) &&
        (!result.eenheid_label || huidigLabel === result.eenheid_label);
      if (exactMatch) { setPackSuggestie(null); return; }
      setPackSuggestie(result);
    }, 400);
    return () => clearTimeout(handle);
  }, [form.omschrijving, form.pack_grootte, form.eenheid_aantal, form.eenheid_label, tab, packSuggestieAfgewezen]);

  // Reset afwijzing wanneer gebruiker iets nieuws typt in omschrijving (=nieuwe context)
  useEffect(() => { setPackSuggestieAfgewezen(false); }, [form.omschrijving]);

  const pasPackSuggestieToe = () => {
    if (!packSuggestie) return;
    setForm(p => ({
      ...p,
      pack_grootte: packSuggestie.pack_grootte || p.pack_grootte || '',
      eenheid_aantal: packSuggestie.eenheid_aantal || p.eenheid_aantal || '',
      eenheid_label: packSuggestie.eenheid_label || p.eenheid_label || '',
    }));
    setPackSuggestie(null);
    setPackSuggestieAfgewezen(true);  // Sluit suggesties voor deze omschrijving
  };

  // Gedeelde mapping van API-data naar form-state
  const fillFormFromArtikel = (a) => {
    // Helper: gebruik nullish coalescing zodat 0-waarden correct getoond worden
    const num = (v) => (v === null || v === undefined ? '' : v);
    setForm({
      categorie: a.categorie || '', art_code: a.art_code || '', ean: a.ean || '', omschrijving: a.omschrijving || '',
      eenheid: a.eenheid || 'stuk', pack_grootte: num(a.pack_grootte), prijs_basis: a.prijs_basis || 'stuk',
      aankoopprijs: num(a.aankoopprijs), btw: a.btw ?? 21, leverancier: a.leverancier || '',
      familie: a.familie || '', prijzen: a.prijzen || {},
      voorraad: num(a.voorraad), min_voorraad: num(a.min_voorraad),
      gewicht_kg: num(a.gewicht_kg), volume_m3: num(a.volume_m3),
      hs_code: a.hs_code || '', oorsprong_land: a.oorsprong_land || '',
      hs_beschrijving: a.hs_beschrijving || '',
      hs_motivatie: a.hs_motivatie || '',
      foto_url: a.foto_url || '',
      toon_ean_op_document: !!a.toon_ean_op_document,
      leveranciers: a.leveranciers || [],
      eenheid_aantal: num(a.eenheid_aantal),
      eenheid_label: a.eenheid_label || '',
      toon_eenheid_prijs: a.toon_eenheid_prijs !== false,
      catalogus_verbergen: !!a.catalogus_verbergen,
      actie: !!a.actie, actie_tot: a.actie_tot || '', actie_tekst: a.actie_tekst || '',
      vertalingen: a.vertalingen || {},
      varianten_actief: !!a.varianten_actief,
      dimensies: a.dimensies || [],
      varianten: a.varianten || [],
    });
  };

  // Klikt gebruiker op suggestie → laad alle gegevens in het formulier hieronder + chip met aantal-veld
  const kiesBestaand = (art) => {
    setNieuwScan('');
    setNieuwScanSugg([]);
    setNieuwAantal(1);
    // Zoek het volledige artikel-object in de reeds geladen lijst
    const full = artikels.find(a => a.id === art.id) || art;
    setEditId(art.id);
    fillFormFromArtikel(full);
    setNieuwPendingArt(full);
    setTimeout(() => { nieuwAantalRef.current?.focus(); nieuwAantalRef.current?.select(); }, 80);
  };

  // Wanneer gebruiker de chip wegklikt of escape doet — alles terugzetten
  const annuleerKeuze = () => {
    setNieuwPendingArt(null);
    setNieuwAantal(1);
    setEditId(null);
    setForm({ ...emptyForm });
    setTimeout(() => nieuwScanRef.current?.focus(), 50);
  };

  // Gebruiker wil expliciet enkel bewerken (zonder voorraad-bij-flow)
  const openBestaandArtikel = (art) => {
    setNieuwPendingArt(null);
    setNieuwScan('');
    setNieuwScanSugg([]);
    setEditId(art.id);
    const full = artikels.find(a => a.id === art.id) || art;
    fillFormFromArtikel(full);
  };

  // Voorvullen en focussen op het nieuw-artikel-formulier wanneer er geen match is
  const gaNaarFormulier = (typed) => {
    const code = (typed || nieuwScan || '').trim();
    if (!code) return;
    const isEan = /^\d{8,14}$/.test(code);
    setForm(p => ({
      ...p,
      art_code: isEan ? p.art_code : code,
      ean: isEan ? code : p.ean,
    }));
    setNieuwScan('');
    setNieuwScanSugg([]);
    setNieuwPendingArt(null);
    // Focus op art_code (of omschrijving als EAN al voorvullt)
    setTimeout(() => {
      if (isEan) {
        // Bij EAN: focus op omschrijving want art_code is leeg en moet nog
        const omschr = document.querySelector('[data-testid="art-omschrijving"]');
        omschr?.focus();
      } else {
        artCodeRef.current?.focus();
        artCodeRef.current?.select?.();
      }
    }, 80);
  };

  // Voeg artikel toe aan "Te verwerken"-lijst (bulk-verwerking) i.p.v. direct POST
  const voegVoorraadToe = () => {
    if (!nieuwPendingArt) return;
    const aantal = parseFloat(nieuwAantal) || 0;
    if (aantal <= 0) { toast.error('Aantal moet groter zijn dan 0'); return; }
    setNieuwOntvRegels(prev => {
      const idx = prev.findIndex(r => r.art_id === nieuwPendingArt.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], aantal: (parseFloat(next[idx].aantal) || 0) + aantal };
        return next;
      }
      return [
        {
          tmp_id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          art_id: nieuwPendingArt.id,
          art_code: nieuwPendingArt.art_code,
          omschrijving: nieuwPendingArt.omschrijving,
          ean: nieuwPendingArt.ean || '',
          aantal,
          aankoopprijs: parseFloat(nieuwPendingArt.aankoopprijs) || 0,
          voorraad_voor: parseFloat(nieuwPendingArt.voorraad) || 0,
        },
        ...prev,
      ];
    });
    toast.success(`${nieuwPendingArt.art_code} toegevoegd aan lijst`, { description: `+${aantal} stuks` });
    setNieuwPendingArt(null);
    setNieuwAantal(1);
    setNieuwScan('');
    // Reset form & editId zodat scan-balk weer zichtbaar wordt en je verder kan scannen
    setEditId(null);
    setForm({ ...emptyForm });
    setTimeout(() => nieuwScanRef.current?.focus(), 60);
  };

  const updNieuwOntvRegel = (tmp_id, veld, waarde) => {
    setNieuwOntvRegels(prev => prev.map(r => r.tmp_id === tmp_id ? { ...r, [veld]: waarde } : r));
  };
  const delNieuwOntvRegel = (tmp_id) => {
    setNieuwOntvRegels(prev => prev.filter(r => r.tmp_id !== tmp_id));
  };

  // Verwerk alle regels in 1 keer via bulk-endpoint
  const verwerkAlleNieuwOntv = async () => {
    if (nieuwOntvRegels.length === 0) { toast.info('Geen regels om te verwerken'); return; }
    setNieuwOntvBusy(true);
    try {
      const regels = nieuwOntvRegels.map(r => ({
        art_id: r.art_id,
        aantal: parseFloat(r.aantal) || 0,
        aankoopprijs: r.aankoopprijs !== '' && r.aankoopprijs !== null && r.aankoopprijs !== undefined ? parseFloat(r.aankoopprijs) : null,
      })).filter(r => r.aantal > 0);
      const res = await API.post('/inventaris/ontvangst', { regels });
      toast.success(`${res.data.verwerkt} artikel(s) verwerkt`, {
        description: `${res.data.totaal_aantal} stuks · totale waarde ${fmt(res.data.totaal_waarde)}`,
      });
      setNieuwOntvRegels([]);
      // Reset form & editId zodat we klaar zijn voor de volgende ronde
      setEditId(null);
      setForm({ ...emptyForm });
      setNieuwPendingArt(null);
      load();
    } catch (e) {
      toast.error('Verwerken mislukt', { description: e.response?.data?.detail || e.message });
    } finally {
      setNieuwOntvBusy(false);
    }
  };

  const u = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));
  // Vertalingen (catalogus): handmatig aanpassen = bron vastleggen zodat AI ze niet overschrijft tot de NL-tekst wijzigt
  const [vertaalBusy, setVertaalBusy] = useState(false);
  const zetVertaling = (taal, veld, val) => setForm(p => ({
    ...p,
    vertalingen: { ...(p.vertalingen || {}), [taal]: { ...((p.vertalingen || {})[taal] || {}), [veld]: val, bron: p.omschrijving || '', bron_actie: p.actie ? (p.actie_tekst || '').trim() : '', auto: false } },
  }));
  const aiVertaalArtikel = async () => {
    const targetId = editId || form.id;
    if (!targetId) { toast.error('Sla het artikel eerst op, daarna kan AI het vertalen'); return; }
    setVertaalBusy(true);
    try {
      await API.put(`/artikels/${targetId}`, { omschrijving: form.omschrijving, actie_tekst: form.actie ? (form.actie_tekst || '').trim() : '' });
      const r = await API.post(`/artikels/${targetId}/vertaal`, {});
      setForm(p => ({ ...p, vertalingen: r.data.vertalingen || {} }));
      toast.success(r.data.mislukt?.length ? `Vertaald (mislukt: ${r.data.mislukt.join(', ').toUpperCase()})` : 'Artikel vertaald naar FR, EN en DE');
    } catch (e) {
      toast.error('AI-vertaling mislukt: ' + (e.response?.data?.detail || e.message));
    } finally { setVertaalBusy(false); }
  };
  // Helper: converteer interne waarde naar BE-display (punt → komma)
  const fmtDec = (v) => {
    if (v === '' || v === undefined || v === null) return '';
    return String(v).replace('.', ',');
  };
  // Accepteer komma als decimaal-scheider (BE/NL gewoonte) + leeg mag.
  // Bewaart trailing komma/punt tijdens typen (bv. "12," → user typt cijfer erna).
  const uPrijs = (key) => (e) => {
    const raw = (e.target.value || '').replace(',', '.');
    setForm(p => ({ ...p, prijzen: { ...p.prijzen, [key]: raw === '' ? '' : (isNaN(parseFloat(raw)) ? p.prijzen[key] : raw) } }));
  };
  const uDec = (k) => (e) => {
    const raw = (e.target.value || '').replace(',', '.');
    setForm(p => ({ ...p, [k]: raw === '' ? '' : (isNaN(parseFloat(raw)) ? p[k] : raw) }));
  };

  const existingFamilies = useMemo(() => Object.keys(params?.families || {}), [params]);
  const isNewFamilie = form.familie && !existingFamilies.includes(form.familie);

  const groepen = params?.groepen || [];
  const cats = params?.categorieen || [];

  const filtered = artikels.filter(a =>
    (!q || a.omschrijving?.toLowerCase().includes(q.toLowerCase()) || a.art_code?.toLowerCase().includes(q.toLowerCase()) || (a.ean || '').toLowerCase().includes(q.toLowerCase())) &&
    (!catF || a.categorie === catF) &&
    (!zonderAankoop || !(parseFloat(a.aankoopprijs) > 0)) &&
    (!alleenFotoProbleem || !!fotoStatus.map[a.id])
  )
    // Sorteer standaard op categorie → art_code, OF op de actieve user-sort (kolom-header klik).
    // Ongecategoriseerd komt onderaan in default-modus.
    .sort((a, b) => {
      if (sortBy && sortBy.col) {
        const val = (x) => {
          switch (sortBy.col) {
            case 'art_code': return (x.art_code || '').toLowerCase();
            case 'ean': return (x.ean || '').toLowerCase();
            case 'omschrijving': return (x.omschrijving || '').toLowerCase();
            case 'categorie': return (x.categorie || 'zzz').toLowerCase();
            case 'eenheid': return (x.eenheid || '').toLowerCase();
            case 'aankoop': return parseFloat(x.aankoopprijs) || 0;
            case 'btw': return parseFloat(x.btw_pct) || 0;
            default: return '';
          }
        };
        const va = val(a); const vb = val(b);
        let cmp;
        if (typeof va === 'number') cmp = va - vb;
        else cmp = String(va).localeCompare(String(vb), 'nl-BE');
        return sortBy.dir === 'desc' ? -cmp : cmp;
      }
      const catA = a.categorie || 'zzz';
      const catB = b.categorie || 'zzz';
      const c = catA.localeCompare(catB, 'nl-BE');
      if (c !== 0) return c;
      return (a.art_code || '').localeCompare(b.art_code || '', 'nl-BE');
    });
  // Paginatie voor artikels-lijst (alleen actief in PLATTE weergave — gegroepeerd toont alles)
  const { pageSize, setPageSize, curPage, setPage, paged } = usePagination(filtered, 'artikels', 50);

  // Gegroepeerde data: { "Schroeven": [art1, art2], "Bouten": [art3], ... }
  // Sorteert categorieën alfabetisch; ongecategoriseerde artikels onderaan onder "— Zonder categorie —"
  const filteredGegroupeerd = (() => {
    if (!grouped) return null;
    const map = {};
    filtered.forEach(a => {
      const k = a.categorie || '— Zonder categorie —';
      (map[k] = map[k] || []).push(a);
    });
    return Object.entries(map).sort((a, b) => {
      if (a[0] === '— Zonder categorie —') return 1;
      if (b[0] === '— Zonder categorie —') return -1;
      // 📌 User-gedefinieerde categorie-volgorde heeft prioriteit (uit catVolgorde state, ook gebruikt door PDF-catalogus).
      // Categorieën die in catVolgorde staan komen eerst in die volgorde; rest alfabetisch.
      const idxA = catVolgorde.findIndex(x => x.cat === a[0]);
      const idxB = catVolgorde.findIndex(x => x.cat === b[0]);
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      if (idxA !== -1) return -1;  // a heeft volgorde, b niet → a komt eerst
      if (idxB !== -1) return 1;
      return a[0].localeCompare(b[0], 'nl-BE');
    });
  })();
  const aActies = useActiesToggle({
    storageKey: 'acties_vis_artikels',
    actions: [
      { k: 'edit', label: '✏️ Bewerken' },
      { k: 'dup', label: '📋 Dupliceren' },
      { k: 'delete', label: '🗑️ Verwijderen (admin)' },
      // Optionele KOLOM-toggle (standaard uit). Familie is een sub-groep met
      // gedeelde prijzen — meestal niet nodig in de hoofdlijst.
      { k: 'col_familie', label: '👨‍👩‍👦 Familie-kolom tonen', default: false },
    ],
  });

  const updateParam = (key, value) => setParams(p => ({ ...p, [key]: value }));

  const saveParams = async () => {
    setSaving(true);
    await API.put('/artikel-params', params);
    setSaving(false);
  };

  const addGroep = () => {
    if (!newGroep.trim()) return;
    const key = newGroep.trim().toLowerCase().replace(/\s+/g, '_');
    const colors = ['#8B5CF6', '#EC4899', '#14B8A6', '#F59E0B', '#6366F1', '#EF4444'];
    const color = colors[(groepen.length) % colors.length];
    setParams(p => ({ ...p, groepen: [...(p.groepen || []), { key, label: newGroep.trim(), color }] }));
    setNewGroep('');
  };

  const removeGroep = (key) => setParams(p => ({ ...p, groepen: (p.groepen || []).filter(g => g.key !== key) }));

  const saveArtikel = async (opts) => {
    if (!form.art_code && !form.omschrijving) { alert('Vul minstens een artikelcode of omschrijving in'); return; }
    // Anti-dubbele submit: wanneer een save al loopt, deze klik negeren.
    if (saving) return;
    setSaving(true);
    // Helper: parseFloat die komma als decimaal accepteert
    const pf = (v) => {
      if (v === null || v === undefined || v === '') return 0;
      const n = parseFloat(String(v).replace(',', '.'));
      return isNaN(n) ? 0 : n;
    };
    const pi = (v) => {
      if (v === null || v === undefined || v === '') return 0;
      const n = parseInt(String(v).replace(',', '.'), 10);
      return isNaN(n) ? 0 : n;
    };

    try {
      if (isNewFamilie && form.familie) {
        const updatedFamilies = { ...(params?.families || {}), [form.familie]: form.prijzen };
        const updatedParams = { ...params, families: updatedFamilies };
        await API.put('/artikel-params', updatedParams);
        setParams(updatedParams);
      }

      // Normaliseer prijzen-object: komma → float
      const normPrijzen = Object.fromEntries(
        Object.entries(form.prijzen || {}).map(([k, v]) => [k, pf(v)])
      );

      const data = {
        categorie: form.categorie || cats[0] || '',
        art_code: form.art_code, ean: form.ean || '', omschrijving: form.omschrijving,
        eenheid: form.eenheid, pack_grootte: pf(form.pack_grootte),
        prijs_basis: form.eenheid === 'pack' && form.prijs_basis === 'pack' ? 'pack' : 'stuk',
        aankoopprijs: pf(form.aankoopprijs), btw: pi(form.btw) || 21,
        leverancier: form.leverancier, familie: form.familie, prijzen: normPrijzen,
        voorraad: pf(form.voorraad), min_voorraad: pf(form.min_voorraad),
        gewicht_kg: pf(form.gewicht_kg), volume_m3: pf(form.volume_m3),
        hs_code: (form.hs_code || '').trim(), oorsprong_land: (form.oorsprong_land || '').trim().toUpperCase(),
        hs_beschrijving: (form.hs_beschrijving || '').trim(),
        hs_motivatie: (form.hs_motivatie || '').trim(),
        varianten_actief: !!form.varianten_actief,
        dimensies: form.dimensies || [],
        varianten: form.varianten || [],
        toon_ean_op_document: !!form.toon_ean_op_document,
        eenheid_aantal: pf(form.eenheid_aantal),
        eenheid_label: (form.eenheid_label || '').trim(),
        toon_eenheid_prijs: form.toon_eenheid_prijs !== false,
        catalogus_verbergen: !!form.catalogus_verbergen,
        actie: !!form.actie, actie_tot: form.actie ? (form.actie_tot || '') : '', actie_tekst: form.actie ? (form.actie_tekst || '').trim() : '',
        vertalingen: form.vertalingen || {},
        foto_url: form.foto_url || '',  // ✅ foto meesturen bij POST (zonder deze regel ging foto verloren bij dupliceren)
        leveranciers: (form.leveranciers || []).filter(l => (l.bedrijf || '').trim()).map(l => ({
          leverancier_id: l.leverancier_id || null,
          bedrijf: (l.bedrijf || '').trim(),
          aankoopprijs: pf(l.aankoopprijs),
          leveringstermijn_dagen: pi(l.leveringstermijn_dagen),
          moq: parseFloat(l.moq) || 0,
          actief: l.actief !== false,
        })),
      };

      let savedArt;
      // Belangrijke fallback: ook als `editId` nog niet door react-state-update gegaan is,
      // gebruik `form.id` (door eerdere save al gezet) zodat we PUT doen i.p.v. POST.
      const targetId = editId || form.id;
      if (targetId) {
        const res = await API.put(`/artikels/${targetId}`, data);
        savedArt = res.data;
      } else {
        const res = await API.post('/artikels', data);
        savedArt = res.data;
      }
      if (!opts?.silent) {
        if (nieuwPendingArt) {
          toast.success(`${savedArt.art_code} bijgewerkt`);
        } else if (targetId) {
          toast.success(`${savedArt.art_code || 'Artikel'} bijgewerkt`);
        } else {
          toast.success(`${savedArt.art_code || 'Artikel'} aangemaakt`);
          setEditId(savedArt.id);
          setForm(p => ({ ...p, id: savedArt.id }));
          setGenAsk(null);
        }
      }
      setSaving(false);
      load();
      return savedArt;
    } catch (err) {
      setSaving(false);
      // Toon toast met de backend-foutmelding (bv. "Artikelcode 'XYZ' bestaat al — …")
      const msg = err?.response?.data?.detail || err?.message || 'Onbekende fout';
      const status = err?.response?.status;
      if (status === 409) {
        toast.error('Conflict — artikel niet opgeslagen', { description: msg, duration: 8000 });
      } else if (status === 403) {
        toast.error('Niet toegestaan', { description: msg });
      } else {
        toast.error('Opslaan mislukt', { description: msg });
      }
      // Re-throw voor callers die een specifieke flow doen (bv. VariantenPanel.regenereer met silent=true).
      // Bij gewone UI-klik (geen silent) NIET re-throwen — de toast volstaat en anders krijgt de
      // gebruiker een "Uncaught runtime error" overlay.
      if (opts?.silent) throw err;
    }
  };

  const startEdit = (a) => {
    fillFormFromArtikel(a);
    // Extra velden die enkel via Artikel-bewerken (niet via 'kies bestaand') gebruikt worden
    setForm(p => ({ ...p, import_tarief: a.import_tarief || null }));
    setEditId(a.id);
    setGenAsk(false); // geen vraag tonen bij bewerken
    setTab('nieuw');
  };

  // ─── Dupliceer een bestaand artikel als basis voor een nieuw artikel ─────
  // Laadt alle gegevens van het bron-artikel in het formulier, maar wist
  // unieke identifiers (id, ean, variant-codes) zodat de gebruiker het kan
  // opslaan als nieuw artikel zonder uniciteitsconflict.
  const dupliceerArtikel = (a) => {
    const bron = artikels.find(x => x.id === a.id) || a;
    fillFormFromArtikel(bron);
    // Reset unieke velden + voorraad (start telkens vers)
    setForm(p => ({
      ...p,
      id: undefined,
      art_code: `${bron.art_code || ''}-KOPIE`.replace(/^-KOPIE$/, 'KOPIE'),
      ean: '',
      voorraad: '',
      // 🧹 Pack-gegevens leegmaken — typische valkuil bij duplicatie:
      // gebruiker dupliceert vaak een artikel met ANDER pack-formaat (bv. 60st→25st),
      // maar vergat tot nu toe `eenheid_aantal` aan te passen. Resultaat: shop & facturen
      // toonden de oude pack-totaal. Door deze velden leeg te zetten, dwingen we
      // de gebruiker om bewust de juiste waarden in te vullen.
      pack_grootte: '',
      eenheid_aantal: '',
      eenheid_label: '',
      // Variant-codes & EAN's leegmaken zodat user ze opnieuw kan genereren
      varianten: (bron.varianten || []).map(v => ({
        ...v,
        art_code: '',
        ean: '',
        voorraad: 0,
      })),
      // import_tarief overnemen indien aanwezig (geen unieke veld)
      import_tarief: bron.import_tarief || null,
      // Vlag om in de UI een banner te tonen "controleer pack-gegevens"
      __duplicaat_pack_reset: true,
    }));
    setEditId(null);          // → POST = nieuw artikel
    setNieuwPendingArt(null); // → toon geen "voorraad toevoegen"-chip
    setGenAsk(false);
    setTab('nieuw');
    toast.success(`Artikel "${bron.art_code || bron.omschrijving}" gedupliceerd`, {
      description: '⚠️ Vul opnieuw pack-grootte, eenheid_aantal en eenheid_label in voor het nieuwe artikel!',
      duration: 7000,
    });
  };

  const delArtikel = async (id) => {
    if (!window.confirm('Artikel verwijderen?')) return;
    await API.delete(`/artikels/${id}`);
    load();
  };

  const eenheidLabel = (e) => eenheden.find(x => x.value === e)?.label || e;

  const exportCSV = async () => {
    try {
      // Gebruik /artikels/export-csv (compatibel met de import-parser én de blanco template)
      // ipv het oude /artikels/export endpoint dat 'prijs_basic' kolommen produceerde (incompatibel).
      const r = await API.get('/artikels/export-csv', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/csv;charset=utf-8' }));
      const a = document.createElement('a');
      a.href = url;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `artikels-export-${today}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch { alert('Export mislukt'); }
  };
  const importCSV = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    const text = await file.text();
    try {
      const r = await API.post('/artikels/import', text, { headers: { 'Content-Type': 'text/plain' } });
      setImportMsg(`${r.data.imported} artikels geimporteerd`);
      load();
      setTimeout(() => setImportMsg(''), 3000);
    } catch { setImportMsg('Import mislukt'); }
    e.target.value = '';
  };

  if (!params) return <div className="text-gray-400">Laden...</div>;

  return (
    <div data-testid="artikels-page" className={nieuwOntvRegels.length > 0 ? 'pb-32' : ''}>
      <h1 className="page-heading">Artikels</h1>

      {/* Tabs */}
      <div className="flex border-b-2 border-gray-200 mb-6" data-testid="artikel-tabs">
        {TABS.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => { setTab(t.key); if (t.key !== 'nieuw') { setEditId(null); setForm({...emptyForm}); } }}
              className={`flex items-center gap-2 px-5 py-3 text-sm font-semibold transition-colors -mb-[2px]
                ${tab === t.key ? 'border-b-[3px] border-[#002FA7] text-[#002FA7]' : 'border-b-[3px] border-transparent text-gray-500 hover:text-gray-800'}`}
              data-testid={`tab-${t.key}`}
            >
              <Icon size={16} weight={tab === t.key ? 'bold' : 'regular'} /> {t.label}
            </button>
          );
        })}
      </div>

      {/* ─── Alle Artikels Tab ──────────────────────────────────── */}
      {tab === 'lijst' && (
        <div data-testid="lijst-tab">
          <div className="article-filter-bar mb-4" data-testid="article-filter-bar">
            <div className="relative flex-1" style={{ maxWidth: 280 }}>
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input className="field-input" style={{ paddingLeft: 36 }} placeholder="Zoek op code of naam..." value={q} onChange={e => setQ(e.target.value)} data-testid="art-search" />
            </div>
            <select className="field-input" style={{ maxWidth: 170 }} value={catF} onChange={e => setCatF(e.target.value)} data-testid="art-cat-filter">
              <option value="">Alle categorieen</option>
              {cats.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <button
              type="button"
              className={`btn btn-sm inline-flex items-center gap-1.5 transition-colors ${zonderAankoop ? 'btn-primary' : 'btn-secondary'}`}
              style={zonderAankoop ? { background: '#DC2626', borderColor: '#DC2626', color: '#fff' } : { borderColor: '#DC2626', color: '#DC2626' }}
              onClick={() => setZonderAankoop(!zonderAankoop)}
              title="Toon enkel artikels zonder aankoopprijs (= 0 of leeg)"
              data-testid="art-filter-zonder-aankoop"
            >
              ⚠️ Zonder aankoopprijs ({artikels.filter(a => !(parseFloat(a.aankoopprijs) > 0)).length})
            </button>
            {fotoStatus.count > 0 && (
              <button
                type="button"
                className={`btn btn-sm inline-flex items-center gap-1.5 transition-colors ${alleenFotoProbleem ? 'btn-primary' : 'btn-secondary'}`}
                style={alleenFotoProbleem ? { background: '#D97706', borderColor: '#D97706', color: '#fff' } : { borderColor: '#D97706', color: '#B45309' }}
                onClick={() => setAlleenFotoProbleem(!alleenFotoProbleem)}
                title="Toon enkel artikels waarvan de foto niet op de PDF (leverbon/bestelbon/catalogus) verschijnt"
                data-testid="art-filter-foto-probleem"
              >
                📷 Foto onvindbaar ({fotoStatus.count})
              </button>
            )}
            {fotoStatus.herstelbaar > 0 && isAdmin && (
              <button
                type="button"
                className="btn btn-sm btn-secondary inline-flex items-center gap-1.5"
                style={{ borderColor: '#059669', color: '#047857' }}
                disabled={fotoHerstelBusy}
                onClick={() => { if (window.confirm(`${fotoStatus.herstelbaar} foto('s) automatisch herstellen door de foto van het bron-artikel te kopiëren?`)) herstelFotos(); }}
                title="Kopieert de foto van het bron-artikel (waarnaar de foto-link verwijst) naar dit artikel, zodat ze weer op de PDF verschijnt"
                data-testid="art-foto-herstel-alles"
              >
                {fotoHerstelBusy ? '⏳ Bezig…' : `🔧 Herstel ${fotoStatus.herstelbaar} automatisch`}
              </button>
            )}
            <button
              type="button"
              className={`btn btn-sm inline-flex items-center gap-1.5 transition-colors ${prijsModus !== 'stuk' ? 'btn-primary' : 'btn-secondary'}`}
              style={prijsModus === 'pack'
                ? { background: '#7C3AED', borderColor: '#7C3AED', color: '#fff' }
                : prijsModus === 'beide'
                ? { background: '#0891B2', borderColor: '#0891B2', color: '#fff' }
                : { borderColor: '#7C3AED', color: '#7C3AED' }}
              onClick={() => {
                // 3-staps cyclus: stuk → pack → beide → stuk
                const next = prijsModus === 'stuk' ? 'pack' : prijsModus === 'pack' ? 'beide' : 'stuk';
                setPrijsModus(next);
                try { localStorage.setItem('art_prijs_modus', next); } catch { /* quota */ }
              }}
              title="Klik om te wisselen tussen stukprijs, pack-prijs (= stuk × aantal/pak), of beide naast elkaar"
              data-testid="art-toggle-prijs-modus"
              translate="no"
            >
              <span>📦 Prijs:</span>
              <strong className="ml-1">
                {prijsModus === 'pack' ? 'PACK' : prijsModus === 'beide' ? 'BEIDE' : 'STUK'}
              </strong>
              <span className={`ml-1 inline-flex items-center text-[10px] font-bold px-1.5 py-0.5 rounded ${prijsModus !== 'stuk' ? 'bg-white/25 text-white' : 'bg-purple-50 text-purple-700'}`}>
                {prijsModus === 'stuk' ? 'OFF' : prijsModus === 'pack' ? 'ON' : 'BOTH'}
              </span>
            </button>
            <button
              type="button"
              className={`btn btn-sm inline-flex items-center gap-1.5 transition-colors ${grouped ? 'btn-primary' : 'btn-secondary'}`}
              onClick={toggleGrouped}
              title="Toon alle artikels gegroepeerd per categorie (Schroeven, Bouten, ...) onder elkaar"
              data-testid="art-toggle-gegroepeerd"
            >
              📂 {grouped ? 'Plat overzicht' : 'Gegroepeerd per categorie'}
            </button>
            {grouped && (
              <button
                type="button"
                className="btn btn-sm btn-secondary inline-flex items-center gap-1.5 text-purple-700 border-purple-300 hover:bg-purple-50"
                onClick={() => setCatModalOpen(true)}
                title="Bepaal zelf de volgorde van categorieën. Werkt zowel in de hoofdlijst als in de PDF-catalogus."
                data-testid="art-cat-volgorde-btn"
              >
                ↕️ Volgorde aanpassen
              </button>
            )}
            <div className="article-toolbar" data-testid="article-toolbar">
              <button
                className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                onClick={() => setLabelOpen(true)}
                data-testid="open-label-export"
                title="Barcode-labels exporteren (Dymo / Brother / Avery / custom)"
              >
                <Tag size={14} /> Labels ({Object.values(selected).filter(Boolean).length || filtered.length})
              </button>
              {isAdmin && (
                <button
                  className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                  disabled={aiFotoBusy}
                  onClick={async () => {
                    const zonderFoto = artikels.filter(a => !a.foto_url);
                    const geselecteerde = artikels.filter(a => selected[a.id]);
                    const doel = geselecteerde.length > 0 ? geselecteerde : zonderFoto;
                    if (doel.length === 0) { alert('Alle artikels hebben al een foto.'); return; }
                    const msg = geselecteerde.length > 0
                      ? `AI-foto's genereren voor ${geselecteerde.length} geselecteerde artikel(s)? (~15s/foto)`
                      : `AI-foto's genereren voor ${doel.length} artikel(s) zonder foto? Dit duurt ~15 seconden per artikel.`;
                    if (!window.confirm(msg)) return;
                    setAiFotoBusy(true);
                    setAiFotoProgress(`Bezig met ${doel.length} artikels...`);
                    try {
                      const r = await API.post('/artikels/foto-ai-bulk', {
                        artikel_ids: doel.map(a => a.id),
                        alleen_zonder_foto: geselecteerde.length === 0,
                        max: doel.length,
                      }, { timeout: 600000 });
                      const res = r.data;
                      alert(`✓ ${res.gegenereerd} foto's gegenereerd${res.mislukt > 0 ? ` · ${res.mislukt} mislukt` : ''}.`);
                      await load();
                    } catch (e) {
                      alert('AI-foto generatie mislukt: ' + (e.response?.data?.detail || e.message));
                    } finally {
                      setAiFotoBusy(false);
                      setAiFotoProgress('');
                    }
                  }}
                  data-testid="ai-fotos-genereren"
                  title="Gebruik AI (Gemini Nano Banana) om product-foto's te genereren op basis van de artikel-benaming"
                  style={{ borderColor: '#EC4899', color: '#EC4899' }}
                >
                  {aiFotoBusy ? '⏳ Bezig…' : '🎨 AI Foto\'s'}
                </button>
              )}
              {isAdmin && (
                <button
                  className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                  onClick={async () => {
                    try {
                      // Stap 1: dry-run om de count + breakdown te krijgen
                      const dryR = await API.post('/artikels/foto-cleanup-broken?dry_run=true');
                      const cnt = dryR.data?.broken_count || 0;
                      const herstelbaar = dryR.data?.restorable || 0;
                      const bt = dryR.data?.by_type || {};
                      if (cnt === 0 && herstelbaar === 0) {
                        alert('✅ Geen defecte foto-links gevonden. Alles in orde!');
                        return;
                      }
                      const lijst = (dryR.data?.broken_artikels || []).slice(0, 15).map(b => b.art_code).filter(Boolean).join(', ');
                      const meer = cnt > 15 ? ` en ${cnt - 15} andere` : '';
                      const breakdown = [
                        bt.uploads_missing > 0 ? `• ${bt.uploads_missing} disk-bestand weg (oude uploads)` : null,
                        bt.db_foto_missing > 0 ? `• ${bt.db_foto_missing} DB-foto ontbreekt (MongoDB doc weg)` : null,
                        bt.db_foto_cross_user > 0 ? `• ${bt.db_foto_cross_user} DB-foto bij andere tenant (orphan)` : null,
                        bt.db_foto_empty > 0 ? `• ${bt.db_foto_empty} DB-foto leeg (0 bytes content)` : null,
                        bt.url_mismatch > 0 ? `• ${bt.url_mismatch} URL wijst naar ander artikel zonder foto` : null,
                        bt.extern_dalle > 0 ? `• ${bt.extern_dalle} DALL-E/OpenAI URL (verloopt na 1u ⚠️)` : null,
                        bt.extern_other > 0 ? `• ${bt.extern_other} externe URL` : null,
                        bt.onbekend > 0 ? `• ${bt.onbekend} onbekend formaat` : null,
                        herstelbaar > 0 ? `• ${herstelbaar} artikel(s) zonder foto-link waarvan de foto nog bestaat → wordt automatisch hersteld` : null,
                      ].filter(Boolean).join('\n');
                      const kop = cnt > 0 ? `${cnt} artikel(s) hebben een defecte foto-link:` : 'Geen defecte links, maar er valt wel iets te herstellen:';
                      const msg = `${kop}\n\n${breakdown}\n\n${cnt > 0 ? `Voorbeelden: ${lijst}${meer}\n\n` : ''}Door de links op te schonen toont de app weer een nette placeholder; herstelbare foto's worden automatisch teruggezet.\n\nDoorgaan?`;
                      if (!window.confirm(msg)) return;
                      // Stap 2: echte cleanup
                      const r = await API.post('/artikels/foto-cleanup-broken?dry_run=false');
                      const hersteld = r.data?.restored || 0;
                      alert(`✅ ${r.data?.cleaned || 0} defecte foto-link(s) opgeruimd${hersteld ? `, ${hersteld} foto('s) automatisch hersteld` : ''}. Vernieuw de pagina om het resultaat te zien.`);
                      // Reload artikels list
                      window.location.reload();
                    } catch (e) {
                      alert('Opruimen mislukt: ' + (e.response?.data?.detail || e.message));
                    }
                  }}
                  data-testid="cleanup-broken-fotos-btn"
                  title="Detecteer artikels met een foto-URL waarvan het bestand niet meer bestaat (typisch na een productie-deploy waarbij de uploads-folder leeg werd). De link wordt gewist zodat de mooie 'geen foto' placeholder weer wordt getoond — daarna kun je opnieuw uploaden."
                  style={{ borderColor: '#DC2626', color: '#DC2626' }}
                >
                  🧹 Defecte foto-links
                </button>
              )}
              {isAdmin && (
                <button
                  className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                  onClick={async () => {
                    const code = window.prompt('Geef de artikel-code in om te diagnosticeren (bv. "CW502600", "AFH250MM"):');
                    if (!code || !code.trim()) return;
                    try {
                      const r = await API.get(`/artikels/foto-diagnose/${encodeURIComponent(code.trim())}`);
                      const d = r.data;
                      if (!d.found) {
                        alert(`Artikel "${code}" niet gevonden.`);
                        return;
                      }
                      const report = [
                        `📋 Diagnose voor: ${d.art_code} (id=${d.id})`,
                        ``,
                        `foto_url: ${d.foto_url || '(leeg)'}`,
                        `type: ${d.foto_url_type || '-'}`,
                        `AI gegenereerd: ${d.foto_ai_generated ? 'ja' : 'nee'}`,
                        `Geüpload op: ${d.foto_uploaded_at || '-'}`,
                        ``,
                        `── MongoDB ──`,
                        `Document bestaat: ${d.has_db_doc ? '✓' : '✗'}`,
                        `Content aanwezig: ${d.has_db_content ? '✓' : '✗'}`,
                        `Size: ${d.db_size || 0} bytes`,
                        `User-ID match: ${d.db_user_id_match === null ? '-' : (d.db_user_id_match ? '✓' : '✗ (orphan)')}`,
                        ``,
                        `── Object Storage ──`,
                        `Storage-path: ${d.storage_path || '-'}`,
                        `Bereikbaar in storage: ${d.storage_reachable == null ? '-' : (d.storage_reachable ? '✓' : '✗')}`,
                        ``,
                        `── Disk ──`,
                        `Bestandsnaam: ${d.disk_file || '-'}`,
                        `Bestaat op disk: ${d.disk_exists ? '✓' : '✗'}`,
                        ``,
                        `── Resultaat ──`,
                        d.conclusion || '-',
                      ].join('\n');
                      alert(report);
                    } catch (e) {
                      alert('Diagnose mislukt: ' + (e.response?.data?.detail || e.message));
                    }
                  }}
                  data-testid="diagnose-foto-btn"
                  title="Diagnose een specifiek artikel: vul de art_code in en zie precies wat er met zijn foto aan de hand is."
                  style={{ borderColor: '#7C3AED', color: '#7C3AED' }}
                >
                  🔍 Diagnose foto
                </button>
              )}
              {isAdmin && (
                <button
                  className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                  onClick={async () => {
                    try {
                      const r = await API.get('/artikels/export-csv', { responseType: 'blob' });
                      const url = URL.createObjectURL(r.data);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `artikels-export-${new Date().toISOString().slice(0, 10)}.csv`;
                      a.click();
                      URL.revokeObjectURL(url);
                    } catch (e) {
                      alert('Export mislukt: ' + (e.response?.data?.detail || e.message));
                    }
                  }}
                  data-testid="export-artikels-csv"
                  title="Exporteer alle artikels als CSV (zelfde kolommen als blanco template → bewerk in Excel → terug importeren)"
                  style={{ borderColor: '#0891B2', color: '#0891B2' }}
                >
                  📤 Export volledig
                </button>
              )}
              {isAdmin && (
                <button
                  className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                  onClick={() => setImportOpen(true)}
                  data-testid="open-ai-import"
                  title="AI-gestuurde artikel-import uit CSV/Excel"
                  style={{ borderColor: '#7C3AED', color: '#7C3AED' }}
                >
                  <Sparkle size={14} weight="fill" /> AI Import
                </button>
              )}
              {isAdmin && (
                <button
                  className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                  onClick={() => setWizardOpen(true)}
                  data-testid="open-prijs-wizard"
                  title="Alle prijzen in 1 klik aanpassen met een percentage"
                  style={{ borderColor: '#059669', color: '#059669' }}
                >
                  📈 Prijs-wizard
                </button>
              )}
              {isAdmin && (
                <button
                  className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                  onClick={async () => {
                    if (bulkEenheidBusy) return;
                    const ok = await confirm({
                      titel: 'Eenheidsprijs bulk-extractie',
                      bericht: 'Loop ALLE artikels af en vul automatisch eenheid_aantal + eenheid_label in (regex eerst, AI als fallback). Bestaande waarden worden niet overschreven.',
                      bevestig: 'Start',
                    });
                    if (!ok) return;
                    setBulkEenheidBusy(true);
                    toast.info('AI eenheidsprijs bulk-extractie gestart…', { description: 'Dit kan tot enkele minuten duren bij grote artikellijsten' });
                    try {
                      const r = await API.post('/artikels/extract-eenheid-bulk', { alleen_lege: true, gebruik_ai: true });
                      const d = r.data || {};
                      toast.success(`${d.bijgewerkt} artikel(s) bijgewerkt`, {
                        description: `regex: ${d.via_regex} · AI: ${d.via_ai} · geen match: ${d.overgeslagen_geen_match} · al ingevuld: ${d.overgeslagen_al_ingevuld}`,
                        duration: 8000,
                      });
                      load();
                    } catch (e) {
                      toast.error('Bulk-extractie mislukt', { description: e?.response?.data?.detail || e.message });
                    } finally {
                      setBulkEenheidBusy(false);
                    }
                  }}
                  disabled={bulkEenheidBusy}
                  data-testid="art-bulk-eenheid"
                  title="Loop alle artikels en vul eenheid_aantal + eenheid_label automatisch in"
                  style={{ borderColor: '#D97706', color: '#D97706' }}
                >
                  <Sparkle size={14} weight="duotone" /> {bulkEenheidBusy ? 'Bezig…' : 'AI eenheidsprijs bulk'}
                </button>
              )}
              {isAdmin && Object.values(selected).filter(Boolean).length > 0 && (
                <>
                  <label
                    className="btn btn-secondary btn-sm inline-flex items-center gap-1.5 cursor-pointer"
                    style={{ borderColor: '#7C3AED', color: '#7C3AED' }}
                    data-testid="art-bulk-foto"
                    title="Upload 1 foto en pas ze toe op alle aangevinkte artikels"
                  >
                    <UploadSimple size={14} />
                    Foto globaal ({Object.values(selected).filter(Boolean).length})
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        const ids = Object.keys(selected).filter(id => selected[id]);
                        if (ids.length === 0) return;
                        if (!window.confirm(`Foto "${f.name}" toepassen op ${ids.length} geselecteerde artikel(s)?\n\nBestaande foto's worden vervangen.`)) {
                          e.target.value = '';
                          return;
                        }
                        const fd = new FormData();
                        fd.append('file', f);
                        fd.append('artikel_ids', ids.join(','));
                        try {
                          const r = await API.post('/artikels/foto-bulk', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 });
                          const res = r.data;
                          alert(`✓ ${res.toegepast} artikel(s) bijgewerkt${res.mislukt > 0 ? ` · ${res.mislukt} mislukt` : ''}${res.niet_gevonden > 0 ? ` · ${res.niet_gevonden} niet gevonden` : ''}.`);
                          setSelected({});
                          load();
                        } catch (err) {
                          alert('Bulk foto-upload mislukt: ' + (err.response?.data?.detail || err.message));
                        } finally {
                          e.target.value = '';
                        }
                      }}
                    />
                  </label>

                  {(() => {
                    // "Foto → overige" — pakt automatisch de foto van het EERSTE geselecteerde artikel
                    // met foto (volgens de volgorde in de lijst) en kopieert naar alle andere geselecteerde.
                    // Gebruiker hoeft geen nummer meer te typen.
                    const selIds = Object.keys(selected).filter(id => selected[id]);
                    if (selIds.length < 2) return null;
                    // Bewaar de volgorde van de lijst (paged/filtered): eerste met foto = bron
                    const ordered = filtered.filter(a => selIds.includes(a.id));
                    const bron = ordered.find(a => a.foto_url);
                    if (!bron) return null;
                    const doelCount = selIds.length - 1;
                    return (
                      <button
                        className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                        style={{ borderColor: '#059669', color: '#059669' }}
                        data-testid="art-foto-kopieer"
                        title={`Kopieer foto van "${bron.art_code}" naar de ${doelCount} andere geselecteerde artikel(s)`}
                        onClick={async () => {
                          const doel = selIds.filter(id => id !== bron.id);
                          if (doel.length === 0) { alert('Selecteer minstens 1 ander artikel om naar te kopiëren.'); return; }
                          if (!window.confirm(`Foto van bovenste artikel "${bron.art_code}" kopiëren naar ${doel.length} geselecteerd(e) artikel(s)?\n\nBestaande foto's worden vervangen.`)) return;
                          try {
                            const r = await API.post('/artikels/foto-kopieer', { bron_id: bron.id, doel_ids: doel });
                            const res = r.data;
                            alert(`✓ ${res.toegepast} artikel(s) bijgewerkt${res.mislukt > 0 ? ` · ${res.mislukt} mislukt` : ''}.`);
                            setSelected({});
                            load();
                          } catch (err) {
                            alert('Kopiëren mislukt: ' + (err.response?.data?.detail || err.message));
                          }
                        }}
                      >
                        📋 Foto → overige ({doelCount})
                      </button>
                    );
                  })()}

                  {(() => {
                    // Bulk: artikels uit de catalogus halen (of terugzetten)
                    const selIds = Object.keys(selected).filter(id => selected[id]);
                    if (selIds.length === 0) return null;
                    const selArts = artikels.filter(a => selIds.includes(a.id));
                    const alleVerborgen = selArts.length > 0 && selArts.every(a => a.catalogus_verbergen);
                    const nieuweWaarde = !alleVerborgen;
                    return (
                      <button
                        className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                        style={{ borderColor: '#4B5563', color: '#4B5563' }}
                        data-testid="art-bulk-catalogus-verbergen"
                        title={nieuweWaarde ? 'Deze artikels nooit meer in de catalogus-PDF afdrukken' : 'Deze artikels weer in de catalogus-PDF toelaten'}
                        onClick={async () => {
                          if (!window.confirm(`${selIds.length} artikel(s) ${nieuweWaarde ? 'UITSLUITEN van' : 'weer TOELATEN in'} de catalogus-PDF?`)) return;
                          let okC = 0, fail = 0;
                          for (const id of selIds) {
                            try { await API.put(`/artikels/${id}`, { catalogus_verbergen: nieuweWaarde }); okC++; } catch { fail++; }
                          }
                          toast[fail ? 'warning' : 'success'](`${okC} artikel(s) bijgewerkt${fail ? ` · ${fail} mislukt` : ''}`);
                          setSelected({});
                          load();
                        }}
                      >
                        <Prohibit size={14} weight="bold" />
                        {nieuweWaarde ? `Niet in catalogus (${selIds.length})` : `Weer in catalogus (${selIds.length})`}
                      </button>
                    );
                  })()}

                  <button
                    className="btn btn-danger btn-sm inline-flex items-center gap-1.5"
                    onClick={async () => {
                      const ids = Object.keys(selected).filter(id => selected[id]);
                      if (ids.length === 0) return;
                      const ok = await confirm({
                        title: `${ids.length} artikel(en) verwijderen`,
                        subtitle: 'Definitief verwijderen — niet omkeerbaar.',
                        message: `Je staat op het punt om ${ids.length} artikel(en) te verwijderen. Deze actie kan niet ongedaan worden gemaakt.`,
                        tone: 'danger',
                        confirmText: `Verwijder ${ids.length}`,
                        confirmWord: ids.length >= 5 ? 'VERWIJDER' : null,
                      });
                      if (!ok) return;
                      let okC = 0, fail = 0;
                      for (const id of ids) {
                        try { await API.delete(`/artikels/${id}`); okC++; } catch { fail++; }
                      }
                      setSelected({});
                      load();
                      alert(`${ok} verwijderd${fail > 0 ? ` · ${fail} mislukt` : ''}.`);
                    }}
                    data-testid="art-bulk-delete"
                    title="Geselecteerde artikels verwijderen (admin)"
                  >
                    <Trash size={14} /> Verwijder ({Object.values(selected).filter(Boolean).length})
                  </button>
                </>
              )}
              <button className="btn btn-secondary btn-sm" onClick={exportCSV} data-testid="export-artikels" title="Exporteer de artikellijst als CSV"><DownloadSimple size={14} /> Export lijst</button>
              <button
                className="btn btn-secondary btn-sm inline-flex items-center gap-1"
                onClick={() => setCatModalOpen(true)}
                data-testid="catalogus-pdf-btn"
                title="Genereer een PDF-catalogus met foto's en de gekozen prijsgroep"
              >
                📕 Catalogus PDF
              </button>
              <button
                className="btn btn-secondary btn-sm inline-flex items-center gap-1"
                data-testid="data-check-btn"
                title="Scan alle artikels op verdachte eenheid- en prijs-data"
                onClick={async () => {
                  setDcOpen(true);
                  setDcLoading(true);
                  setDcResult(null);
                  try {
                    const r = await API.get('/artikels/data-check');
                    setDcResult(r.data);
                  } catch (e) {
                    toast.error('Fout bij scan: ' + (e.response?.data?.detail || e.message));
                  } finally {
                    setDcLoading(false);
                  }
                }}
              >
                <Warning size={14} /> Data-controle
              </button>
              <label className="btn btn-secondary btn-sm cursor-pointer" data-testid="import-artikels"><UploadSimple size={14} /> Import CSV<input type="file" accept=".csv" className="hidden" onChange={importCSV} /></label>
              <button
                className="btn btn-secondary btn-sm inline-flex items-center gap-1 text-purple-700 border-purple-300 hover:bg-purple-50"
                onClick={async () => {
                  try {
                    const apiBase = process.env.REACT_APP_BACKEND_URL;
                    const tok = localStorage.getItem('emergent_billing_token') || '';
                    const r = await fetch(`${apiBase}/api/artikels/foto-export-zip`, {
                      headers: { Authorization: `Bearer ${tok}` },
                    });
                    if (!r.ok) { toast.error('Export mislukt (' + r.status + ')'); return; }
                    const blob = await r.blob();
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `artikel-fotos-${new Date().toISOString().slice(0,10)}.zip`;
                    a.click();
                    URL.revokeObjectURL(url);
                    toast.success(`ZIP gedownload (${r.headers.get('X-Foto-Count') || '?'} foto's)`);
                  } catch (e) {
                    toast.error('Fout: ' + e.message);
                  }
                }}
                title="Download alle foto's als ZIP (genaamd naar art_code) — handig om foto's tussen Preview/Productie te kopiëren"
                data-testid="fotos-export-zip"
              >
                📦 Export foto's
              </button>
              <label
                className="btn btn-secondary btn-sm cursor-pointer text-purple-700 border-purple-300 hover:bg-purple-50 inline-flex items-center gap-1"
                title="Upload een ZIP met foto's. Filenamen = art_code (bv. AFST620G.jpg). Elke file wordt automatisch aan het juiste artikel gekoppeld."
                data-testid="fotos-import-zip"
              >
                📤 Import foto's
                <input
                  type="file"
                  accept=".zip"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (!window.confirm(`Upload "${f.name}" (${(f.size/1024/1024).toFixed(1)}MB)?\n\nElke foto wordt gekoppeld via art_code als filename.`)) {
                      e.target.value = '';
                      return;
                    }
                    const fd = new FormData();
                    fd.append('file', f);
                    try {
                      const r = await API.post('/artikels/foto-import-zip', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      const d = r.data || {};
                      toast.success(`✅ ${d.geïmporteerd} foto's geïmporteerd. ${d.geskipt_geen_match} skipped (geen art_code match), ${d.mislukt} failed.`);
                      load();
                    } catch (err) {
                      toast.error('Import mislukt: ' + (err.response?.data?.detail || err.message));
                    } finally {
                      e.target.value = '';
                    }
                  }}
                />
              </label>
            </div>
            <span className="article-toolbar-count" data-testid="article-visible-count">{filtered.length} artikel(en)</span>
          </div>
          {importMsg && <div className="mb-3 p-2 border border-green-200 bg-green-50 text-green-800 text-sm">{importMsg}</div>}

          <div className="section-card">
            <div className="section-body overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th style={{ width: 28 }}>
                      <input
                        type="checkbox"
                        checked={filtered.length > 0 && filtered.every(a => selected[a.id])}
                        onChange={e => {
                          const next = { ...selected };
                          if (e.target.checked) filtered.forEach(a => { next[a.id] = true; });
                          else filtered.forEach(a => { delete next[a.id]; });
                          setSelected(next);
                        }}
                        data-testid="art-select-all"
                      />
                    </th>
                    <th>Code</th><th>EAN</th><th>Omschrijving</th><th>Categorie</th><th>Eenheid</th>{aActies.visible.col_familie && <th>Familie</th>}
                    {groepen.map(g => (
                      <th key={g.key} className="right" style={{ color: g.color }}>
                        {g.label}
                        <span className="ml-1 text-[9px] font-bold opacity-70">
                          {prijsModus === 'beide' ? '/st + /pak' : prijsModus === 'pack' ? '/pak' : '/st'}
                        </span>
                      </th>
                    ))}
                    <th className="right">Aankoop</th><th>BTW</th><th style={{ minWidth: 90 }}>{aActies.Header}</th>
                  </tr>
                  {/* 🔀 Sub-rij met klikbare sort-knoppen (per kolom). Klik 1×=asc, 2×=desc, 3×=reset */}
                  <tr className="bg-gray-50 border-t border-gray-200" style={{ fontSize: '10px' }}>
                    <th></th>
                    {[
                      { col: 'art_code', label: 'Code' },
                      { col: 'ean', label: 'EAN' },
                      { col: 'omschrijving', label: 'Omschrijving' },
                      { col: 'categorie', label: 'Categorie' },
                      { col: 'eenheid', label: 'Eenheid' },
                    ].map(({ col, label }) => {
                      const active = sortBy?.col === col;
                      const arrow = active ? (sortBy.dir === 'asc' ? '▲' : '▼') : '⇅';
                      return (
                        <th key={col} style={{ padding: '4px 6px' }}>
                          <button
                            type="button"
                            onClick={() => toggleSort(col)}
                            className={`text-[10px] uppercase tracking-wider font-semibold hover:text-[#002FA7] transition-colors ${active ? 'text-[#002FA7]' : 'text-gray-400'}`}
                            title={`Sorteer op ${label}${active ? ` (${sortBy.dir === 'asc' ? 'oplopend' : 'aflopend'} — klik nog eens voor reset)` : ''}`}
                            data-testid={`sort-${col}`}
                          >
                            {arrow} {label}
                          </button>
                        </th>
                      );
                    })}
                    {aActies.visible.col_familie && <th></th>}
                    {groepen.map(g => <th key={g.key}></th>)}
                    <th style={{ padding: '4px 6px' }} className="right">
                      <button
                        type="button"
                        onClick={() => toggleSort('aankoop')}
                        className={`text-[10px] uppercase tracking-wider font-semibold hover:text-[#002FA7] transition-colors ${sortBy?.col === 'aankoop' ? 'text-[#002FA7]' : 'text-gray-400'}`}
                        title="Sorteer op aankoopprijs"
                        data-testid="sort-aankoop"
                      >
                        {sortBy?.col === 'aankoop' ? (sortBy.dir === 'asc' ? '▲' : '▼') : '⇅'} Aankoop
                      </button>
                    </th>
                    <th style={{ padding: '4px 6px' }}>
                      <button
                        type="button"
                        onClick={() => toggleSort('btw')}
                        className={`text-[10px] uppercase tracking-wider font-semibold hover:text-[#002FA7] transition-colors ${sortBy?.col === 'btw' ? 'text-[#002FA7]' : 'text-gray-400'}`}
                        title="Sorteer op BTW-percentage"
                        data-testid="sort-btw"
                      >
                        {sortBy?.col === 'btw' ? (sortBy.dir === 'asc' ? '▲' : '▼') : '⇅'} BTW
                      </button>
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {/* Helper: renderArtRow → DRY zodat we 'm in zowel platte als gegroepeerde modus kunnen gebruiken */}
                  {(() => {
                    const renderArtRow = (a) => (
                    <tr
                      key={a.id}
                      className="cursor-pointer hover:bg-[#F0F4FF] transition-colors"
                      onClick={() => startEdit(a)}
                      data-testid={`art-row-${a.id}`}
                    >
                      <td onClick={e => e.stopPropagation()}>
                        <input
                          type="checkbox"
                          checked={!!selected[a.id]}
                          onChange={e => setSelected(p => { const n = { ...p }; if (e.target.checked) n[a.id] = true; else delete n[a.id]; return n; })}
                          data-testid={`art-select-${a.id}`}
                        />
                      </td>
                      <td className="mono text-xs font-medium">
                        <div className="flex items-center gap-2">
                          {a.foto_url ? (
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${a.foto_url}`}
                              alt=""
                              style={{ width: 56, height: 56, objectFit: 'cover', border: fotoStatus.map[a.id] ? '2px solid #D97706' : '1px solid #e5e7eb', flexShrink: 0, borderRadius: 4, cursor: 'zoom-in' }}
                              title={fotoStatus.map[a.id] ? fotoStatus.map[a.id].reason : (a.foto_ai_generated ? 'AI-gegenereerde foto' : 'Heeft foto')}
                              onClick={(e) => { e.stopPropagation(); window.open(`${process.env.REACT_APP_BACKEND_URL}${a.foto_url}`, '_blank'); }}
                              onError={(e) => {
                                // Foto bestaat niet meer op de server (verdwenen na deploy)
                                // → vervang door placeholder zodat geen lelijk broken-icon getoond wordt
                                e.currentTarget.style.display = 'none';
                                const ph = e.currentTarget.nextElementSibling;
                                if (ph) ph.style.display = 'flex';
                              }}
                              data-testid={`art-thumb-${a.id}`}
                            />
                          ) : null}
                          {a.foto_url ? (
                            <div
                              style={{ width: 56, height: 56, background: '#FEF3C7', border: '1px dashed #F59E0B', flexShrink: 0, borderRadius: 4, display: 'none', flexDirection: 'column' }}
                              className="items-center justify-center text-[10px] text-[#92400E] leading-tight text-center px-1"
                              title="Foto-URL bestaat in DB maar bestand is verloren gegaan — opnieuw uploaden a.u.b."
                              data-testid={`art-thumb-broken-${a.id}`}
                            >
                              <span style={{ fontSize: 18 }}>📷</span>
                              <span style={{ fontSize: 9 }}>opnieuw uploaden</span>
                            </div>
                          ) : (
                            <div style={{ width: 56, height: 56, background: '#F3F4F6', border: '1px dashed #D1D5DB', flexShrink: 0, borderRadius: 4 }} className="flex items-center justify-center text-[11px] text-gray-400">geen</div>
                          )}
                          <div className="flex flex-col gap-0.5">
                            <span>{a.art_code || '-'}</span>
                            {a.catalogus_verbergen && (
                              <span
                                className="text-[9px] font-bold px-1 py-0 rounded bg-gray-200 text-gray-700 border border-gray-400 whitespace-nowrap"
                                title="Dit artikel wordt nooit in de catalogus-PDF afgedrukt"
                                data-testid={`art-niet-catalogus-${a.id}`}
                              >
                                🚫 niet in catalogus
                              </span>
                            )}
                            {fotoStatus.map[a.id] && (
                              <span className="inline-flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                <span
                                  className="text-[9px] font-bold px-1 py-0 rounded bg-amber-100 text-amber-800 border border-amber-300 whitespace-nowrap"
                                  title={fotoStatus.map[a.id].reason}
                                  data-testid={`art-foto-warn-${a.id}`}
                                >
                                  ⚠️ foto onvindbaar
                                </span>
                                {fotoStatus.map[a.id].herstelbaar && isAdmin && (
                                  <button
                                    type="button"
                                    className="text-[9px] font-bold px-1 py-0 rounded bg-emerald-50 text-emerald-700 border border-emerald-300 hover:bg-emerald-100 whitespace-nowrap"
                                    disabled={fotoHerstelBusy}
                                    onClick={() => herstelFotos([a.id])}
                                    title={`Kopieer de foto van bron-artikel ${fotoStatus.map[a.id].bron_id} naar dit artikel`}
                                    data-testid={`art-foto-herstel-${a.id}`}
                                  >
                                    🔧 herstel
                                  </button>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="mono text-[10px] text-gray-500" title={a.toon_ean_op_document ? 'Zichtbaar op documenten' : 'Enkel intern'}>
                        {a.ean ? (
                          <span className="inline-flex items-center gap-1">
                            {a.ean}
                            {a.toon_ean_op_document && <span className="text-[8px] font-bold px-1 py-0 bg-green-50 text-green-700 border border-green-200">DOC</span>}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="font-medium">{a.omschrijving || '-'}</td>
                      <td><span className="badge badge-gray">{a.categorie}</span></td>
                      <td className="text-xs">{eenheidLabel(a.eenheid)}{a.eenheid === 'pack' && a.pack_grootte ? ` (${a.pack_grootte} st.)` : ''}{a.eenheid === 'pack' && a.prijs_basis === 'pack' && <span className="ml-1 text-[9px] font-bold text-purple-600" title="Prijzen van dit artikel zijn per pak ingevuld" data-testid={`art-prijs-basis-${a.id}`}>PRIJS/PAK</span>}</td>
                      {aActies.visible.col_familie && <td className="text-xs">{a.familie || '-'}</td>}
                      {groepen.map(g => {
                        const stukPrijs = stukPrijsVan(a, a.prijzen?.[g.key]);
                        const pg = parseInt(a.pack_grootte, 10) || 0;
                        const packPrijs = pg > 0 ? stukPrijs * pg : stukPrijs;
                        // Artikel met prijs ingevuld per pak: in 'stuk'-modus toch de ingevoerde pakprijs tonen (+ PAK-badge)
                        const perPakArt = a.prijs_basis === 'pack' && a.eenheid === 'pack' && pg > 0;
                        // 'beide': toon beide gestapeld (alleen zinvol als pg > 1 én verschil > 0)
                        const heeftBeide = pg > 1 && Math.abs(packPrijs - stukPrijs) > 0.001;
                        if (prijsModus === 'beide' && heeftBeide) {
                          return (
                            <td
                              key={`${g.key}-dual`}
                              className="right"
                              style={{ color: g.color, fontWeight: 500, lineHeight: 1.15, padding: '4px 6px' }}
                              data-testid={`art-rij-prijs-${a.id}-${g.key}`}
                              title={`Stuk ${fmt(stukPrijs)} × ${pg} = Pak ${fmt(packPrijs)}`}
                              translate="no"
                            >
                              <div className="flex flex-col items-end gap-0.5">
                                <span className="text-[10px] font-normal text-gray-500 tabular-nums" data-testid={`art-rij-stuk-${a.id}-${g.key}`}>
                                  <span>{fmt(stukPrijs)}</span>
                                  <span className="opacity-60">/st</span>
                                </span>
                                <span className="text-[12px] font-bold tabular-nums" data-testid={`art-rij-pak-${a.id}-${g.key}`}>
                                  <span>{fmt(packPrijs)}</span>
                                  <span className="text-[9px] font-bold text-purple-600 ml-0.5">PAK</span>
                                </span>
                              </div>
                            </td>
                          );
                        }
                        const toon = (prijsModus === 'pack' || perPakArt) ? packPrijs : stukPrijs;
                        const isPackBerekend = (prijsModus === 'pack' || perPakArt) && pg > 0;
                        return (
                          <td
                            key={`${g.key}-single`}
                            className="right"
                            style={{ color: g.color, fontWeight: 500 }}
                            title={isPackBerekend ? (perPakArt ? `Ingevuld per pak: ${fmt(packPrijs)}/pak = ${fmt(stukPrijs)}/st (${pg} st.)` : `${fmt(stukPrijs)}/st × ${pg} st = ${fmt(packPrijs)}/pak`) : ''}
                            data-testid={`art-rij-prijs-${a.id}-${g.key}`}
                            translate="no"
                          >
                            <span>{fmt(toon)}</span>
                            {isPackBerekend && (
                              <span className="ml-1 text-[9px] font-bold text-purple-600">PAK</span>
                            )}
                          </td>
                        );
                      })}
                      <td className="right">{fmt(a.aankoopprijs)}</td>
                      <td data-testid={`article-vat-${a.id}`}>{vatLabel(a.btw)}</td>
                      <td onClick={e => e.stopPropagation()}>
                        <div className="flex gap-1">
                          {!aActies.anyOn && <span className="text-[10px] text-gray-300">—</span>}
                          {aActies.visible.edit && (
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(a)} data-testid={`edit-art-${a.id}`}><PencilSimple size={14} /></button>
                          )}
                          {aActies.visible.dup && (
                          <button
                            className="btn btn-ghost btn-sm"
                            onClick={() => dupliceerArtikel(a)}
                            data-testid={`dup-art-${a.id}`}
                            title="Dupliceer dit artikel (incl. prijzen, varianten, dimensies)"
                          >
                            <Copy size={14} />
                          </button>
                          )}
                          {isAdmin && aActies.visible.delete && (
                            <button className="btn btn-danger btn-sm" onClick={() => delArtikel(a.id)} data-testid={`del-art-${a.id}`} title="Verwijderen (admin)"><Trash size={12} /></button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                    // Aantal kolommen: select + code + ean + omschr + cat + eenheid + (familie?) + groepen + aankoop + btw + acties
                    const totColspan = 9 + (aActies.visible.col_familie ? 1 : 0) + groepen.length;

                    // Gegroepeerde weergave: render een categorie-header rij + bijhorende artikel-rijen
                    if (grouped && filteredGegroupeerd) {
                      return filteredGegroupeerd.map(([cat, items]) => {
                        const isOpen = !groupClosed[cat];
                        return (
                          <React.Fragment key={cat}>
                            <tr
                              className="cursor-pointer select-none"
                              style={{ background: '#F0F4FF', borderTop: '2px solid #002FA7' }}
                              onClick={() => toggleGroup(cat)}
                              data-testid={`art-group-header-${cat}`}
                            >
                              <td colSpan={totColspan} className="py-2 px-3 font-bold text-[#002FA7] text-sm">
                                <span className="inline-block w-4 text-center mr-1">{isOpen ? '▾' : '▸'}</span>
                                📂 {cat}
                                <span className="ml-2 text-xs font-normal text-gray-600">({items.length} artikel{items.length === 1 ? '' : 's'})</span>
                              </td>
                            </tr>
                            {isOpen && items.map(renderArtRow)}
                          </React.Fragment>
                        );
                      });
                    }
                    // Platte weergave: gewone paginatie
                    return paged.map(renderArtRow);
                  })()}
                  {filtered.length === 0 && (
                    <tr><td colSpan={9 + (aActies.visible.col_familie ? 1 : 0) + groepen.length} className="text-center text-gray-400 py-8">
                      Geen artikels gevonden. <button className="text-[#002FA7] underline" onClick={() => setTab('nieuw')}>Maak een nieuw artikel aan</button>
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
            {!grouped && <Pagination total={filtered.length} pageSize={pageSize} setPageSize={setPageSize} curPage={curPage} setPage={setPage} />}
          </div>
        </div>
      )}

      {/* ─── Nieuw / Bewerk Artikel Tab ────────────────────────── */}
      {tab === 'nieuw' && (
        <div data-testid="nieuw-tab">
          {/* Scan-balk — check of artikel al bestaat + voorraad direct kunnen aanvullen */}
          {(!editId || nieuwPendingArt) && (
            <div
              className="bg-purple-50 border-2 border-purple-300 rounded p-3 mb-3 [&_.field-input]:py-1.5 [&_.field-input]:text-sm [&_.form-label]:text-[10px]"
              style={layoutModus === 'compact' ? { maxWidth: 880, marginLeft: 'auto', marginRight: 'auto' } : undefined}
              data-testid="nieuw-scan-balk"
            >
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <Barcode size={18} weight="duotone" className="text-purple-700" />
                <div className="font-bold text-purple-900">Bestaat dit artikel al?</div>
                <span className="text-[11px] text-purple-700">— bestaand artikel? <strong>kies → typ aantal → ENTER</strong> = voorraad +N · niet gevonden? vul formulier hieronder in</span>
              </div>
              <div className="flex items-center gap-2 flex-wrap md:flex-nowrap relative">
                <div className="flex-1 min-w-[200px] relative">
                  <input
                    ref={nieuwScanRef}
                    type="text"
                    value={nieuwScan}
                    onChange={e => setNieuwScan(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'ArrowDown' && nieuwScanSugg.length > 0) {
                        e.preventDefault(); setNieuwScanIdx(i => Math.min(i + 1, nieuwScanSugg.length - 1));
                      } else if (e.key === 'ArrowUp' && nieuwScanSugg.length > 0) {
                        e.preventDefault(); setNieuwScanIdx(i => Math.max(i - 1, 0));
                      } else if (e.key === 'Enter') {
                        e.preventDefault();
                        if (nieuwPendingArt) { voegVoorraadToe(); return; }
                        if (nieuwScanSugg.length > 0) { kiesBestaand(nieuwScanSugg[nieuwScanIdx] || nieuwScanSugg[0]); return; }
                        // Geen suggesties + minstens 2 letters → spring naar formulier met voorvulling
                        if ((nieuwScan || '').trim().length >= 2 && !nieuwScanLoading) {
                          gaNaarFormulier(nieuwScan);
                        }
                      } else if (e.key === 'Escape') {
                        if (nieuwPendingArt) { annuleerKeuze(); return; }
                        setNieuwScan(''); setNieuwScanSugg([]);
                      }
                    }}
                    placeholder="Scan/typ artikelcode, EAN of (deel van) omschrijving…"
                    autoComplete="off"
                    className="w-full h-11 px-3 text-base font-mono bg-white border-2 border-purple-400 focus:border-purple-700 focus:outline-none rounded"
                    data-testid="nieuw-scan-input"
                  />
                  {nieuwScanSugg.length > 0 && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-white border-2 border-purple-300 rounded shadow-lg max-h-80 overflow-auto">
                      {nieuwScanSugg.map((s, i) => (
                        <div
                          key={s.id}
                          onMouseEnter={() => setNieuwScanIdx(i)}
                          className={`flex items-center gap-3 border-b border-gray-100 last:border-0 ${i === nieuwScanIdx ? 'bg-purple-50' : 'hover:bg-gray-50'}`}
                        >
                          <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); kiesBestaand(s); }}
                            className="flex-1 text-left px-3 py-2 flex items-center gap-3 min-w-0"
                            data-testid={`nieuw-sugg-${i}`}
                          >
                            <div className="font-mono font-bold text-[#002FA7] text-sm shrink-0 w-28 truncate">{s.art_code}</div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm text-gray-900 truncate">{s.omschrijving}</div>
                              <div className="text-[10px] text-gray-500 font-mono">{s.ean ? `EAN ${s.ean}` : ''}{s.categorie ? ` · ${s.categorie}` : ''}</div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-[10px] uppercase text-gray-400">stock</div>
                              <div className="text-sm font-bold text-gray-700">{Number(s.voorraad || 0).toFixed(2).replace('.', ',')}</div>
                            </div>
                          </button>
                          <button
                            type="button"
                            onMouseDown={(e) => { e.preventDefault(); openBestaandArtikel(s); }}
                            className="px-2 py-1 mr-2 text-[10px] font-semibold text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-100"
                            title="Open in bewerk-modus i.p.v. voorraad bij te voegen"
                          >
                            Bewerken
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {nieuwScanSugg.length === 0 && (nieuwScan || '').trim().length >= 2 && !nieuwScanLoading && !nieuwPendingArt && (
                    <div className="absolute z-30 left-0 right-0 mt-1 bg-white border-2 border-green-300 rounded shadow-lg p-3" data-testid="nieuw-geen-banner">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 min-w-0">
                          <CheckCircle size={20} weight="duotone" className="text-[#059669] shrink-0" />
                          <div className="text-sm text-gray-900">
                            <strong>Geen overeenkomstig artikel gevonden</strong>
                            <span className="text-gray-500"> — vul aan in het formulier (typ ENTER of klik →)</span>
                          </div>
                        </div>
                        <button
                          onMouseDown={(e) => { e.preventDefault(); gaNaarFormulier(nieuwScan); }}
                          className="px-3 py-1.5 text-xs font-semibold text-white bg-[#059669] hover:bg-[#047857] rounded inline-flex items-center gap-1.5 shrink-0"
                          data-testid="ga-naar-formulier"
                          title="Spring naar formulier hieronder"
                        >
                          <Plus size={12} weight="bold" /> Aanmaken
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                <input
                  ref={nieuwAantalRef}
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={nieuwAantal}
                  onChange={e => setNieuwAantal(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); if (nieuwPendingArt) voegVoorraadToe(); }
                    else if (e.key === 'Escape' && nieuwPendingArt) {
                      annuleerKeuze();
                    }
                  }}
                  placeholder="Aantal"
                  title="Aantal stuks om toe te voegen"
                  className={`w-24 h-11 px-3 text-base text-right border-2 focus:outline-none rounded bg-white shrink-0 ${nieuwPendingArt ? 'border-[#059669] focus:border-[#047857] ring-2 ring-green-200' : 'border-purple-400 focus:border-purple-700'}`}
                  data-testid="nieuw-aantal-input"
                />
                <button
                  onClick={() => { if (nieuwPendingArt) voegVoorraadToe(); }}
                  disabled={!nieuwPendingArt}
                  className={`h-11 px-4 text-sm font-semibold inline-flex items-center gap-1.5 text-white rounded shrink-0 ${nieuwPendingArt ? 'bg-[#059669] hover:bg-[#047857]' : 'bg-gray-300 cursor-not-allowed'}`}
                  data-testid="nieuw-toevoegen"
                  title={nieuwPendingArt ? 'Toe te voegen aan lijst' : 'Kies eerst een bestaand artikel'}
                >
                  <Truck size={14} weight="bold" /> Voeg toe
                </button>
              </div>

              {/* Pending-chip — toont gekozen artikel klaar voor voorraad-aanvulling */}
              {nieuwPendingArt && (
                <div className="mt-3 flex items-center gap-3 bg-green-50 border-2 border-green-300 rounded p-3" data-testid="nieuw-pending-chip">
                  <CheckCircle size={20} weight="fill" className="text-[#059669] shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-wider text-green-700 font-semibold">Bestaand artikel — formulier hieronder is bewerkbaar</div>
                    <div className="font-mono font-bold text-green-900">{nieuwPendingArt.art_code}</div>
                    <div className="text-xs text-gray-700 truncate">{nieuwPendingArt.omschrijving}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-[10px] uppercase text-gray-500">huidige stock</div>
                    <div className="text-sm font-bold text-gray-900">{Number(nieuwPendingArt.voorraad || 0).toFixed(2).replace('.', ',')}</div>
                  </div>
                  <button
                    onClick={annuleerKeuze}
                    className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded"
                    title="Annuleer keuze (ESC) — terug naar leeg formulier"
                  >
                    <X size={16} />
                  </button>
                </div>
              )}

              <div className="text-[11px] text-purple-700 mt-2">
                Tip: ↑↓ navigeren · ENTER kiest of bevestigt aantal · ESC sluit/annuleert · "Bewerken"-knop opent het volledige artikel-formulier i.p.v. enkel voorraad toe te voegen.
              </div>
            </div>
          )}

          {/* Te verwerken regels — bulk-lijst om in 1 keer te verwerken (persistent per dag in localStorage) */}
          {!editId && nieuwOntvRegels.length > 0 && (() => {
            const filterQ = (nieuwOntvFilter || '').trim().toLowerCase();
            const gefilterd = filterQ
              ? nieuwOntvRegels.filter(r =>
                  (r.art_code || '').toLowerCase().includes(filterQ) ||
                  (r.omschrijving || '').toLowerCase().includes(filterQ) ||
                  (r.ean || '').toLowerCase().includes(filterQ))
              : nieuwOntvRegels;
            const totaalAantal = nieuwOntvRegels.reduce((s, r) => s + (parseFloat(r.aantal) || 0), 0);
            const totaalWaarde = nieuwOntvRegels.reduce((s, r) => s + ((parseFloat(r.aantal) || 0) * (parseFloat(r.aankoopprijs) || 0)), 0);
            const ontbrekendePrijs = nieuwOntvRegels.filter(r => !(parseFloat(r.aankoopprijs) > 0)).length;
            return (
            <div className="bg-white border border-gray-200 rounded overflow-hidden mb-4" data-testid="nieuw-ontv-regels">
              <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200 flex-wrap gap-3">
                <div>
                  <div className="font-bold text-gray-900">
                    Te verwerken regels ({nieuwOntvRegels.length}) <span className="text-[10px] font-normal text-gray-500">— vandaag · {new Date().toLocaleDateString('nl-BE')}</span>
                  </div>
                  <div className="text-[11px] text-gray-500">
                    Totaal {totaalAantal} stuks · waarde <strong className="text-gray-800">{fmt(totaalWaarde)}</strong>
                    {ontbrekendePrijs > 0 && <span className="ml-2 text-amber-600">⚠ {ontbrekendePrijs} regel(s) zonder aankoopprijs</span>}
                    <span className="ml-2 text-amber-600">· bewaard tot middernacht</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {nieuwOntvRegels.length >= 5 && (
                    <input
                      type="text"
                      value={nieuwOntvFilter}
                      onChange={e => setNieuwOntvFilter(e.target.value)}
                      placeholder="Filter regels…"
                      className="px-2 py-1.5 text-xs border border-gray-300 rounded w-44 focus:border-purple-600 focus:outline-none"
                      data-testid="nieuw-ontv-filter"
                    />
                  )}
                  <button
                    onClick={() => setNieuwOntvRegels([])}
                    disabled={nieuwOntvBusy}
                    className="btn btn-sm bg-white border border-gray-300 text-gray-700 hover:bg-gray-50"
                  >
                    Wissen
                  </button>
                  <button
                    onClick={verwerkAlleNieuwOntv}
                    disabled={nieuwOntvBusy}
                    className="btn btn-sm inline-flex items-center gap-1.5 bg-[#059669] text-white hover:bg-[#047857] disabled:bg-gray-300"
                    data-testid="nieuw-ontv-verwerk"
                  >
                    <CheckCircle size={14} /> {nieuwOntvBusy ? 'Bezig…' : 'Verwerken'}
                  </button>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="data-table text-xs">
                  <thead>
                    <tr>
                      <th>Art. code</th>
                      <th>Omschrijving</th>
                      <th className="right">Stock voor</th>
                      <th className="right">Aantal</th>
                      <th className="right">Stock na</th>
                      <th className="right">Aankoop €</th>
                      <th className="right">Waarde</th>
                      <th className="right" style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {gefilterd.map(r => {
                      const aant = parseFloat(r.aantal) || 0;
                      const ap = parseFloat(r.aankoopprijs) || 0;
                      const waarde = aant * ap;
                      const heeftPrijs = ap > 0;
                      return (
                        <tr key={r.tmp_id} data-testid={`nieuw-ontv-rij-${r.art_code}`}>
                          <td className="font-mono font-bold">{r.art_code}</td>
                          <td>{r.omschrijving}</td>
                          <td className="right text-gray-500">{Number(r.voorraad_voor).toFixed(2).replace('.', ',')}</td>
                          <td className="right">
                            <input
                              type="number"
                              step="0.01"
                              min="0.01"
                              value={r.aantal}
                              onChange={e => updNieuwOntvRegel(r.tmp_id, 'aantal', e.target.value)}
                              className="w-20 px-2 py-1 border border-gray-300 rounded text-right text-xs font-bold focus:border-[#059669] focus:outline-none"
                            />
                          </td>
                          <td className="right font-bold text-[#059669]">{Number(r.voorraad_voor + aant).toFixed(2).replace('.', ',')}</td>
                          <td className="right">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={r.aankoopprijs ?? ''}
                              onChange={e => updNieuwOntvRegel(r.tmp_id, 'aankoopprijs', e.target.value)}
                              placeholder="0,00"
                              className={`w-20 px-2 py-1 border rounded text-right text-xs focus:outline-none ${heeftPrijs ? 'border-gray-300 focus:border-[#059669]' : 'border-amber-400 bg-amber-50 focus:border-amber-600'}`}
                              title={heeftPrijs ? 'Aankoopprijs (mag aangepast worden)' : 'Geen aankoopprijs ingesteld — vul in voor correcte stockwaarde'}
                            />
                          </td>
                          <td className={`right font-bold ${heeftPrijs ? 'text-gray-900' : 'text-amber-600'}`}>{fmt(waarde)}</td>
                          <td className="right">
                            <button
                              onClick={() => delNieuwOntvRegel(r.tmp_id)}
                              className="p-1 text-red-500 hover:bg-red-50 rounded"
                              title="Verwijderen uit lijst"
                            >
                              <Trash size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filterQ && gefilterd.length === 0 && (
                      <tr><td colSpan="8" className="text-center text-gray-400 py-4">Geen regels gevonden voor "{filterQ}"</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              {filterQ && (
                <div className="px-3 py-1.5 text-[10px] text-gray-500 bg-gray-50 border-t border-gray-200">
                  {gefilterd.length} van {nieuwOntvRegels.length} regels getoond · <button onClick={() => setNieuwOntvFilter('')} className="text-[#002FA7] hover:underline">filter wissen</button>
                </div>
              )}
            </div>
            );
          })()}

          <div className="section-card" data-overview-form>
            <div className="section-header">
              <span className="section-title">
                {nieuwPendingArt
                  ? <>Bestaand artikel — <span className="text-[#059669]">pas gegevens aan indien nodig</span></>
                  : (editId ? 'Artikel bewerken' : 'Nieuw artikel toevoegen')}
              </span>
              <div className="flex items-center gap-2">
                {/* Layout-toggle: klassiek / compact */}
                <div className="inline-flex items-center bg-gray-100 rounded p-0.5 border border-gray-300">
                  <button
                    type="button"
                    onClick={() => wisselLayout('klassiek')}
                    className={`text-[10px] px-2 py-1 rounded font-bold transition ${layoutModus === 'klassiek' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                    title="Klassieke ruime layout"
                    data-testid="layout-klassiek"
                  >
                    Klassiek
                  </button>
                  <button
                    type="button"
                    onClick={() => wisselLayout('compact')}
                    className={`text-[10px] px-2 py-1 rounded font-bold transition ${layoutModus === 'compact' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}
                    title="Compacte dichte layout"
                    data-testid="layout-compact"
                  >
                    Compact
                  </button>
                </div>
                {(editId && !nieuwPendingArt) && <button className="btn btn-ghost btn-sm" onClick={() => { setEditId(null); setForm({...emptyForm}); }}>Annuleren</button>}
                <button className="btn btn-primary btn-sm" onClick={saveArtikel} disabled={saving} data-testid="save-artikel-top-btn">
                  {saving ? 'Opslaan…' : editId ? '💾 Bijwerken' : '💾 Toevoegen'}
                </button>
              </div>
            </div>
            <div className={layoutModus === 'compact' ? 'art-form-compact' : '[&_.field-input]:py-1.5 [&_.field-input]:text-sm [&_.form-label]:text-[10px] [&_.form-label]:mb-0.5'}>
            <div className="p-4">
              {/* SECTIE 1: Identificatie */}
              <div className="flex items-center gap-2 mb-2 pb-1.5 border-b border-gray-100">
                <IdentificationCard size={16} weight="duotone" className="text-[#002FA7]" />
                <div className="text-sm font-bold text-gray-800">Identificatie</div>
                <div className="text-[10px] text-gray-400">— hoe je dit artikel terugvindt</div>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div className="form-group">
                  <label className="form-label">Categorie</label>
                  <select className="field-input" value={form.categorie} onChange={u('categorie')} data-testid="art-categorie">
                    <option value="">-- Kies --</option>
                    {cats.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Art. code <span className="text-red-500">*</span></label>
                  <input ref={artCodeRef} className="field-input" value={form.art_code} onChange={u('art_code')} data-testid="art-code" />
                </div>
                <div className="form-group form-full" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Omschrijving <span className="text-red-500">*</span></label>
                  <input className="field-input" value={form.omschrijving} onChange={u('omschrijving')} data-testid="art-omschrijving" />
                </div>
              </div>

              {/* Vraag-banner: EAN auto genereren? — alleen bij nieuw artikel, nog geen keuze gemaakt, EAN leeg */}
              {!editId && genAsk === null && !form.ean && (
                <div className="mb-5 flex items-center gap-3 px-4 py-3 border-l-4 border-[#002FA7] bg-[#F0F4FF]" data-testid="ean-gen-banner">
                  <Sparkle size={22} weight="duotone" className="text-[#002FA7] shrink-0" />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-gray-900">Automatisch EAN-barcode genereren?</div>
                    <div className="text-[11px] text-gray-600">Wij maken een unieke EAN-13 aan (intern gebruik — prefix 20). Handig voor eigen producten zonder fabrieksbarcode.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      let ean = generateEan13();
                      // Anti-dupe: probeer max 10x als de EAN al bestaat
                      for (let i = 0; i < 10 && artikels.some(a => a.ean === ean); i++) ean = generateEan13();
                      setForm(p => ({ ...p, ean }));
                      setGenAsk(true);
                    }}
                    className="btn btn-primary btn-sm whitespace-nowrap"
                    data-testid="ean-gen-yes"
                  >
                    Ja, genereer
                  </button>
                  <button
                    type="button"
                    onClick={() => setGenAsk(false)}
                    className="btn btn-ghost btn-sm whitespace-nowrap border border-gray-300"
                    data-testid="ean-gen-no"
                  >
                    Nee, bedankt
                  </button>
                </div>
              )}

              {/* SECTIE 2: Barcode (EAN) */}
              <div className="flex items-center gap-2 mb-2 pt-1.5 pb-1.5 border-b border-gray-100">
                <Barcode size={16} weight="duotone" className="text-[#002FA7]" />
                <div className="text-sm font-bold text-gray-800">Barcode (EAN)</div>
                <div className="text-[10px] text-gray-400">— scan, genereer of typ in</div>
              </div>

              {/* EAN + tonen-op-document toggle */}
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">EAN-code / Barcode</label>
                  <div className="flex gap-2">
                    <input className="field-input font-mono flex-1" value={form.ean} onChange={u('ean')} placeholder="5412345678901" data-testid="art-ean" />
                    <button
                      type="button"
                      onClick={() => {
                        let ean = generateEan13();
                        for (let i = 0; i < 10 && artikels.some(a => a.ean === ean && a.id !== editId); i++) ean = generateEan13();
                        setForm(p => ({ ...p, ean }));
                      }}
                      className="btn btn-ghost btn-sm whitespace-nowrap inline-flex items-center gap-1.5 border border-gray-300 hover:border-[#002FA7] hover:text-[#002FA7]"
                      title="Genereer geldige EAN-13 (intern)"
                      data-testid="art-ean-generate"
                    >
                      <Sparkle size={16} weight="regular" /> Genereer
                    </button>
                    <button
                      type="button"
                      onClick={() => setScannerOpen(true)}
                      className="btn btn-ghost btn-sm whitespace-nowrap inline-flex items-center gap-1.5 border border-gray-300 hover:border-[#002FA7] hover:text-[#002FA7]"
                      title="Scan barcode met camera"
                      data-testid="art-ean-scan"
                    >
                      <Barcode size={18} weight="regular" /> Scannen
                    </button>
                  </div>
                  <div className="text-[10px] text-gray-400 mt-0.5">
                    Zoekbaar in alle artikelzoekers. Standaard verborgen op documenten.
                    {form.ean && !isValidEan13(form.ean) && /^\d{13}$/.test(form.ean) && (
                      <span className="ml-2 text-amber-600 font-medium">⚠ Ongeldige EAN-13 checksum</span>
                    )}
                  </div>
                  {/* Barcode SVG-preview */}
                  {form.ean && (
                    <div className="mt-2 inline-block p-2 bg-white border border-gray-200" data-testid="art-ean-preview">
                      <BarcodePreview value={form.ean} height={44} />
                    </div>
                  )}
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">Tonen op document</label>
                  <div className="flex items-center gap-2 h-[38px]">
                    <IOSToggle checked={!!form.toon_ean_op_document} onChange={v => setForm(p => ({ ...p, toon_ean_op_document: v }))} size="md" testid="art-toon-ean" />
                    <span className="text-xs text-gray-500">{form.toon_ean_op_document ? 'EAN wordt getoond op factuur/offerte/bestelbon' : 'EAN enkel intern (niet op documenten)'}</span>
                  </div>
                </div>
              </div>

              {/* SECTIE 3: Prijzen & BTW */}
              <div className="flex items-center gap-2 mb-2 pt-1.5 pb-1.5 border-b border-gray-100">
                <Coin size={16} weight="duotone" className="text-[#059669]" />
                <div className="text-sm font-bold text-gray-800">Prijzen & BTW</div>
                <div className="text-[10px] text-gray-400">— aankoop, BTW en hoofdleverancier</div>
              </div>
              <div className="grid grid-cols-5 gap-3 mb-3">
                <div className="form-group">
                  <label className="form-label">Eenheid</label>
                  <select className="field-input" value={form.eenheid} onChange={u('eenheid')} data-testid="art-eenheid">
                    {eenheden.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
                  </select>
                </div>
                {form.eenheid === 'pack' && (
                  <div className="form-group">
                    <label className="form-label">Stuks per pack</label>
                    <input type="number" className="field-input" value={form.pack_grootte} onChange={u('pack_grootte')} placeholder="12" data-testid="art-pack-grootte" />
                  </div>
                )}
                {form.eenheid === 'pack' && (
                  <div className="form-group">
                    <label className="form-label">Prijs ingevuld per</label>
                    <select className="field-input" value={form.prijs_basis || 'stuk'} onChange={u('prijs_basis')} data-testid="art-prijs-basis"
                      title="Per stuk: pakprijs = stukprijs × stuks/pak. Per pak: de ingevulde verkoop- én aankoopprijs zijn al de pakprijs.">
                      <option value="stuk">Per stuk (× stuks/pak)</option>
                      <option value="pack">Per pak (prijs = pakprijs)</option>
                    </select>
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Aankoopprijs (excl.){form.eenheid === 'pack' && <span className="text-gray-400 text-[10px]"> · per {perPak ? 'pak' : 'stuk'}</span>}</label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="field-input"
                    value={fmtDec(form.aankoopprijs)}
                    onChange={uDec('aankoopprijs')}
                    placeholder="0,00"
                    data-testid="art-aankoopprijs"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Verkoopprijs (excl.) <span className="text-gray-400 text-[10px]">· hoofd-prijs (Basic) · per {form.eenheid === 'pack' ? (perPak ? 'pak' : 'stuk') : (form.eenheid || 'stuk')}</span></label>
                  <input
                    type="text"
                    inputMode="decimal"
                    className="field-input"
                    value={fmtDec(form.prijzen?.basic)}
                    onChange={uPrijs('basic')}
                    placeholder={form.eenheid === 'pack' ? (perPak ? '0,00 / pak' : '0,00 / st') : '0,00'}
                    data-testid="art-verkoopprijs"
                    style={{ borderColor: '#3B82F6', color: '#3B82F6', fontWeight: 600 }}
                  />
                  {form.eenheid === 'pack' && parseInt(form.pack_grootte, 10) > 0 && parseFloat(form.prijzen?.basic) > 0 && (
                    <div className="text-[10px] font-bold text-purple-700 mt-0.5" data-testid="art-verkoopprijs-pak">
                      {perPak
                        ? `= € ${stukVanIngevoerd(form.prijzen.basic).toFixed(2).replace('.', ',')} / st (${parseInt(form.pack_grootte, 10)} st./pak)`
                        : `= € ${packVanIngevoerd(form.prijzen.basic).toFixed(2).replace('.', ',')} / pak (${parseInt(form.pack_grootte, 10)} st.)`}
                    </div>
                  )}
                  {(parseFloat(form.aankoopprijs) > 0 && parseFloat(form.prijzen?.basic) > 0) && (() => {
                    const ap = parseFloat(form.aankoopprijs);
                    const vp = parseFloat(form.prijzen.basic);
                    const winst = vp - ap;
                    const marge = ap > 0 ? ((winst / ap) * 100) : 0;
                    const c = marge >= 30 ? '#16A34A' : marge >= 10 ? '#D97706' : '#DC2626';
                    return (
                      <div className="text-[10px] font-mono mt-0.5" style={{ color: c }} data-testid="art-marge-info">
                        Winst: € {winst.toFixed(2)} · Marge: {marge.toFixed(1)}%
                      </div>
                    );
                  })()}
                </div>
                <div className="form-group">
                  <label className="form-label">BTW %</label>
                  <select className="field-input" value={form.btw} onChange={u('btw')} data-testid="art-btw">
                    <option value="21">21%</option><option value="6">6%</option><option value="0">0%</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Leverancier</label>
                  <input className="field-input" value={form.leverancier} onChange={u('leverancier')} placeholder="Leverancier BV" data-testid="art-leverancier" />
                  <div className="text-[10px] text-gray-400 mt-0.5">Hoofdleverancier (legacy). Voor meerdere leveranciers → zie tabel hieronder.</div>
                </div>
              </div>

              {/* SECTIE 4: Voorkeurs-leveranciers */}
              <div className="flex items-center gap-2 mb-2 pt-1.5 pb-1.5 border-b border-gray-100">
                <Truck size={16} weight="duotone" className="text-purple-700" />
                <div className="text-sm font-bold text-gray-800">Voorkeurs-leveranciers</div>
                <div className="text-[10px] text-gray-400">— meerdere leveranciers met eigen prijs/levertijd</div>
              </div>
              {/* Voorkeurs-leveranciers: multi-supplier per artikel */}
              <div className="mb-3 border border-gray-200 bg-[#FAFBFC]">
                <div className="px-3 py-2 border-b border-gray-200 flex items-center justify-between">
                  <div>
                    <div className="text-xs font-bold uppercase tracking-wider text-gray-700">Voorkeurs-leveranciers</div>
                    <div className="text-[10px] text-gray-500">Systeem kiest automatisch de goedkoopste actieve leverancier als primaire.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setForm(p => ({
                      ...p,
                      leveranciers: [...(p.leveranciers || []), { bedrijf: '', leverancier_id: null, aankoopprijs: '', leveringstermijn_dagen: '', moq: '', actief: true }],
                    }))}
                    className="btn btn-ghost btn-sm inline-flex items-center gap-1 border border-gray-300 text-[11px]"
                    data-testid="art-add-lev"
                  ><Plus size={12} /> Leverancier toevoegen</button>
                </div>
                {(form.leveranciers || []).length === 0 ? (
                  <div className="px-4 py-3 text-[11px] text-gray-400 italic">Nog geen leveranciers. Klik op "Leverancier toevoegen" om er één toe te voegen.</div>
                ) : (
                  <table className="data-table text-xs">
                    <thead>
                      <tr>
                        <th>Leverancier</th>
                        <th className="right" style={{ width: 120 }}>Aankoopprijs (€)</th>
                        <th className="right" style={{ width: 100 }}>Levertijd (dgn)</th>
                        <th className="right" style={{ width: 80 }}>MOQ</th>
                        <th style={{ width: 70 }}>Actief</th>
                        <th style={{ width: 30 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {form.leveranciers.map((l, i) => (
                        <tr key={i}>
                          <td>
                            <input
                              className="field-input"
                              value={l.bedrijf || ''}
                              list={`lev-list-${i}`}
                              onChange={e => {
                                const naam = e.target.value;
                                const match = leveranciersList.find(x => x.bedrijf === naam);
                                setForm(p => ({
                                  ...p,
                                  leveranciers: p.leveranciers.map((x, j) => j === i ? { ...x, bedrijf: naam, leverancier_id: match?.id || null } : x),
                                }));
                              }}
                              placeholder="Typ of kies leverancier"
                              data-testid={`art-lev-${i}-bedrijf`}
                            />
                            <datalist id={`lev-list-${i}`}>{leveranciersList.map(x => <option key={x.id} value={x.bedrijf} />)}</datalist>
                          </td>
                          <td>
                            <input type="number" step="0.01" min="0"
                              className="field-input text-right"
                              value={l.aankoopprijs || ''}
                              onChange={e => setForm(p => ({ ...p, leveranciers: p.leveranciers.map((x, j) => j === i ? { ...x, aankoopprijs: e.target.value } : x) }))}
                              data-testid={`art-lev-${i}-prijs`} />
                          </td>
                          <td>
                            <input type="number" min="0"
                              className="field-input text-right"
                              value={l.leveringstermijn_dagen || ''}
                              onChange={e => setForm(p => ({ ...p, leveranciers: p.leveranciers.map((x, j) => j === i ? { ...x, leveringstermijn_dagen: e.target.value } : x) }))}
                              data-testid={`art-lev-${i}-termijn`} />
                          </td>
                          <td>
                            <input type="number" min="0"
                              className="field-input text-right"
                              value={l.moq || ''}
                              onChange={e => setForm(p => ({ ...p, leveranciers: p.leveranciers.map((x, j) => j === i ? { ...x, moq: e.target.value } : x) }))}
                              data-testid={`art-lev-${i}-moq`}
                              placeholder="0" />
                          </td>
                          <td>
                            <IOSToggle
                              checked={l.actief !== false}
                              onChange={v => setForm(p => ({ ...p, leveranciers: p.leveranciers.map((x, j) => j === i ? { ...x, actief: v } : x) }))}
                              size="sm"
                              testid={`art-lev-${i}-actief`}
                            />
                          </td>
                          <td>
                            <button type="button" onClick={() => setForm(p => ({ ...p, leveranciers: p.leveranciers.filter((_, j) => j !== i) }))} className="text-gray-400 hover:text-red-600" title="Verwijder">
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* SECTIE 5: Voorraad & Logistiek */}
              <div className="flex items-center gap-2 mb-2 pt-1.5 pb-1.5 border-b border-gray-100">
                <Cube size={16} weight="duotone" className="text-amber-600" />
                <div className="text-sm font-bold text-gray-800">Voorraad & Logistiek</div>
                <div className="text-[10px] text-gray-400">— familie, voorraad, gewicht/volume voor CMR</div>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div className="form-group">
                  <label className="form-label">Familie (bestaand of nieuw)</label>
                  <input className="field-input" value={form.familie} onChange={u('familie')} placeholder="Typ of kies..." list="families-list" data-testid="art-familie" />
                  <datalist id="families-list">{existingFamilies.map(f => <option key={f} value={f} />)}</datalist>
                </div>
                <div className="form-group">
                  <label className="form-label">
                    Voorraad
                    {form.varianten_actief && (
                      <span className="ml-1 text-[9px] text-blue-700 font-normal normal-case">· auto = som varianten</span>
                    )}
                  </label>
                  <input
                    type="number"
                    className={`field-input ${form.varianten_actief ? 'bg-blue-50 cursor-not-allowed' : ''}`}
                    value={form.voorraad}
                    onChange={u('voorraad')}
                    placeholder="0"
                    readOnly={!!form.varianten_actief}
                    title={form.varianten_actief ? 'Wordt automatisch berekend als som van alle varianten' : ''}
                    data-testid="art-voorraad"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Min. voorraad</label>
                  <input type="number" className="field-input" value={form.min_voorraad} onChange={u('min_voorraad')} placeholder="0" data-testid="art-min-voorraad" />
                </div>
                <div className="form-group">
                  <label className="form-label">Gewicht (kg) <span className="text-gray-400 text-[10px]">· voor CMR</span></label>
                  <input type="text" inputMode="decimal" className="field-input" value={fmtDec(form.gewicht_kg)} onChange={uDec('gewicht_kg')} placeholder="0,000" data-testid="art-gewicht" />
                </div>
                <div className="form-group">
                  <label className="form-label">Volume (m³) <span className="text-gray-400 text-[10px]">· voor CMR</span></label>
                  <input type="text" inputMode="decimal" className="field-input" value={fmtDec(form.volume_m3)} onChange={uDec('volume_m3')} placeholder="0,0000" data-testid="art-volume" />
                </div>
              </div>

              {/* SECTIE 6: Douane & Internationaal */}
              <div className="flex items-center gap-2 mb-2 pt-1.5 pb-1.5 border-b border-gray-100">
                <Globe size={16} weight="duotone" className="text-cyan-700" />
                <div className="text-sm font-bold text-gray-800">Douane & Internationaal</div>
                <div className="text-[10px] text-gray-400">— enkel nodig voor import/export</div>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-3">
                <div className="form-group">
                  <label className="form-label">HS-code <span className="text-gray-400 text-[10px]">· douane / import</span></label>
                  <div className="flex gap-1">
                    <input
                      className="field-input font-mono"
                      value={form.hs_code}
                      onChange={u('hs_code')}
                      placeholder="bv. 8471.30.00"
                      maxLength={14}
                      data-testid="art-hs-code"
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline btn-sm flex-shrink-0"
                      title="🤖 AI suggereert HS-code o.b.v. omschrijving + foto"
                      disabled={!form.omschrijving || hsBusy}
                      onClick={async () => {
                        setHsBusy(true);
                        try {
                          const r = await API.post('/artikels/find-hscode', {
                            omschrijving: form.omschrijving,
                            categorie: form.categorie,
                            foto_url: form.foto_url,
                          });
                          if (r.data?.ok && r.data.hs_code) {
                            setHsReview({ suggestie: r.data, gebruikt_materiaal: '' });
                          } else {
                            toast.error('AI kon geen HS-code voorstellen', { description: r.data?.error });
                          }
                        } catch (e) {
                          toast.error('Fout', { description: e?.response?.data?.detail || e.message });
                        } finally {
                          setHsBusy(false);
                        }
                      }}
                      data-testid="art-hs-zoek"
                    >
                      <Sparkle size={13} weight="duotone" />
                      {hsBusy ? '…' : '🤖'}
                    </button>
                  </div>
                  {/* Validatie + level-badge */}
                  {(() => {
                    const v = hsValidate(form.hs_code);
                    if (v.empty) return null;
                    if (!v.ok) {
                      return (
                        <div className="text-[10px] text-red-700 font-medium mt-1 flex items-center gap-1" data-testid="art-hs-validatie">
                          <Warning size={11} weight="duotone" /> {v.error}
                        </div>
                      );
                    }
                    const label = hsLevelLabel(form.hs_code);
                    return (
                      <div className="text-[10px] mt-1 flex items-center gap-1.5" data-testid="art-hs-niveau">
                        <span className="bg-emerald-50 text-emerald-700 font-bold px-1.5 py-0.5 rounded border border-emerald-200">
                          {label}
                        </span>
                        {v.warning && <span className="text-amber-700">⚠ {v.warning}</span>}
                      </div>
                    );
                  })()}
                </div>
                <div className="form-group">
                  <label className="form-label">Land van oorsprong <span className="text-gray-400 text-[10px]">· ISO-code</span></label>
                  <input
                    className="field-input font-mono uppercase"
                    value={form.oorsprong_land}
                    onChange={u('oorsprong_land')}
                    placeholder="bv. CN, DE, BE"
                    maxLength={2}
                    style={{ textTransform: 'uppercase' }}
                    data-testid="art-oorsprong-land"
                  />
                </div>
              </div>

              {/* SECTIE 6b: Eenheid & Meterprijs */}
              {form.__duplicaat_pack_reset && (
                <div className="mb-2 px-3 py-2 bg-orange-50 border-2 border-orange-300 text-orange-900 text-xs flex items-start gap-2" data-testid="duplicaat-pack-reset-banner">
                  <span className="text-base">⚠️</span>
                  <div>
                    <div className="font-bold mb-0.5">Gedupliceerd — vergeet niet de pack-gegevens opnieuw in te vullen!</div>
                    <div>De velden <strong>Pack-grootte</strong>, <strong>Aantal eenheden</strong> en <strong>Eenheid label</strong> zijn bewust leeggemaakt zodat je ze opnieuw kan instellen voor dit nieuwe artikel. Anders blijft het oude pack-totaal van het origineel zichtbaar op shop & facturen.</div>
                  </div>
                  <button type="button" onClick={() => setForm(p => ({ ...p, __duplicaat_pack_reset: false }))} className="ml-auto text-orange-700 hover:text-orange-900 font-bold text-xs" data-testid="dismiss-duplicaat-banner">✕</button>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-3 mb-3 p-2 border border-red-200 bg-red-50/40" data-testid="art-actie-wrap">
                <label className="inline-flex items-center gap-2 text-sm font-bold text-gray-800 cursor-pointer select-none">
                  <input type="checkbox" checked={!!form.actie} onChange={(e) => setForm(p => ({ ...p, actie: e.target.checked }))} data-testid="art-actie-toggle" />
                  🔴 Actie-artikel <span className="text-[10px] font-normal text-gray-500">— label in de PDF-catalogus</span>
                </label>
                {form.actie && (
                  <>
                    <label className="inline-flex items-center gap-1 text-xs text-gray-700">Tot
                      <input type="date" className="field-input text-xs py-1" value={form.actie_tot || ''} onChange={(e) => setForm(p => ({ ...p, actie_tot: e.target.value }))} data-testid="art-actie-tot" />
                    </label>
                    <label className="inline-flex items-center gap-1 text-xs text-gray-700">Label
                      <input type="text" className="field-input text-xs py-1 w-28" placeholder="Actie / -15%" maxLength={16} value={form.actie_tekst || ''} onChange={(e) => setForm(p => ({ ...p, actie_tekst: e.target.value }))} data-testid="art-actie-tekst" />
                    </label>
                  </>
                )}
                {/* Nooit afdrukken in de catalogus-PDF */}
                <label className="ml-auto inline-flex items-center gap-2 text-sm font-bold text-gray-800 cursor-pointer select-none px-2 py-1 bg-white border border-gray-300" title="Aangevinkt = dit artikel verschijnt NOOIT in de catalogus-PDF (ook niet bij 'alle artikels')">
                  <input
                    type="checkbox"
                    checked={!!form.catalogus_verbergen}
                    onChange={(e) => setForm(p => ({ ...p, catalogus_verbergen: e.target.checked }))}
                    data-testid="art-catalogus-verbergen-toggle"
                  />
                  <Prohibit size={15} weight="bold" className="text-gray-600" />
                  Niet in catalogus
                  <span className="text-[10px] font-normal text-gray-500">— nooit afdrukken in de catalogus-PDF</span>
                </label>
              </div>
              <div className="flex items-center gap-2 mb-2 pt-1.5 pb-1.5 border-b border-gray-100" data-testid="art-vertalingen-kop">
                <Globe size={16} weight="duotone" className="text-indigo-700" />
                <div className="text-sm font-bold text-gray-800">Vertalingen</div>
                <div className="text-[10px] text-gray-400">— voor de catalogus-PDF in FR/EN/DE; leeg = AI vult automatisch aan</div>
                <button
                  type="button"
                  className="ml-auto px-2.5 py-1 text-[11px] font-bold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                  disabled={vertaalBusy || !(form.omschrijving || '').trim()}
                  onClick={aiVertaalArtikel}
                  title={(editId || form.id) ? 'Vertaal de omschrijving (en actie-label) via AI naar FR, EN en DE' : 'Sla het artikel eerst op'}
                  data-testid="art-vertaal-ai"
                >
                  {vertaalBusy ? '⏳ Vertalen…' : '✨ AI vertaal (FR/EN/DE)'}
                </button>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-3" data-testid="art-vertalingen">
                {[['fr', '🇫🇷 Frans'], ['en', '🇬🇧 Engels'], ['de', '🇩🇪 Duits']].map(([t, lbl]) => {
                  const v = (form.vertalingen || {})[t] || {};
                  const verouderd = !!v.omschrijving && (v.bron || '') !== (form.omschrijving || '');
                  return (
                    <div className="form-group" key={t}>
                      <label className="form-label flex items-center gap-1.5">
                        {lbl}
                        {v.omschrijving && !verouderd && <span className={`text-[9px] px-1 ${v.auto === false ? 'bg-emerald-100 text-emerald-800' : 'bg-indigo-100 text-indigo-800'}`}>{v.auto === false ? 'handmatig' : 'AI'}</span>}
                        {verouderd && <span className="text-[9px] px-1 bg-amber-100 text-amber-800" title="De Nederlandse omschrijving is gewijzigd; AI vertaalt opnieuw bij de volgende catalogus">verouderd</span>}
                      </label>
                      <input className="field-input" value={v.omschrijving || ''} onChange={(e) => zetVertaling(t, 'omschrijving', e.target.value)} placeholder="Omschrijving" data-testid={`art-vertaling-${t}`} />
                      {form.actie && (
                        <input className="field-input text-xs py-1 mt-1" value={v.actie_tekst || ''} maxLength={16} onChange={(e) => zetVertaling(t, 'actie_tekst', e.target.value)} placeholder="Actie-label" data-testid={`art-vertaling-actie-${t}`} />
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-2 mb-2 pt-1.5 pb-1.5 border-b border-gray-100">
                <Coin size={16} weight="duotone" className="text-amber-700" />
                <div className="text-sm font-bold text-gray-800">Eenheid & meterprijs</div>
                <div className="text-[10px] text-gray-400">— toont bv. <span className="font-mono">(€ 0,66 / L/m)</span> op offertes/facturen</div>
                <div className="ml-auto flex items-center gap-2" data-testid="art-toon-eenheid-toggle-wrap">
                  <span className="text-[11px] font-semibold text-gray-700">Tonen op documenten</span>
                  <IOSToggle
                    checked={form.toon_eenheid_prijs !== false}
                    onChange={(v) => setForm(p => ({ ...p, toon_eenheid_prijs: v }))}
                    testid="art-toon-eenheid-toggle"
                  />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 mb-3 items-end">
                <div className="form-group">
                  <label className="form-label">Aantal eenheden</label>
                  <input
                    type="number"
                    step="0.0001"
                    className="field-input"
                    value={form.eenheid_aantal}
                    onChange={u('eenheid_aantal')}
                    placeholder="bv. 960"
                    data-testid="art-eenheid-aantal"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Label</label>
                  <input
                    className="field-input"
                    value={form.eenheid_label}
                    onChange={u('eenheid_label')}
                    placeholder="bv. L/m, kg, m², stuk"
                    data-testid="art-eenheid-label"
                  />
                </div>
                <div className="form-group" style={{ gridColumn: 'span 2' }}>
                  <label className="form-label">&nbsp;</label>
                  <button
                    type="button"
                    className="btn btn-outline btn-sm w-full"
                    disabled={!form.omschrijving || aiEenheidBusy}
                    onClick={async () => {
                      setAiEenheidBusy(true);
                      try {
                        const r = await API.post('/artikels/extract-eenheid', { omschrijving: form.omschrijving });
                        if (r.data?.ok && r.data.eenheid_aantal) {
                          setForm(p => ({
                            ...p,
                            eenheid_aantal: r.data.eenheid_aantal,
                            eenheid_label: r.data.eenheid_label || p.eenheid_label,
                          }));
                          toast.success(`Eenheid herkend: ${r.data.eenheid_aantal} ${r.data.eenheid_label}`, {
                            description: `Bron: ${r.data.source === 'ai' ? 'AI' : 'patroon-detectie'}`,
                          });
                        } else {
                          toast.warning('Geen eenheid herkend', { description: 'Vul handmatig in (bv. 960 + L/m)' });
                        }
                      } catch (e) {
                        toast.error('AI-extractie mislukt', { description: e?.response?.data?.detail || e.message });
                      } finally {
                        setAiEenheidBusy(false);
                      }
                    }}
                    data-testid="art-eenheid-ai-extract"
                  >
                    <Sparkle size={14} weight="duotone" className="inline mr-1" />
                    {aiEenheidBusy ? 'Bezig…' : 'Herken automatisch uit omschrijving'}
                  </button>
                  {form.eenheid_aantal && form.eenheid_label && form.aankoopprijs && (
                    <div className="text-[11px] text-gray-500 mt-1">
                      Aankoopprijs / eenheid: <span className="font-mono font-semibold">€ {(parseFloat(form.aankoopprijs) / parseFloat(form.eenheid_aantal)).toFixed(4)} / {form.eenheid_label}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* SECTIE 7: Verkoopprijzen */}
              {/* Prices per group */}
              {form.familie && (
                <>
                  <div className="flex items-center gap-2 mb-2 pt-1.5 pb-1.5 border-b border-gray-100">
                    <Tag size={16} weight="duotone" className="text-[#059669]" />
                    <div className="text-sm font-bold text-gray-800">Verkoopprijzen per {perPak ? 'pak' : 'stuk'}</div>
                    <div className="text-[10px] text-gray-400">— {isNewFamilie ? <span className="text-amber-600 font-semibold">nieuwe familie — vul prijzen in</span> : <>familie: <span className="font-mono font-semibold">{form.familie}</span></>}</div>
                    {form.eenheid === 'pack' && parseInt(form.pack_grootte, 10) > 0 && (
                      <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                        {perPak ? `ⓘ Stukprijs = pakprijs ÷ ${parseInt(form.pack_grootte, 10)} st./pak` : `ⓘ Pakprijs = stukprijs × ${parseInt(form.pack_grootte, 10)} st./pak`}
                      </span>
                    )}
                  </div>
                  <div className="mb-3">
                  <div className="flex gap-4 flex-wrap">
                    {groepen.map(g => {
                      const p = parseFloat(form.prijzen[g.key]) || 0;
                      const pg = parseInt(form.pack_grootte, 10) || 0;
                      const isPack = form.eenheid === 'pack' && pg > 0;
                      const packPrijs = packVanIngevoerd(p);
                      const ea = parseFloat(form.eenheid_aantal) || 0;
                      const el = (form.eenheid_label || '').trim();
                      const showHint = form.toon_eenheid_prijs !== false;
                      const basisVoorEenheid = isPack ? packPrijs : p;
                      const upHint = (showHint && basisVoorEenheid > 0 && ea > 0 && el) ? `€ ${(basisVoorEenheid / ea).toFixed(2).replace('.', ',')} / ${el}` : '';
                      const packHint = (isPack && p > 0) ? (perPak ? `= € ${stukVanIngevoerd(p).toFixed(2).replace('.', ',')} / st` : `= € ${packPrijs.toFixed(2).replace('.', ',')} / pak`) : '';
                      return (
                        <div key={g.key} className="form-group" style={{ minWidth: 110 }}>
                          <label className="form-label" style={{ color: g.color }}>{g.label}</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="field-input"
                            style={{ width: 110, borderColor: g.color, color: g.color, fontWeight: 600 }}
                            value={fmtDec(form.prijzen[g.key])}
                            onChange={uPrijs(g.key)}
                            disabled={!isNewFamilie && !editId}
                            placeholder={perPak ? '0,00 / pak' : '0,00 / st'}
                            data-testid={`art-prijs-${g.key}`}
                          />
                          {packHint && <div className="text-[10px] font-bold text-purple-700 mt-0.5" data-testid={`art-prijs-pak-${g.key}`}>{packHint}</div>}
                          {upHint && <div className="text-[10px] text-amber-700 font-mono mt-0.5" data-testid={`art-prijs-eenheid-${g.key}`}>{upHint}</div>}
                          {winstHint(p, g.key)}
                        </div>
                      );
                    })}
                  </div>
                  {!isNewFamilie && !editId && <div className="text-xs text-gray-400 mt-1">Prijzen overgenomen van bestaande familie.</div>}
                </div>
                </>
              )}

              {!form.familie && groepen.length > 0 && (
                <div className="mb-5">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">
                    Verkoopprijzen per {perPak ? 'pak' : 'stuk'}
                    {form.eenheid === 'pack' && parseInt(form.pack_grootte, 10) > 0 && (
                      <span className="ml-2 text-[10px] font-medium normal-case tracking-normal text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                        {perPak ? `ⓘ Stukprijs = pakprijs ÷ ${parseInt(form.pack_grootte, 10)} st./pak` : `ⓘ Pakprijs = stukprijs × ${parseInt(form.pack_grootte, 10)} st./pak`}
                      </span>
                    )}
                  </div>
                  <div className="flex gap-4 flex-wrap">
                    {groepen.map(g => {
                      const p = parseFloat(form.prijzen[g.key]) || 0;
                      const pg = parseInt(form.pack_grootte, 10) || 0;
                      const isPack = form.eenheid === 'pack' && pg > 0;
                      const packPrijs = packVanIngevoerd(p);
                      const ea = parseFloat(form.eenheid_aantal) || 0;
                      const el = (form.eenheid_label || '').trim();
                      const showHint = form.toon_eenheid_prijs !== false;
                      // Per-eenheid-hint (bv. €/L/m): bij pack-artikel is de basis de PAKPRIJS / eenheid_aantal
                      const basisVoorEenheid = isPack ? packPrijs : p;
                      const upHint = (showHint && basisVoorEenheid > 0 && ea > 0 && el) ? `€ ${(basisVoorEenheid / ea).toFixed(2).replace('.', ',')} / ${el}` : '';
                      const packHint = (isPack && p > 0) ? (perPak ? `= € ${stukVanIngevoerd(p).toFixed(2).replace('.', ',')} / st` : `= € ${packPrijs.toFixed(2).replace('.', ',')} / pak`) : '';
                      return (
                        <div key={g.key} className="form-group" style={{ minWidth: 110 }}>
                          <label className="form-label" style={{ color: g.color }}>{g.label}</label>
                          <input
                            type="text"
                            inputMode="decimal"
                            className="field-input"
                            style={{ width: 110, borderColor: g.color, color: g.color, fontWeight: 600 }}
                            value={fmtDec(form.prijzen[g.key])}
                            onChange={uPrijs(g.key)}
                            placeholder={perPak ? '0,00 / pak' : '0,00 / st'}
                            data-testid={`art-prijs-${g.key}`}
                          />
                          {packHint && <div className="text-[10px] font-bold text-purple-700 mt-0.5" data-testid={`art-prijs-pak-${g.key}`}>{packHint}</div>}
                          {upHint && <div className="text-[10px] text-amber-700 font-mono mt-0.5" data-testid={`art-prijs-eenheid-${g.key}`}>{upHint}</div>}
                          {winstHint(p, g.key)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* VARIANTEN-PANEL — alleen zichtbaar wanneer module aan staat in Instellingen */}
              {inst.varianten_module_actief && (
                <>
                  <div className="flex items-center gap-2 mb-2 pt-1.5 pb-1.5 border-b border-gray-100">
                    <span className="text-base">🎨</span>
                    <div className="text-sm font-bold text-gray-800">Varianten / Maten</div>
                    <div className="text-[10px] text-gray-400">— per artikel optioneel · kleur × maat × breedte</div>
                  </div>
                  <div className="mb-4">
                    <VariantenPanel
                      artId={editId}
                      artCode={form.art_code}
                      artFotoUrl={form.foto_url}
                      actief={!!form.varianten_actief}
                      dimensies={form.dimensies || []}
                      varianten={form.varianten || []}
                      omschrijving={form.omschrijving}
                      categorie={form.categorie}
                      masterVoorraad={form.voorraad}
                      onChange={(next) => setForm(p => {
                        // Map de panel-prop-namen naar form-keys
                        const mapped = {};
                        if ('actief' in next) mapped.varianten_actief = next.actief;
                        if ('dimensies' in next) mapped.dimensies = next.dimensies;
                        if ('varianten' in next) {
                          mapped.varianten = next.varianten;
                          // Auto-sync master.voorraad = som varianten als varianten actief zijn
                          if (p.varianten_actief || next.actief) {
                            mapped.voorraad = next.varianten.reduce((a, v) => a + (parseFloat(v.voorraad) || 0), 0);
                          }
                        }
                        return { ...p, ...mapped };
                      })}
                      onPersist={async () => { await saveArtikel({ silent: true }); }}
                    />
                  </div>
                </>
              )}

              {/* SECTIE 8: Foto */}
              <div className="flex items-center gap-2 mb-2 pt-1.5 pb-1.5 border-b border-gray-100">
                <ImageIcon size={16} weight="duotone" className="text-pink-600" />
                <div className="text-sm font-bold text-gray-800">Artikelfoto</div>
                <div className="text-[10px] text-gray-400">— upload, AI-genereren of plakken (Ctrl+V)</div>
              </div>
              {/* Foto-upload - altijd zichtbaar; bij nieuw artikel eerst auto-save */}
              <div className="mb-4 p-3 border border-gray-200 bg-gray-50" data-testid="artikel-foto-block">
                <label className="text-[11px] font-bold text-gray-600 uppercase tracking-wider block mb-2">
                  📸 Artikelfoto
                  <span className="text-gray-400 text-[10px] font-normal normal-case ml-1">· automatisch verkleind tot max 800px, opgeslagen als JPEG</span>
                </label>
                <div className="flex items-center gap-4">
                  {form.foto_url ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${form.foto_url}`}
                      alt="Artikel"
                      style={{ width: 180, height: 180, objectFit: 'cover', border: '1px solid #e5e7eb', borderRadius: 6, cursor: 'zoom-in' }}
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${form.foto_url}`, '_blank')}
                      data-testid="artikel-foto-preview"
                      title="Klik om op volledige grootte te bekijken"
                    />
                  ) : (
                    <div style={{ width: 180, height: 180, background: '#fff', border: '1px dashed #d1d5db', borderRadius: 6 }} className="flex items-center justify-center text-5xl text-gray-300">
                      🖼
                    </div>
                  )}
                  <div className="flex flex-col gap-2">
                    <label className="btn btn-secondary btn-sm cursor-pointer inline-flex items-center gap-1.5" data-testid="artikel-foto-upload">
                      <UploadSimple size={14} />
                      {form.foto_url ? 'Foto vervangen' : 'Foto toevoegen'}
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0];
                          if (!f) return;
                          let artId = editId;
                          // Bij nieuw artikel: eerst opslaan zodat we een ID hebben om foto aan te koppelen
                          if (!artId) {
                            if (!form.art_code && !form.omschrijving) {
                              alert('Vul eerst minstens de artikel-code of omschrijving in voordat u een foto toevoegt.');
                              e.target.value = '';
                              return;
                            }
                            if (!window.confirm('Het artikel wordt nu eerst opgeslagen zodat de foto eraan gekoppeld kan worden. Doorgaan?')) {
                              e.target.value = '';
                              return;
                            }
                            try {
                              const saved = await saveArtikel({ silent: true });
                              artId = saved?.id;
                              if (!artId) throw new Error('Geen ID ontvangen');
                              setEditId(artId);
                            } catch (err) {
                              alert('Opslaan mislukt: ' + (err.response?.data?.detail || err.message));
                              e.target.value = '';
                              return;
                            }
                          }
                          const fd = new FormData();
                          fd.append('file', f);
                          try {
                            const res = await API.post(`/artikels/${artId}/foto`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                            setForm(p => ({ ...p, foto_url: res.data.foto_url }));
                            load();
                          } catch (err) {
                            alert('Foto-upload mislukt: ' + (err.response?.data?.detail || err.message));
                          } finally {
                            e.target.value = '';
                          }
                        }}
                      />
                    </label>
                    {editId && isAdmin && (
                      <button
                        className="btn btn-secondary btn-sm inline-flex items-center gap-1.5"
                        style={{ borderColor: '#EC4899', color: '#EC4899' }}
                        onClick={async () => {
                          const heeft = !!form.foto_url;
                          const msg = heeft
                            ? 'Nieuwe AI-foto genereren? De huidige foto wordt vervangen. (~15s)'
                            : 'AI-foto genereren op basis van de omschrijving? (~15s)';
                          if (!window.confirm(msg)) return;
                          setForm(p => ({ ...p, _ai_foto_busy: true }));
                          try {
                            const res = await API.post(`/artikels/${editId}/foto-ai`, {}, { timeout: 60000 });
                            setForm(p => ({ ...p, foto_url: res.data.foto_url, _ai_foto_busy: false }));
                            load();
                          } catch (err) {
                            setForm(p => ({ ...p, _ai_foto_busy: false }));
                            alert('AI-foto generatie mislukt: ' + (err.response?.data?.detail || err.message));
                          }
                        }}
                        disabled={!!form._ai_foto_busy}
                        data-testid="artikel-foto-ai-regenereer"
                        title="Genereer een nieuwe AI-productfoto (Gemini Nano Banana) op basis van de artikel-omschrijving"
                      >
                        {form._ai_foto_busy ? '⏳ Genereren…' : (form.foto_url ? '🎨 AI regenereren' : '🎨 AI genereren')}
                      </button>
                    )}
                    {form.foto_url && (
                      <button
                        className="btn btn-secondary btn-sm inline-flex items-center gap-1.5 text-red-600"
                        onClick={async () => {
                          if (!editId || !window.confirm('Foto verwijderen?')) return;
                          try {
                            await API.delete(`/artikels/${editId}/foto`);
                            setForm(p => ({ ...p, foto_url: '' }));
                            load();
                          } catch (err) {
                            alert('Verwijderen mislukt: ' + (err.response?.data?.detail || err.message));
                          }
                        }}
                        data-testid="artikel-foto-delete"
                      >
                        🗑 Verwijderen
                      </button>
                    )}
                    <span className="text-[10px] text-gray-500">JPG, PNG, WEBP, HEIC · max 15MB</span>
                    {!editId && !form.foto_url && (
                      <span className="text-[10px] text-amber-700 font-medium">ℹ️ Bij nieuw artikel wordt het eerst opgeslagen voordat de foto wordt gekoppeld.</span>
                    )}
                  </div>
                  {/* Import-gegevens rechts naast foto + upload-knop */}
                  <div className="flex-1 ml-auto min-w-0 max-w-md" data-testid="art-import-info">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-3">
                      <div className="flex items-center gap-1.5 mb-2">
                        <Globe size={14} weight="duotone" className="text-blue-700" />
                        <div className="text-[11px] font-bold uppercase tracking-wider text-blue-800">Import & TARIC</div>
                      </div>

                      {/* Foto-thumbnail + AI-beschrijving + motivatie wanneer HS-code via AI is gevuld */}
                      {form.hs_code && (form.hs_beschrijving || form.hs_motivatie || form.foto_url) && (
                        <div className="bg-white/70 border border-blue-100 rounded-md p-2 mb-2 flex gap-2.5" data-testid="art-hs-uitleg">
                          {form.foto_url && (
                            <img
                              src={form.foto_url.startsWith('http') ? form.foto_url : `${process.env.REACT_APP_BACKEND_URL}${form.foto_url}`}
                              alt={form.omschrijving || ''}
                              className="w-12 h-12 object-cover rounded border border-blue-200 flex-shrink-0"
                            />
                          )}
                          <div className="flex-1 min-w-0">
                            {form.hs_beschrijving && (
                              <div className="text-[11px] font-semibold text-gray-900 leading-snug" data-testid="art-hs-beschrijving">
                                {form.hs_beschrijving}
                              </div>
                            )}
                            {form.hs_motivatie && (
                              <div className="text-[10px] text-gray-600 italic leading-snug mt-0.5" data-testid="art-hs-motivatie">
                                💡 {form.hs_motivatie}
                              </div>
                            )}
                            {!form.hs_beschrijving && !form.hs_motivatie && form.foto_url && (
                              <div className="text-[10px] text-gray-400 italic">Klik 🤖 om AI-uitleg te genereren</div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <div className="text-[9px] uppercase text-gray-500 font-semibold">HS-code</div>
                          <div className="font-mono font-bold text-gray-900">{form.hs_code || <span className="text-gray-300">—</span>}</div>
                          {form.hs_code && hsLevelLabel(form.hs_code) && (
                            <div className="text-[9px] text-emerald-700 font-bold mt-0.5">{hsLevelLabel(form.hs_code)}</div>
                          )}
                        </div>
                        <div>
                          <div className="text-[9px] uppercase text-gray-500 font-semibold">Oorsprong</div>
                          <div className="font-bold text-gray-900">{form.oorsprong_land || <span className="text-gray-300">—</span>}</div>
                        </div>
                        {form.import_tarief && (() => {
                          const aank = parseFloat(form.aankoopprijs) || 0;
                          const recht_pct = parseFloat(form.import_tarief.invoerrecht_pct) || 0;
                          const recht_eur = aank > 0 ? aank * recht_pct / 100 : 0;
                          const kostprijs_incl = aank + recht_eur;
                          return (
                            <>
                              <div>
                                <div className="text-[9px] uppercase text-gray-500 font-semibold">Invoerrecht</div>
                                <div className="font-bold text-gray-900">{form.import_tarief.invoerrecht_pct ?? '—'}%</div>
                              </div>
                              <div>
                                <div className="text-[9px] uppercase text-gray-500 font-semibold">BTW invoer</div>
                                <div className="font-bold text-gray-900">{form.import_tarief.btw_pct ?? '—'}%</div>
                              </div>
                              {/* Kostprijs-berekening — drie rijen onder elkaar voor leesbaarheid */}
                              <div className="col-span-2 border-t border-blue-200 pt-2 mt-1 space-y-1.5" data-testid="art-kostprijs-berekening">
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-gray-600">Aankoopprijs</span>
                                  <span className="font-bold text-gray-900">{aank > 0 ? fmt(aank) : <span className="text-gray-300">—</span>}</span>
                                </div>
                                <div className="flex items-center justify-between text-xs">
                                  <span className="text-rose-700">+ Kostprijs invoerrecht</span>
                                  <span className="font-bold text-rose-700" data-testid="art-kostprijs-invoerrecht">+ {fmt(recht_eur)}</span>
                                </div>
                                <div className="flex items-center justify-between text-sm pt-1.5 border-t border-blue-100">
                                  <span className="font-semibold text-emerald-800">= Kostprijs incl.</span>
                                  <span className="font-bold text-emerald-700" data-testid="art-kostprijs-incl">{fmt(kostprijs_incl)}</span>
                                </div>
                              </div>
                              {form.import_tarief.antidumping && (
                                <div className="col-span-2 border-t border-blue-200 pt-1.5 mt-0.5">
                                  <div className="text-[9px] uppercase text-gray-500 font-semibold">Antidumping</div>
                                  <div className="text-[11px] text-amber-800 italic leading-tight">{form.import_tarief.antidumping}</div>
                                </div>
                              )}
                              {form.import_tarief.opmerkingen && (
                                <div className="col-span-2">
                                  <div className="text-[9px] uppercase text-gray-500 font-semibold">Opmerkingen</div>
                                  <div className="text-[11px] text-gray-700 leading-tight">{form.import_tarief.opmerkingen}</div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                      {!form.import_tarief && form.hs_code && (
                        <button
                          type="button"
                          className="btn btn-outline btn-sm w-full mt-2 inline-flex items-center justify-center gap-1.5"
                          disabled={tariefBusy}
                          onClick={async () => {
                            setTariefBusy(true);
                            try {
                              const r = await API.get(`/artikels/hscode-tarief/${form.hs_code}`, {
                                params: { oorsprong: form.oorsprong_land || 'CN' },
                              });
                              if (!r.data?.ok) {
                                toast.error('Tarief niet gevonden', { description: r.data?.error });
                                return;
                              }
                              const tarief = {
                                oorsprong: r.data.oorsprong,
                                invoerrecht_pct: r.data.invoerrecht_pct,
                                btw_pct: r.data.btw_pct,
                                antidumping: r.data.antidumping || '',
                                opmerkingen: r.data.opmerkingen || '',
                                updated_at: new Date().toISOString(),
                              };
                              setForm(p => ({ ...p, import_tarief: tarief }));
                              // Bij bestaand artikel: ook persistent opslaan zodat het over reloads heen blijft
                              if (editId) {
                                try { await API.put(`/artikels/${editId}`, { import_tarief: tarief }); } catch { /* niet kritiek */ }
                              }
                              toast.success(`Tarief geladen: ${r.data.invoerrecht_pct}% invoerrecht`);
                            } catch (e) {
                              toast.error('Fout bij ophalen tarief', { description: e?.response?.data?.detail || e.message });
                            } finally {
                              setTariefBusy(false);
                            }
                          }}
                          data-testid="art-tarief-ophalen"
                        >
                          <MagnifyingGlass size={12} weight="duotone" />
                          {tariefBusy ? 'Bezig…' : '🔍 Tarief ophalen'}
                        </button>
                      )}
                      {!form.hs_code && (
                        <div className="text-[10px] text-gray-500 italic mt-2">💡 Vul HS-code in (handmatig of via Import-tab) om import-info te zien.</div>
                      )}
                      {form.import_tarief?.updated_at && (
                        <div className="flex items-center justify-between mt-2">
                          <div className="text-[9px] text-gray-400 italic">Bijgewerkt: {new Date(form.import_tarief.updated_at).toLocaleDateString('nl-BE')}</div>
                          <button
                            type="button"
                            className="text-[10px] text-blue-700 hover:text-blue-900 underline"
                            disabled={tariefBusy}
                            onClick={async () => {
                              setTariefBusy(true);
                              try {
                                const r = await API.get(`/artikels/hscode-tarief/${form.hs_code}`, {
                                  params: { oorsprong: form.oorsprong_land || 'CN' },
                                });
                                if (!r.data?.ok) { toast.error('Tarief niet gevonden'); return; }
                                const tarief = {
                                  oorsprong: r.data.oorsprong,
                                  invoerrecht_pct: r.data.invoerrecht_pct,
                                  btw_pct: r.data.btw_pct,
                                  antidumping: r.data.antidumping || '',
                                  opmerkingen: r.data.opmerkingen || '',
                                  updated_at: new Date().toISOString(),
                                };
                                setForm(p => ({ ...p, import_tarief: tarief }));
                                if (editId) {
                                  try { await API.put(`/artikels/${editId}`, { import_tarief: tarief }); } catch { /* niet kritiek */ }
                                }
                                toast.success('Tarief vernieuwd');
                              } catch (e) {
                                toast.error('Fout', { description: e?.response?.data?.detail || e.message });
                              } finally {
                                setTariefBusy(false);
                              }
                            }}
                            data-testid="art-tarief-vernieuwen"
                          >
                            🔄 Vernieuw
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <button className="btn btn-primary" onClick={saveArtikel} disabled={saving} data-testid="save-artikel-btn">
                {saving ? 'Opslaan...' : editId ? 'Artikel bijwerken' : 'Artikel toevoegen'}
              </button>
            </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── Inventaris Tab — jaareinde-inventaris + stock-reset ───────── */}
      {tab === 'inventaris' && (
        <div data-testid="inventaris-tab" className="-mx-6 -mt-2">
          <InventarisPage />
        </div>
      )}

      {/* ─── Import & TARIC Tab ──────────────────────────────────── */}
      {tab === 'import_taric' && (
        <div data-overview-form><ImportTaricPanel artikels={artikels} onUpdate={load} /></div>
      )}

      {/* ─── Instellingen Tab ──────────────────────────────────── */}
      {tab === 'params' && (
        <div data-testid="params-tab" data-overview-form>
          {/* Varianten / Maten module — opt-in toggle */}
          <div className="section-card mb-4" data-testid="varianten-module-card">
            <div className="section-header">
              <span className="section-title inline-flex items-center gap-2">🎨 Varianten / Maten module</span>
            </div>
            <div className="p-5 flex items-start gap-4">
              <div className="flex-1">
                <div className="text-sm text-gray-700 leading-relaxed">
                  Activeer dit als u artikels verkoopt met <b>kleur, maat, schoenmaat of breedte</b> (kledingwinkel, schoenenwinkel, accessoires).
                  Per artikel kunt u dan tot <b>3 dimensies</b> definiëren — het systeem genereert automatisch alle combinaties als aparte SKU's met eigen voorraad, EAN-barcode en optioneel eigen foto/prijs.
                </div>
                <div className="text-xs text-gray-500 mt-2 leading-relaxed">
                  💡 Voorbeeld: "Levi's 511 jeans" → kleur (Zwart, Donkerblauw, Grijs) × maat (28, 30, 32, 34, 36) = 15 varianten. Op de POS kunt u via barcode of variant-keuze popup de juiste afboeken.
                  <br />⚠️ <b>Eens geactiveerd</b>, blijft de toggle per-artikel <em>"Heeft varianten?"</em> default UIT — uw bestaande artikels veranderen niet.
                </div>
              </div>
              <button
                type="button"
                className={`btn btn-sm shrink-0 ${inst.varianten_module_actief ? 'btn-primary' : 'btn-outline'}`}
                onClick={async () => {
                  try {
                    const next = !inst.varianten_module_actief;
                    await API.put('/instellingen', { varianten_module_actief: next });
                    setInst(prev => ({ ...prev, varianten_module_actief: next }));
                    toast.success(next ? '🎨 Varianten-module geactiveerd' : 'Varianten-module gedeactiveerd');
                  } catch (e) {
                    toast.error('Opslaan mislukt', { description: e?.response?.data?.detail || e.message });
                  }
                }}
                data-testid="art-varianten-module-toggle"
              >
                {inst.varianten_module_actief ? '✓ Module AAN' : 'Module activeren'}
              </button>
            </div>
          </div>

          {/* Variant-artikelcode instellingen — enkel tonen als module actief is */}
          {inst.varianten_module_actief && <VariantCodeSettings />}

          <div className="section-card">
            <div className="section-header"><span className="section-title">Prijsgroepen</span></div>
            <div className="p-5">
              <div className="flex items-end gap-3 flex-wrap mb-4">
                {groepen.map((g, i) => (
                  <div key={g.key} className="relative group">
                    <div className="px-4 py-2 border-2 text-sm font-semibold min-w-[80px] text-center" style={{ borderColor: g.color, color: g.color }}>
                      <div className="text-[10px] text-gray-400 font-normal uppercase">{String.fromCharCode(65 + i)}</div>
                      {g.label}
                    </div>
                    {groepen.length > 2 && (
                      <button onClick={() => removeGroep(g.key)} className="absolute -top-1.5 -right-1.5 bg-white border border-gray-200 rounded-full w-4 h-4 flex items-center justify-center text-[10px] text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">x</button>
                    )}
                  </div>
                ))}
                <div className="flex items-center gap-1">
                  <input className="field-input" style={{ width: 100, padding: '6px 8px', fontSize: 12 }} placeholder="Naam..." value={newGroep} onChange={e => setNewGroep(e.target.value)} onKeyDown={e => e.key === 'Enter' && addGroep()} data-testid="param-new-groep" />
                  <button className="text-[#F97316] font-semibold text-sm hover:underline" onClick={addGroep} data-testid="add-groep-btn">+ Groep</button>
                </div>
              </div>
            </div>
          </div>

          <div className="section-card mt-4">
            <div className="section-header"><span className="section-title">Categorieen</span></div>
            <div className="p-5">
              <div className="flex flex-wrap gap-2">
                {cats.map((cat, i) => (
                  <span key={i} className="inline-flex items-center gap-1 px-3 py-1.5 border border-gray-200 bg-white text-sm">
                    {cat}
                    <button className="text-red-400 hover:text-red-600 ml-1" onClick={() => setParams(p => ({ ...p, categorieen: (p.categorieen || []).filter((_, j) => j !== i) }))}><X size={12} /></button>
                  </span>
                ))}
                <button className="inline-flex items-center gap-1 px-3 py-1.5 border border-dashed border-gray-300 text-sm text-gray-500 hover:border-gray-500"
                  onClick={() => { const n = prompt('Nieuwe categorie:'); if (n) setParams(p => ({ ...p, categorieen: [...(p.categorieen || []), n] })); }}
                  data-testid="add-categorie-btn"><Plus size={12} /> Categorie</button>
              </div>
            </div>
          </div>

          <div className="section-card mt-4">
            <div className="section-header"><span className="section-title">Families & prijzen</span></div>
            <div className="section-body">
              <table className="data-table">
                <thead><tr><th>Familie</th>{groepen.map(g => <th key={g.key} className="right" style={{ color: g.color }}>{g.label}</th>)}<th></th></tr></thead>
                <tbody>
                  {Object.entries(params.families || {}).map(([fam, prijzen]) => (
                    <tr key={fam}>
                      <td className="font-medium">{fam}</td>
                      {groepen.map(g => (
                        <td key={g.key} className="right">
                          <input
                            type="text"
                            inputMode="decimal"
                            className="field-input text-right"
                            style={{ width: 80, padding: '4px 6px', fontSize: 12 }}
                            value={fmtDec(prijzen[g.key])}
                            placeholder="0,00"
                            onChange={e => {
                              const raw = (e.target.value || '').replace(',', '.');
                              setParams(p => ({
                                ...p,
                                families: {
                                  ...p.families,
                                  [fam]: {
                                    ...p.families[fam],
                                    [g.key]: raw === '' ? '' : (isNaN(parseFloat(raw)) ? p.families[fam][g.key] : raw),
                                  },
                                },
                              }));
                            }}
                          />
                        </td>
                      ))}
                      <td><button className="btn btn-danger btn-sm" onClick={() => { if (window.confirm(`Familie "${fam}" verwijderen?`)) setParams(p => { const f = { ...p.families }; delete f[fam]; return { ...p, families: f }; }); }}><Trash size={12} /></button></td>
                    </tr>
                  ))}
                  {Object.keys(params.families || {}).length === 0 && (
                    <tr><td colSpan={groepen.length + 2} className="text-center text-gray-400 py-6">Nog geen families</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <button className="btn btn-primary mt-4" onClick={saveParams} disabled={saving} data-testid="save-params-btn">
            {saving ? 'Opslaan...' : 'Instellingen opslaan'}
          </button>
        </div>
      )}

      <BarcodeScanner
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onDetect={(code) => {
          const existing = artikels.find(a => (a.ean || '').trim() === code.trim());
          if (existing && existing.id !== editId) {
            if (window.confirm(`Er bestaat al een artikel met deze EAN:\n\n${existing.art_code} — ${existing.omschrijving}\n\nWilt u dat artikel bewerken i.p.v. een nieuw aanmaken?`)) {
              startEdit(existing);
              return;
            }
          }
          setForm(p => ({ ...p, ean: code }));
        }}
        title="Scan artikel-barcode"
      />

      <LabelExportModal
        open={labelOpen}
        onClose={() => setLabelOpen(false)}
        selectedArtikels={filtered.filter(a => selected[a.id])}
        allArtikels={filtered}
        prijsGroepen={groepen}
      />

      {/* (Verwijderd: aparte categorie-volgorde modal — de "↕️ Volgorde aanpassen" knop opent nu
           rechtstreeks de bestaande PDF-catalogus modal die dezelfde volgorde-UI bevat.) */}

      {catModalOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/50 sm:p-4"
          onClick={() => !catLoading && (setCatModalOpen(false), setCatPdfReady(null))}
          data-testid="catalogus-pdf-modal"
        >
          <div
            className="bg-white shadow-2xl border border-gray-200 w-full max-w-lg max-h-[95vh] sm:max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 sm:p-6 pb-3 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="text-lg font-bold text-gray-900">📕 Catalogus PDF</h3>
              <button
                className="text-gray-400 hover:text-gray-700 p-1 -m-1"
                onClick={() => !catLoading && (setCatModalOpen(false), setCatPdfReady(null))}
                data-testid="catalogus-pdf-close"
                disabled={catLoading}
                aria-label="Sluit"
              >
                <X size={22} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-6 pt-4 space-y-4" data-testid="catalogus-pdf-body">
              <div>
                <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide">Stijl</label>
                <select
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={catStijl}
                  onChange={(e) => setCatStijl(e.target.value)}
                  data-testid="catalogus-pdf-stijl"
                  disabled={catLoading}
                >
                  <option value="compact-pro">⭐ Compact Pro — 4 kolommen, premium B2B (aanbevolen, -45% pagina&apos;s)</option>
                  <option value="klassiek">Klassiek — 3 kolommen, ruime witruimte</option>
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3" data-testid="catalogus-pdf-opmaak">
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide">Kleur</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      className="h-9 w-12 border border-gray-300 cursor-pointer disabled:opacity-40"
                      value={catKleur || ((typeof document !== 'undefined' && getComputedStyle(document.documentElement).getPropertyValue('--brand-primary').trim()) || '#002FA7')}
                      onChange={(e) => { setCatKleur(e.target.value); setCatZwartWit(false); }}
                      disabled={catLoading || catZwartWit}
                      title="Accentkleur voor titels, prijzen en lijnen"
                      data-testid="catalogus-pdf-kleur"
                    />
                    {catKleur && !catZwartWit && (
                      <button type="button" className="text-[11px] text-blue-700 underline" onClick={() => setCatKleur('')} data-testid="catalogus-pdf-kleur-reset">Huisstijl</button>
                    )}
                    <label className="inline-flex items-center gap-1.5 cursor-pointer text-xs font-semibold text-gray-700 select-none ml-1">
                      <input type="checkbox" checked={catZwartWit} onChange={(e) => setCatZwartWit(e.target.checked)} disabled={catLoading} data-testid="catalogus-pdf-zwartwit" />
                      Zwart-wit
                    </label>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">{catZwartWit ? 'Foto\u2019s in grijstinten, geen kleur — ideaal voor zwart-wit printers.' : 'In kleur (huisstijl of eigen accentkleur).'}</p>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide">Tekstgrootte</label>
                  <select
                    className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={catTekstSchaal}
                    onChange={(e) => setCatTekstSchaal(e.target.value)}
                    disabled={catLoading}
                    data-testid="catalogus-pdf-tekstgrootte"
                  >
                    <option value="0.85">Klein (85%)</option>
                    <option value="1">Normaal (100%)</option>
                    <option value="1.15">Groot (115%)</option>
                    <option value="1.3">Extra groot (130%)</option>
                    <option value="1.5">Zeer groot (150%)</option>
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1">Geldt voor alle tekst, ook de voorwaarden onderaan.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3" data-testid="catalogus-pdf-taal-klant">
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide">Taal</label>
                  <select
                    className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    value={catTaal}
                    onChange={(e) => wisselCatTaal(e.target.value)}
                    disabled={catLoading}
                    data-testid="catalogus-pdf-taal"
                  >
                    <option value="nl">🇧🇪 Nederlands</option>
                    <option value="fr">🇫🇷 Français</option>
                    <option value="en">🇬🇧 English</option>
                    <option value="de">🇩🇪 Deutsch</option>
                  </select>
                  <p className="text-[11px] text-gray-500 mt-1">Titels, labels en footer in deze taal. Voorwaarden worden per taal onthouden.</p>
                  {catTaal !== 'nl' && (
                    <div className="mt-2 border border-indigo-200 bg-indigo-50/60 p-2.5 text-[11px]" data-testid="catalogus-vertaal-panel">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-indigo-900">
                          <Globe size={13} weight="duotone" className="inline mr-1 -mt-0.5" />
                          <strong>Inhoud in {catTaal.toUpperCase()}:</strong>{' '}
                          {catVertStatus ? (
                            <span data-testid="catalogus-vertaal-status">
                              {catVertStatus.vertaald}/{catVertStatus.totaal} artikelnamen vertaald
                              {catVertStatus.ontbrekend > 0 ? ` · ${catVertStatus.ontbrekend} ontbreken (worden automatisch aangevuld bij genereren)` : ' ✓'}
                            </span>
                          ) : '…'}
                        </div>
                        {catVertStatus?.ontbrekend > 0 && (
                          <button
                            type="button"
                            className="px-2.5 py-1 bg-indigo-600 text-white font-bold hover:bg-indigo-700 disabled:opacity-50"
                            disabled={catVertBusy || catLoading}
                            onClick={vertaalAlleArtikels}
                            data-testid="catalogus-vertaal-alles"
                          >
                            {catVertBusy ? `⏳ ${catVertProgress?.done ?? 0}/${catVertProgress?.totaal ?? '…'}` : `✨ Vertaal nu alle artikels (AI)`}
                          </button>
                        )}
                      </div>
                      {catVertBusy && catVertProgress?.totaal > 0 && (
                        <div className="h-1.5 bg-indigo-100 mt-2 overflow-hidden">
                          <div className="h-full bg-indigo-600 transition-[width] duration-300" style={{ width: `${Math.min(100, Math.round((catVertProgress.done / catVertProgress.totaal) * 100))}%` }} />
                        </div>
                      )}
                      {catVertStatus?.categorieen?.length > 0 && (
                        <details className="mt-2" data-testid="catalogus-vertaal-categorieen">
                          <summary className="cursor-pointer text-indigo-800 font-semibold select-none">Categorienamen ({catVertStatus.categorieen.filter(c => c.vertaling).length}/{catVertStatus.categorieen.length} vertaald) — klik om na te kijken of aan te passen</summary>
                          <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-2 gap-1.5 max-h-44 overflow-y-auto pr-1">
                            {catVertStatus.categorieen.map((c, i) => (
                              <div key={c.cat} className="flex items-center gap-1.5">
                                <span className="w-[40%] truncate text-gray-600" title={c.cat}>{c.cat}</span>
                                <span className="text-gray-400">→</span>
                                <input
                                  className="flex-1 border border-gray-300 px-1.5 py-0.5 text-[11px] bg-white"
                                  placeholder="(AI vult aan bij genereren)"
                                  value={c.vertaling}
                                  onChange={(e) => setCatVertStatus(s => ({ ...s, categorieen: s.categorieen.map((x, j) => j === i ? { ...x, vertaling: e.target.value } : x) }))}
                                  onBlur={(e) => bewaarCatVertaling(c.cat, e.target.value)}
                                  data-testid={`catalogus-vertaal-cat-${i}`}
                                />
                              </div>
                            ))}
                          </div>
                        </details>
                      )}
                      <div className="text-indigo-700/80 mt-1.5">Vertalingen worden per artikel bewaard en zijn aanpasbaar in de artikelfiche (sectie "Vertalingen").</div>
                    </div>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide">Persoonlijke prijslijst voor klant</label>
                  <input
                    list="catalogus-klant-lijst"
                    className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Typ klantnaam… (leeg = algemene catalogus)"
                    value={catKlant ? catKlant.naam : catKlantZoek}
                    onChange={(e) => {
                      const v = e.target.value; setCatKlantZoek(v);
                      const hit = catKlanten.find(k => k.naam === v);
                      setCatKlantId(hit ? hit.id : '');
                      if (hit && ['nl', 'fr', 'en', 'de'].includes(hit.taal) && hit.taal !== catTaal) wisselCatTaal(hit.taal);
                    }}
                    disabled={catLoading}
                    data-testid="catalogus-pdf-klant"
                  />
                  <datalist id="catalogus-klant-lijst">
                    {catKlanten.slice(0, 500).map(k => <option key={k.id} value={k.naam} />)}
                  </datalist>
                  {catKlant ? (
                    <p className="text-[11px] text-emerald-700 mt-1 flex items-center gap-2" data-testid="catalogus-pdf-klant-info">
                      <span>✓ Prijsgroep <strong data-testid="catalogus-pdf-effectieve-prijsgroep">{catPrijsgroep.charAt(0).toUpperCase() + catPrijsgroep.slice(1)}</strong> + klantkortingen</span>
                      <button type="button" className="underline text-red-600" onClick={() => { setCatKlantId(''); setCatKlantZoek(''); }} data-testid="catalogus-pdf-klant-wis">wis</button>
                    </p>
                  ) : <p className="text-[11px] text-gray-500 mt-1">Toont nettoprijzen met de kortingen van die klant; klantnaam op de voorpagina.</p>}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3" data-testid="catalogus-pdf-extra">
                <label className="inline-flex items-start gap-2 cursor-pointer text-xs font-semibold text-gray-700 select-none border border-gray-200 p-2">
                  <input type="checkbox" className="mt-0.5" checked={catVoorpagina} onChange={(e) => setCatVoorpagina(e.target.checked)} disabled={catLoading} data-testid="catalogus-pdf-voorpagina" />
                  <span>Voorpagina + inhoudsopgave<span className="block text-[10px] font-normal text-gray-500">logo, geldigheid, categorieën met paginanummer</span></span>
                </label>
                <label className="inline-flex items-start gap-2 text-xs font-semibold text-gray-700 select-none border border-gray-200 p-2">
                  <span className="mt-0.5">🟢</span>
                  <span>"Nieuw"-label voor artikels jonger dan
                    <input type="number" min="0" max="365" className="w-14 mx-1 border border-gray-300 px-1 py-0.5 text-xs" value={catLabelNieuwDagen} onChange={(e) => setCatLabelNieuwDagen(e.target.value)} disabled={catLoading} data-testid="catalogus-pdf-label-nieuw" /> dagen
                    <span className="block text-[10px] font-normal text-gray-500">0 = geen label</span></span>
                </label>
                <label className="inline-flex items-start gap-2 cursor-pointer text-xs font-semibold text-gray-700 select-none border border-gray-200 p-2">
                  <input type="checkbox" className="mt-0.5" checked={catLabelActie} onChange={(e) => setCatLabelActie(e.target.checked)} disabled={catLoading} data-testid="catalogus-pdf-label-actie" />
                  <span>🔴 "Actie"-labels tonen<span className="block text-[10px] font-normal text-gray-500">artikels met vinkje "Actie" in de artikelfiche</span></span>
                </label>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide" htmlFor="catalogus-prijsgroep">Prijsgroep voor deze catalogus</label>
                <select
                  id="catalogus-prijsgroep"
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={catPrijsgroep}
                  onChange={(e) => setCatPrijsgroep(e.target.value)}
                  data-testid="catalogus-pdf-prijsgroep"
                  disabled={catLoading}
                >
                  <option value="basic">Basic</option>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                  <option value="bulk">Bulk</option>
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide">Artikels in catalogus</label>
                <select
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={catAlleenMetFoto ? 'foto' : 'alle'}
                  onChange={(e) => setCatAlleenMetFoto(e.target.value === 'foto')}
                  data-testid="catalogus-pdf-fotofilter"
                  disabled={catLoading}
                >
                  <option value="foto">Alleen artikels met foto (aanbevolen)</option>
                  <option value="alle">Alle artikels (ook zonder foto)</option>
                </select>
                {artikels.filter(a => a.catalogus_verbergen).length > 0 && (
                  <p className="text-[11px] text-gray-500 mt-1" data-testid="catalogus-pdf-uitgesloten-hint">
                    🚫 {artikels.filter(a => a.catalogus_verbergen).length} artikel(s) zijn uitgesloten via het vinkje “Niet in catalogus” — die komen nooit in de PDF.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs font-bold uppercase text-gray-600 mb-1 tracking-wide">Barcode op de catalogus</label>
                <select
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={catBarcodeMode}
                  onChange={(e) => setCatBarcodeMode(e.target.value)}
                  data-testid="catalogus-pdf-barcodemode"
                  disabled={catLoading}
                >
                  <option value="ean">EAN-128 (alleen artikels met EAN)</option>
                  <option value="art_code">Art. code (Code-128)</option>
                  <option value="beide">Beide — EAN + Art. code</option>
                  <option value="geen">Geen barcode</option>
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  De gekozen waarde wordt onder elk artikel als scanbare Code-128 / EAN-128 streepjescode afgedrukt.
                </p>
              </div>
              {/* ─── Categorie-volgorde + inclusie ─── */}
              <div data-testid="catalogus-pdf-volgorde-section">
                <div className="flex items-center justify-between mb-1 gap-2">
                  <label className="text-xs font-bold uppercase text-gray-600 tracking-wide">Volgorde categorieën in PDF</label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px] font-bold text-gray-700 select-none">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 accent-[#002FA7]"
                      checked={catNieuwePagina}
                      onChange={(e) => setCatNieuwePagina(e.target.checked)}
                      data-testid="catalogus-pdf-nieuwe-pagina"
                      disabled={catLoading}
                    />
                    Nieuwe pagina per categorie
                  </label>
                </div>
                <div className="border border-gray-200 max-h-56 overflow-y-auto bg-gray-50">
                  {catVolgorde.length === 0 && (
                    <div className="text-center text-xs text-gray-400 py-4">Geen categorieën gevonden.</div>
                  )}
                  {catVolgorde.map((item, idx) => (
                    <div
                      key={item.cat}
                      className={`flex items-center justify-between gap-2 px-2 py-1.5 border-b border-gray-100 last:border-b-0 text-xs ${item.included ? 'bg-white' : 'bg-gray-100 text-gray-400'}`}
                      data-testid={`catalogus-pdf-cat-row-${item.cat}`}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <input
                          type="checkbox"
                          className="w-3.5 h-3.5 accent-[#002FA7] flex-shrink-0"
                          checked={item.included}
                          onChange={() => toggleCatIncluded(idx)}
                          data-testid={`catalogus-pdf-cat-check-${item.cat}`}
                          disabled={catLoading}
                        />
                        <span className="font-mono text-[10px] text-gray-400 w-6 text-right flex-shrink-0">{idx + 1}.</span>
                        <span className="font-medium truncate">{item.cat}</span>
                      </div>
                      <div className="flex gap-0.5 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => moveCat(idx, -1)}
                          disabled={idx === 0 || catLoading}
                          className="px-1.5 py-0.5 text-[#002FA7] hover:bg-[#002FA7]/10 disabled:opacity-30 disabled:hover:bg-transparent text-sm font-bold"
                          data-testid={`catalogus-pdf-cat-up-${item.cat}`}
                          title="Omhoog"
                        >▲</button>
                        <button
                          type="button"
                          onClick={() => moveCat(idx, 1)}
                          disabled={idx === catVolgorde.length - 1 || catLoading}
                          className="px-1.5 py-0.5 text-[#002FA7] hover:bg-[#002FA7]/10 disabled:opacity-30 disabled:hover:bg-transparent text-sm font-bold"
                          data-testid={`catalogus-pdf-cat-down-${item.cat}`}
                          title="Omlaag"
                        >▼</button>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-gray-500 mt-1">
                  Sleep met ▲▼ om de afdrukvolgorde te bepalen. Vink uit om een categorie weg te laten uit de PDF.
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1 gap-2">
                  <label className="text-xs font-bold uppercase text-gray-600 tracking-wide flex-1">Voorwaarden (optioneel)</label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px] font-bold text-gray-700 select-none">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 accent-[#002FA7]"
                      checked={catVoorwaardenAfdrukken}
                      onChange={(e) => setCatVoorwaardenAfdrukken(e.target.checked)}
                      data-testid="catalogus-pdf-voorwaarden-afdrukken"
                      disabled={catLoading}
                    />
                    {catVoorwaardenAfdrukken ? 'Afdrukken' : 'Niet afdrukken'}
                  </label>
                  <label className="inline-flex items-center gap-1.5 cursor-pointer text-[11px] font-bold text-gray-700 select-none" title="Drukt de voorwaarden onderaan ELKE pagina af zodat klanten nooit kunnen zeggen 'we hebben het niet gezien'. Standaard aanbevolen.">
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 accent-[#7C3AED]"
                      checked={catVoorwaardenElkePagina}
                      onChange={(e) => setCatVoorwaardenElkePagina(e.target.checked)}
                      data-testid="catalogus-pdf-voorwaarden-elke-pagina"
                      disabled={catLoading || !catVoorwaardenAfdrukken}
                    />
                    Op elke pagina ⭐
                  </label>
                  {catTaal !== 'nl' && nlVoorwaardenTekst() && (
                    <button
                      type="button"
                      className="text-[10px] text-indigo-700 hover:underline font-bold disabled:opacity-50"
                      disabled={catVwVertaalBusy || catLoading}
                      onClick={() => vertaalVoorwaarden(catTaal)}
                      title="Vertaalt je Nederlandse voorwaarden via AI naar deze taal; je kan het resultaat nog aanpassen."
                      data-testid="catalogus-pdf-voorwaarden-vertaal"
                    >
                      {catVwVertaalBusy ? '⏳ Vertalen…' : `✨ Vertaal uit NL (AI)`}
                    </button>
                  )}
                  {catVoorwaarden && (
                    <button
                      type="button"
                      className="text-[10px] text-red-600 hover:underline font-medium"
                      onClick={() => { setCatVoorwaarden(''); try { localStorage.removeItem(vwKey(catTaal)); } catch { /* noop */ } }}
                      data-testid="catalogus-pdf-voorwaarden-wis"
                    >
                      Wissen
                    </button>
                  )}
                </div>
                <textarea
                  className={`w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono ${!catVoorwaardenAfdrukken ? 'bg-gray-50 text-gray-400' : ''}`}
                  rows={5}
                  value={catVoorwaarden}
                  onChange={(e) => setCatVoorwaarden(e.target.value)}
                  placeholder="Bv.&#10;Transportkosten: € 25 (gratis vanaf € 500)&#10;Levertijd: 3-5 werkdagen&#10;Betaling: 30 dagen netto&#10;Retour: 14 dagen, ongeopend"
                  data-testid="catalogus-pdf-voorwaarden"
                  disabled={catLoading}
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  {catVoorwaardenAfdrukken
                    ? 'Wordt op de laatste pagina van de PDF toegevoegd. Tekst en keuze worden onthouden.'
                    : '⚠️ Voorwaarden worden NIET afgedrukt. Tekst blijft bewaard voor later.'}
                </p>
              </div>
              <div>
                <div className="flex items-center justify-between mb-1 gap-2">
                  <label className="text-xs font-bold uppercase text-gray-600 tracking-wide flex-1">Geldig tot (optioneel)</label>
                  {catGeldigTot && (
                    <button
                      type="button"
                      className="text-[10px] text-red-600 hover:underline font-medium"
                      onClick={() => { setCatGeldigTot(''); try { localStorage.removeItem('catalogus-geldig-tot'); } catch { /* noop */ } }}
                      data-testid="catalogus-pdf-geldig-tot-wis"
                    >
                      Wissen
                    </button>
                  )}
                </div>
                <input
                  type="date"
                  className="w-full border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={catGeldigTot}
                  onChange={(e) => setCatGeldigTot(e.target.value)}
                  data-testid="catalogus-pdf-geldig-tot"
                  disabled={catLoading}
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  {catGeldigTot
                    ? `Footer toont: "Geldig van afdrukdatum t.e.m. ${(() => { try { const [y,m,d] = catGeldigTot.split('-'); return `${d}/${m}/${y}`; } catch { return catGeldigTot; } })()}"`
                    : 'Leeg = "Geldig vanaf afdrukdatum" (zonder einddatum).'}
                </p>
              </div>
            </div>
            <div
              className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:justify-end gap-2 px-4 sm:px-6 py-3 border-t border-gray-200 bg-white sticky bottom-0"
              style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
              data-testid="catalogus-pdf-footer"
            >
              {catPdfReady && catMailOpen && (
                <div className="basis-full order-first border border-[#002FA7]/30 bg-[#F4F7FF] p-3 space-y-2" data-testid="catalogus-mail-paneel">
                  <div className="text-xs font-bold text-[#002FA7] uppercase">📧 Catalogus mailen{catKlant ? ` aan ${catKlant.naam}` : ''}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <input className="border border-gray-300 px-3 py-2 text-sm" placeholder="ontvanger@bedrijf.be" value={catMail.to} onChange={(e) => setCatMail(m => ({ ...m, to: e.target.value }))} data-testid="catalogus-mail-to" />
                    <input className="border border-gray-300 px-3 py-2 text-sm" placeholder={(catTaalTekst[catTaal] || catTaalTekst.nl).onderwerp} value={catMail.onderwerp} onChange={(e) => setCatMail(m => ({ ...m, onderwerp: e.target.value }))} data-testid="catalogus-mail-onderwerp" />
                  </div>
                  <textarea className="w-full border border-gray-300 px-3 py-2 text-sm" rows={3} placeholder={(catTaalTekst[catTaal] || catTaalTekst.nl).tekst} value={catMail.bericht} onChange={(e) => setCatMail(m => ({ ...m, bericht: e.target.value }))} data-testid="catalogus-mail-bericht" />
                  <div className="flex justify-end gap-2">
                    <button type="button" className="btn btn-secondary btn-sm" onClick={() => setCatMailOpen(false)} disabled={catMailBusy}>Annuleer</button>
                    <button type="button" className="btn btn-primary btn-sm font-bold" onClick={catVerstuurMail} disabled={catMailBusy} data-testid="catalogus-mail-verstuur">{catMailBusy ? 'Versturen…' : 'Verstuur met PDF-bijlage'}</button>
                  </div>
                </div>
              )}
              <button
                className="btn btn-secondary w-full sm:w-auto"
                style={{ minHeight: 44 }}
                onClick={() => { setCatModalOpen(false); setCatPdfReady(null); }}
                disabled={catLoading}
                data-testid="catalogus-pdf-cancel"
              >
                {catPdfReady ? 'Sluiten' : 'Annuleer'}
              </button>
              {catPdfReady ? (
                <>
                <button
                  type="button"
                  className="btn w-full sm:w-auto font-bold inline-flex items-center justify-center gap-1.5"
                  style={{ minHeight: 44, background: '#25D366', borderColor: '#25D366', color: '#fff' }}
                  data-testid="catalogus-pdf-share"
                  onClick={async () => {
                    try {
                      const shareData = {
                        title: 'Catalogus PDF',
                        text: 'Onze catalogus in PDF',
                        files: catPdfReady.file ? [catPdfReady.file] : [],
                      };
                      if (navigator.canShare && catPdfReady.file && navigator.canShare({ files: shareData.files })) {
                        await navigator.share(shareData);
                      } else if (navigator.share) {
                        // Fallback: deel zonder file (toont link)
                        await navigator.share({ title: shareData.title, text: shareData.text });
                      } else {
                        // Geen Web Share API → kopieer link
                        try { await navigator.clipboard.writeText(catPdfReady.url); toast.success('Link gekopieerd'); } catch { /* noop */ }
                      }
                    } catch (e) {
                      if (e?.name !== 'AbortError') {
                        toast.error('Delen niet beschikbaar — gebruik "PDF openen" en deel handmatig.');
                      }
                    }
                  }}
                >
                  📤 Delen via…
                </button>
                <button
                  type="button"
                  className="btn w-full sm:w-auto font-bold inline-flex items-center justify-center gap-1.5"
                  style={{ minHeight: 44, background: '#128C7E', borderColor: '#128C7E', color: '#fff' }}
                  onClick={catWhatsApp}
                  title={catKlant?.tel ? `WhatsApp naar ${catKlant.tel}` : 'WhatsApp met link naar de PDF (7 dagen geldig)'}
                  data-testid="catalogus-pdf-whatsapp"
                >
                  💬 WhatsApp
                </button>
                <button
                  type="button"
                  className="btn w-full sm:w-auto font-bold inline-flex items-center justify-center gap-1.5"
                  style={{ minHeight: 44, background: '#002FA7', borderColor: '#002FA7', color: '#fff' }}
                  onClick={() => setCatMailOpen(o => !o)}
                  data-testid="catalogus-pdf-mail"
                >
                  📧 Mailen
                </button>
                <button
                  type="button"
                  className="btn btn-primary w-full sm:w-auto font-bold inline-flex items-center justify-center gap-2"
                  style={{ minHeight: 44, background: '#10B981', borderColor: '#10B981' }}
                  data-testid="catalogus-pdf-open"
                  onClick={() => {
                    const isIOSDevice = isIOSPlatform();
                    if (isIOSDevice && catPdfReady.serverUrl) {
                      // iOS / iPadOS: gebruik echte server-URL (geen blob) → werkt 100% in Safari PDF-viewer
                      window.location.href = catPdfReady.serverUrl;
                    } else if (isIOSDevice) {
                      // iOS fallback: blob in zelfde tab
                      window.location.href = catPdfReady.url;
                    } else {
                      // Desktop / Android: nieuwe tab met blob
                      const w = window.open(catPdfReady.url, '_blank');
                      if (!w) window.location.href = catPdfReady.url;
                    }
                  }}
                >
                  📄 PDF openen
                </button>
                <a
                  className="btn btn-secondary w-full sm:w-auto font-bold inline-flex items-center justify-center gap-2"
                  style={{ minHeight: 44 }}
                  href={catPdfReady.url}
                  download={catPdfReady.fname}
                  data-testid="catalogus-pdf-download"
                  onClick={() => {
                    setTimeout(() => { setCatModalOpen(false); setCatPdfReady(null); }, 400);
                  }}
                >
                  💾 Downloaden
                </a>
                </>
              ) : (
              <button
                className="btn btn-primary w-full sm:w-auto font-bold"
                style={{ minHeight: 44 }}
                disabled={catLoading}
                data-testid="catalogus-pdf-genereer"
                onClick={async () => {
                  setCatLoading(true);
                  try {
                    // Anderstalig + nog onvertaalde artikels? Eerst vertalen (met voortgang), dan pas de PDF (voorkomt time-outs).
                    if (catTaal !== 'nl' && catVertStatus?.ontbrekend > 0) await vertaalAlleArtikels();
                    // Onthoud voorwaarden-tekst + afdruk-keuze + geldig-tot + volgorde voor de volgende keer
                    try {
                      if (catVoorwaarden.trim()) localStorage.setItem('catalogus-voorwaarden', catVoorwaarden);
                      else localStorage.removeItem('catalogus-voorwaarden');
                      localStorage.setItem('catalogus-voorwaarden-print', catVoorwaardenAfdrukken ? 'true' : 'false');
                      if (catGeldigTot) localStorage.setItem('catalogus-geldig-tot', catGeldigTot);
                      else localStorage.removeItem('catalogus-geldig-tot');
                      localStorage.setItem('catalogus-cat-volgorde', JSON.stringify(catVolgorde));
                      localStorage.setItem('catalogus-nieuwe-pagina', catNieuwePagina ? 'true' : 'false');
                    } catch { /* noop */ }
                    // Stuur voorwaarden ENKEL mee als de checkbox aan staat.
                    // Anderstalige catalogus zonder eigen voorwaarden? → NL-tekst automatisch via AI vertalen.
                    let vwTekst = catVoorwaarden.trim();
                    if (catVoorwaardenAfdrukken && !vwTekst && catTaal !== 'nl' && nlVoorwaardenTekst()) vwTekst = (await vertaalVoorwaarden(catTaal)).trim();
                    const voorwaardenPayload = (catVoorwaardenAfdrukken && vwTekst) ? vwTekst : '';
                    // Volgorde: enkel aangevinkte categorieën, in de gekozen volgorde.
                    const ingeslotenCats = catVolgorde.filter(x => x.included).map(x => x.cat);
                    // iPadOS 13+ Safari rapporteert zichzelf als MacIntel — detecteer ook via touch
                    const isIOSDevice = isIOSPlatform();
                    // iOS Safari blob-URL workaround: vraag server om een tijdelijke URL.
                    let serverUrl = null;
                    // Persist de nieuwe keuzes
                    try {
                      localStorage.setItem('catalogus-stijl', catStijl);
                      localStorage.setItem('catalogus-voorwaarden-elke-pagina', String(catVoorwaardenElkePagina));
                      localStorage.setItem('catalogus-kleur', catKleur || '');
                      localStorage.setItem('catalogus-zwart-wit', String(catZwartWit));
                      localStorage.setItem('catalogus-tekst-schaal', catTekstSchaal);
                      localStorage.setItem('catalogus-taal', catTaal);
                      localStorage.setItem('catalogus-voorpagina', String(catVoorpagina));
                      localStorage.setItem('catalogus-label-nieuw', String(catLabelNieuwDagen));
                      localStorage.setItem('catalogus-label-actie', String(catLabelActie));
                      if (catVoorwaarden.trim()) localStorage.setItem(vwKey(catTaal), catVoorwaarden);
                    } catch { /* ignore */ }
                    const catPayload = {
                      prijsgroep: catPrijsgroep,
                      alleen_met_foto: catAlleenMetFoto,
                      barcode_mode: catBarcodeMode,
                      voorwaarden: voorwaardenPayload,
                      voorwaarden_elke_pagina: catVoorwaardenElkePagina,
                      stijl: catStijl,
                      geldig_tot: catGeldigTot || '',
                      // Filter op enkel geselecteerde categorieën (lege list = alle)
                      categorieen: ingeslotenCats.length > 0 && ingeslotenCats.length < catVolgorde.length ? ingeslotenCats : [],
                      cat_volgorde: ingeslotenCats,
                      nieuwe_pagina_per_cat: catNieuwePagina,
                      ...catOpmaakPayload,
                    };
                    let serverToken = null;
                    if (isIOSDevice) {
                      try {
                        const tokenRes = await API.post('/artikels/catalogus-pdf', { ...catPayload, mode: 'token' });
                        if (tokenRes.data?.token) {
                          serverToken = tokenRes.data.token;
                          serverUrl = `${process.env.REACT_APP_BACKEND_URL}/api/temp-pdf/${tokenRes.data.token}`;
                        }
                      } catch { /* fallback naar blob hieronder */ }
                    }
                    const r = await API.post('/artikels/catalogus-pdf', catPayload, { responseType: 'blob' });
                    // axios met responseType: 'blob' geeft al een Blob terug — direct gebruiken
                    const blob = r.data;
                    const url = URL.createObjectURL(blob);
                    const fname = `catalogus-${new Date().toISOString().split('T')[0]}.pdf`;
                    // Bewaar ook de echte File-instantie voor native share (WhatsApp etc.)
                    let file = null;
                    try { file = new File([blob], fname, { type: 'application/pdf' }); } catch { /* sommige oude browsers */ }
                    setCatPdfReady({ url, fname, file, blob, serverUrl, serverToken, payload: catPayload });
                    setCatMailOpen(false);
                    setCatMail({ to: catKlant?.email || '', onderwerp: '', bericht: '' });
                    // Cleanup oude blob URLs na 5 minuten om geheugen vrij te geven
                    setTimeout(() => { try { URL.revokeObjectURL(url); } catch { /* noop */ } }, 5 * 60_000);
                  } catch (e) {
                    toast.error('Fout bij genereren: ' + (e.response?.data?.detail || e.message));
                  } finally {
                    setCatLoading(false);
                    if (catTaal !== 'nl') laadCatVertStatus(catTaal);
                  }
                }}
              >
                {catLoading ? (catTaal !== 'nl' && catVertStatus?.ontbrekend > 0 ? '⏳ Vertalen + genereren…' : '⏳ Bezig…') : '📕 Genereer PDF'}
              </button>
              )}
            </div>
          </div>
        </div>
      )}

      {dcOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
          onClick={() => setDcOpen(false)}
          data-testid="data-check-modal"
        >
          <div
            className="bg-white shadow-2xl border border-gray-200 w-full max-w-4xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Warning size={22} className="text-amber-600" weight="fill" />
                <h3 className="text-lg font-bold text-gray-900">Data-controle Artikels</h3>
              </div>
              <button className="text-gray-400 hover:text-gray-700" onClick={() => setDcOpen(false)} data-testid="data-check-close">
                <X size={20} />
              </button>
            </div>
            {dcLoading && (
              <div className="p-10 text-center text-gray-500 text-sm">Bezig met scannen van alle artikels…</div>
            )}
            {!dcLoading && dcResult && (
              <>
                <div className="px-5 py-4 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center gap-3 text-sm">
                  <span className="text-gray-700">
                    <strong>{dcResult.total_artikels}</strong> artikels gescand
                  </span>
                  <span className="text-gray-400">·</span>
                  <button
                    onClick={() => setDcSevFilter('all')}
                    className={`px-2 py-0.5 text-xs font-bold border ${dcSevFilter === 'all' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-700 border-gray-300'}`}
                    data-testid="dc-filter-all"
                  >
                    Alle ({dcResult.warnings.length})
                  </button>
                  <button
                    onClick={() => setDcSevFilter('high')}
                    className={`px-2 py-0.5 text-xs font-bold border ${dcSevFilter === 'high' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-red-700 border-red-200'}`}
                    data-testid="dc-filter-high"
                  >
                    Hoog ({dcResult.telling.high})
                  </button>
                  <button
                    onClick={() => setDcSevFilter('medium')}
                    className={`px-2 py-0.5 text-xs font-bold border ${dcSevFilter === 'medium' ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-amber-700 border-amber-200'}`}
                    data-testid="dc-filter-medium"
                  >
                    Middel ({dcResult.telling.medium})
                  </button>
                  <button
                    onClick={() => setDcSevFilter('low')}
                    className={`px-2 py-0.5 text-xs font-bold border ${dcSevFilter === 'low' ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-blue-700 border-blue-200'}`}
                    data-testid="dc-filter-low"
                  >
                    Laag ({dcResult.telling.low})
                  </button>
                </div>
                <div className="overflow-y-auto flex-1">
                  {dcResult.warnings.length === 0 ? (
                    <div className="p-10 text-center text-green-600">
                      <CheckCircle size={48} weight="fill" className="mx-auto mb-2" />
                      <div className="font-bold">Alle data is gezond!</div>
                      <div className="text-sm text-gray-500 mt-1">Geen problemen gedetecteerd in {dcResult.total_artikels} artikels.</div>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr className="text-left text-xs font-bold uppercase text-gray-600">
                          <th className="px-4 py-2">Sev</th>
                          <th className="px-4 py-2">Art. code</th>
                          <th className="px-4 py-2">Omschrijving</th>
                          <th className="px-4 py-2">Veld</th>
                          <th className="px-4 py-2">Probleem & suggestie</th>
                          <th className="px-4 py-2"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {dcResult.warnings
                          .filter(w => dcSevFilter === 'all' || w.severity === dcSevFilter)
                          .map((w, idx) => (
                            <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                              <td className="px-4 py-2">
                                <span className={`inline-block px-2 py-0.5 text-[10px] font-bold uppercase ${
                                  w.severity === 'high' ? 'bg-red-100 text-red-700' :
                                  w.severity === 'medium' ? 'bg-amber-100 text-amber-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {w.severity === 'high' ? 'Hoog' : w.severity === 'medium' ? 'Middel' : 'Laag'}
                                </span>
                              </td>
                              <td className="px-4 py-2 font-mono text-xs font-bold text-gray-900">{w.art_code}</td>
                              <td className="px-4 py-2 text-gray-700 max-w-[200px] truncate" title={w.omschrijving}>{w.omschrijving}</td>
                              <td className="px-4 py-2 font-mono text-xs text-gray-500">{w.veld}</td>
                              <td className="px-4 py-2 text-xs">
                                <div className="font-medium text-gray-900">{w.probleem}</div>
                                <div className="text-gray-500 italic mt-0.5">💡 {w.suggestie}</div>
                              </td>
                              <td className="px-4 py-2">
                                <button
                                  className="text-xs font-bold text-blue-600 hover:text-blue-800 underline"
                                  onClick={() => {
                                    setDcOpen(false);
                                    // open de artikel-fiche
                                    const art = artikels.find(x => x.id === w.art_id);
                                    if (art) {
                                      setForm({
                                        ...emptyForm,
                                        ...art,
                                        prijzen: art.prijzen || {},
                                        leveranciers: art.leveranciers || [],
                                      });
                                      setEditId(art.id);
                                      setTab('nieuw');
                                    }
                                  }}
                                  data-testid={`dc-open-${w.art_id}`}
                                  title="Open dit artikel om te corrigeren"
                                >
                                  Open →
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </>
            )}
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button className="btn btn-secondary btn-sm" onClick={() => setDcOpen(false)} data-testid="data-check-cancel">
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}
      <ArtikelImportModal
        open={importOpen}
        onClose={() => { setImportOpen(false); setImportInitialCsv(''); }}
        onDone={() => { setImportOpen(false); setImportInitialCsv(''); load(); }}
        initialCsv={importInitialCsv}
      />
      <PrijsWizardModal
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onStuurNaarImport={(csv) => { setWizardOpen(false); setImportInitialCsv(csv); setImportOpen(true); }}
      />

      {/* Sticky onderbalk: ALTIJD zichtbaar zodra er onverwerkte regels zijn — voorkomt dat de gebruiker vergeet te verwerken */}
      {nieuwOntvRegels.length > 0 && (() => {
        const totaalAantal = nieuwOntvRegels.reduce((s, r) => s + (parseFloat(r.aantal) || 0), 0);
        const totaalWaarde = nieuwOntvRegels.reduce((s, r) => s + ((parseFloat(r.aantal) || 0) * (parseFloat(r.aankoopprijs) || 0)), 0);
        return (
          <div
            className="fixed bottom-0 left-0 right-0 z-40 bg-amber-50 border-t-2 border-amber-400 shadow-2xl px-4 py-3"
            data-testid="artikels-sticky-verwerk-bar"
          >
            <div className="max-w-screen-2xl mx-auto flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2 text-amber-900">
                <Warning size={22} weight="fill" className="text-amber-600" />
                <div>
                  <div className="text-sm font-bold leading-tight">
                    {nieuwOntvRegels.length} onverwerkte regel(s) — niet vergeten te verwerken!
                  </div>
                  <div className="text-xs leading-tight">
                    Totaal {totaalAantal} stuks · waarde <span className="font-bold">{fmt(totaalWaarde)}</span> wordt bij voorraad bijgeteld zodra je verwerkt
                  </div>
                </div>
              </div>
              <div className="flex-1" />
              <button
                className="inline-flex items-center gap-2 bg-white border border-amber-400 text-amber-800 hover:bg-amber-100 px-3 py-2 rounded text-sm font-semibold"
                onClick={() => setNieuwOntvRegels([])}
                disabled={nieuwOntvBusy}
                data-testid="artikels-sticky-wissen"
              >
                <X size={16} /> Wissen
              </button>
              <button
                className="inline-flex items-center gap-2 bg-[#059669] hover:bg-[#047857] text-white px-6 py-3 rounded text-base font-bold shadow-lg disabled:opacity-50 animate-pulse"
                disabled={nieuwOntvBusy}
                onClick={() => { setTab('nieuw'); verwerkAlleNieuwOntv(); }}
                data-testid="artikels-sticky-verwerk"
              >
                <CheckCircle size={20} weight="fill" />
                {nieuwOntvBusy ? 'Bezig met verwerken…' : `Verwerk ${nieuwOntvRegels.length} regel(s) — voorraad +${totaalAantal}`}
              </button>
            </div>
          </div>
        );
      })()}

      {/* AI HS-code review modal — gestart via 🤖-knop in artikel-formulier */}
      <HsCodeReviewModal
        artikel={hsReview ? {
          id: form.id,
          omschrijving: form.omschrijving,
          art_code: form.art_code,
          categorie: form.categorie,
          foto_url: form.foto_url,
          oorsprong_land: form.oorsprong_land,
        } : null}
        suggestie={hsReview?.suggestie}
        gebruiktMateriaal={hsReview?.gebruikt_materiaal}
        onApply={(hsCode, meta) => {
          setForm(p => ({
            ...p,
            hs_code: hsCode,
            hs_beschrijving: meta?.beschrijving || '',
            hs_motivatie: meta?.motivatie || '',
            // Bestaand tarief invalideren — anders horen oude % bij verkeerde HS-code
            import_tarief: p.hs_code !== hsCode ? null : p.import_tarief,
          }));
          toast.success(`HS-code ingevuld: ${hsCode}`, { description: 'Vergeet niet het artikel op te slaan.' });
          setHsReview(null);
        }}
        onClose={() => setHsReview(null)}
        onSuggestieChange={(nieuwe, materiaal) =>
          setHsReview((prev) => prev ? { ...prev, suggestie: nieuwe, gebruikt_materiaal: materiaal } : prev)
        }
      />
      {aActies.Menu}
    </div>
  );
}
